import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import { getSocketClientOptions, getSocketUrl } from '../../utils/socketConfig';
import { ChatPage } from './components/Chat';
import { PreviewPage } from './components/Preview';
import styles from './styles.module.css';
import { Title } from './components/Title';
import { ColorSelector } from './components/CollorSelector';
import { NicheSelector } from './components/NicheSelector';
import { ProductImageSelector } from '../../shared/campaign/ProductImageSelector';
import { VisualStyleSelector } from './components/VisualStyleSelector';
import {
  UserAccountsService,
  SubscriptionService,
  EventMapService,
} from '../../services';
import { emitUserPlanCountUpdated } from '../../services/userPlanCountService';
import MarkdownPreview from './components/MarkdownPreview';
import { markdownToPdf } from './components/MarkdownPreview/utils';
import DownloadButton from './components/DownloadIButton';
import ImagesScrollableContainer from './components/ImagesScrollableContainer';
import { getBusinessByLogin } from '../../services/business';
import { getBrandingByBusinessId } from '../../services/brandingService';
import { getBusinessNichebyId } from '../../services/businessNicheService';
import { PreviewArt } from './components/PreviewArt';
import { DownloadZipCard } from './components/DownloadZipCard';
import { BatchEditLimitNoticeModal } from './components/BatchEditLimitNoticeModal';
import { CampaignUpsellFlow } from './components/CampaignUpsellFlow';
import { ArtsLimitUpsellFlow } from './components/ArtsLimitUpsellFlow';
import { SubscribedArtsLimitUpsellFlow } from './components/SubscribedArtsLimitUpsellFlow';
import { openUpsellFlow, openUpsellCheckoutSuccess, resolveUpsellCheckoutSuccess, UPSELL_FLOWS, UpsellCheckoutProvider } from './components/UpsellFlows';
import { SocialMediaSelector } from './components/SocialMediaSelector';
import { ScheduleSelector } from './components/ScheduleSelector';
import { SchedulingProgress } from './components/SchedulingProgress';
import { PostQuantitySelector } from './components/PostQuantitySelector';
import PaletteSelector from './components/PaletteSelector';
import { CampaignEvaluationModal } from '../../components/CampaignEvaluationModal';
import { CampaignEvaluationRewardModal } from '../../components/CampaignEvaluationModal/CampaignEvaluationRewardModal';
import {
  dismissCampaignEvaluationForSession,
  isCampaignEvaluationDismissedInSession,
} from '../../utils/campaignEvaluationSession';
import { submitCampaignEvaluation } from '../../utils/submitCampaignEvaluation';
import { useClient } from '../../hooks/useClient';
import { normalizeNetworkList } from './utils/socialNetworkMapper';
import { scheduleCampaignPublications, parseDateFromArt } from './utils/scheduleCampaignPublications';
import { isCompleteScheduleDateTimeFields, mergeArtsWithScheduleData } from '../../shared/campaign/utils/scheduleDateTime';
import { formatDateToScheduleFields } from '../../shared/campaign/utils/scheduleTimezone';
import { enrichArtsWithPublicationStatus } from './utils/publicationStatus';
import { notifySuccessCustom, notifyWarningCustom } from '../../utils/notify';
import {
  getArtGenerationAvailability,
  snapshotArtAvailability,
  warnIfArtLimitTotalDroppedWithoutUsageIncrease,
  getCampaignAvailability,
  getStrategyAvailability,
  getAISuggestionAvailability,
} from '../../utils/artConsumption';
import {
  isArtCreditsSyncPending,
  pollArtCreditsUntilUpdated,
  waitForArtCreditsSync,
} from '../../utils/artCreditsSync';
import { getCampaignArtById, postCampaignImagesUpload } from '../../services/campaignService';
import { usePaywall } from '../../contexts/PaywallContext/usePaywall';
import { useCampaignSidebar } from '../../contexts/CampaignSidebarContext/useCampaignSidebar';
import { StepsList } from './components/StepsList';
import { MIN_ARTS_TO_CREATE_CAMPAIGN } from '../../constants/campaignCreation';
import {
  LAST_ACTIVE_CAMPAIGN_KEY,
  persistActiveCampaignForReturn,
} from './utils/campaignRestore';
import {
  registerCheckoutRefreshRunner,
  savePendingCheckoutRefresh,
  unregisterCheckoutRefreshRunner,
} from './components/UpsellFlows/upsellCheckoutRefresh';
import { waitForCampaignDownloadAccess, checkCampaignDownloadAccess } from './utils/campaignDownloadAccess';
import {
  buildArtsReadyApprovalQuestion,
  buildCompletedCampaignQuestion,
  buildHistoryFromCampaignData,
  deriveSchedulingLockFromHistory,
  findPendingQuestionFromCampaignData,
  mergeArtsByPieceNumber,
  normalizeCampaignArtsPayload,
  resolveEffectiveCampaignStatus,
  sortArtsByPieceNumber,
} from './utils/campaignSwitchRestore';
import { normalizeCampaignArtEntry } from '../../shared/campaign/utils/artDisplayCacheBust';
import {
  clearStoredCampaignPrompt,
  getStoredCampaignPrompt,
} from '../../utils/campaignPromptStorage';

const getStorageToken = () => {
  const rawToken = window.localStorage.getItem('token');
  if (!rawToken) return null;

  try {
    return JSON.parse(rawToken);
  } catch {
    return rawToken;
  }
};





const parseMaybeJson = (value) => {
  if (typeof value !== 'string') {
    return value;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return value;
  }

  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
    return value;
  }

  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
};

// Função auxiliar para converter Base64 em Blob para upload multipart
const base64ToBlob = (base64, filename) => {
  try {
    const arr = base64.split(',');
    const mimeMatch = arr[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : 'image/png';
    const bstr = atob(arr[1] || arr[0]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  } catch (error) {
    console.error('Error converting base64 to blob:', error);
    return null;
  }
};

/** Índice da peça vindo do socket/Prisma pode ser number ou string — evita falha em === */
const normalizeCampaignPieceNumber = (value) => {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const normalizeCampaignUuid = (value) => {
  if (value == null || value === '') return null;
  return String(value).trim().toLowerCase();
};

/** Mesma imagem com query string diferente (ex.: URL assinada) ainda deve casar com `results`. */
const artUrlsLooselyMatch = (a, b) => {
  if (a == null || b == null) return false;
  if (a === b) return true;
  if (typeof a !== 'string' || typeof b !== 'string') return false;
  try {
    const ua = new URL(a);
    const ub = new URL(b);
    return `${ua.origin}${ua.pathname}` === `${ub.origin}${ub.pathname}`;
  } catch {
    return a.split('?')[0] === b.split('?')[0];
  }
};

const extractSocialSelectionFromHistory = (history = []) => {
  if (!Array.isArray(history)) {
    return [];
  }

  const socialMediaHistory = [...history]
    .reverse()
    .find((item) => item?.component === 'SocialMediaSelector');

  if (!socialMediaHistory) {
    return [];
  }

  const parsedAnswer = parseMaybeJson(socialMediaHistory.answer);
  return Array.isArray(parsedAnswer) ? parsedAnswer : [];
};

const disableTypingForBackendMessage = (message = {}) => {
  if (!message?.backend) {
    return message;
  }

  return {
    ...message,
    disableTypingAnimation: true,
  };
};

const hasBackendMessage = (messages = [], text = '') =>
  messages.some((item) => item?.backend === text);

const getFiniteOrderValue = (value) => {
  if (value == null || value === '') {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const appendMessageWithTailOrderForSocialMediaSelector = (
  messages = [],
  message = {},
) => {
  const tailComponents = ['SocialMediaSelector', 'SchedulingProgress'];
  if (!tailComponents.includes(message?.component)) {
    return [...messages, message];
  }

  const lastOrder = messages.reduce(
    (max, item) => Math.max(max, Number(item?.messageOrder ?? 0)),
    0,
  );
  // const currentOrder = getFiniteOrderValue(message?.messageOrder);
  const tailOrder = lastOrder + 0.1;

  return [
    ...messages,
    {
      ...message,
      messageOrder: tailOrder,
    },
  ];
};

const appendBackendMessageIfMissing = (messages = [], message = {}) => {
  // const backendText = message?.backend;

  // if (!backendText) {
  //   return [...messages, message];
  // }

  // if (hasBackendMessage(messages, backendText)) {
  //   return messages;
  // }

  // If message has ordering info, insert it in the correct ordered position
  const messageOrderNew = getFiniteOrderValue(message?.messageOrder);

  if (messageOrderNew != null) {
    const copy = [...messages];
    let inserted = false;

    for (let i = 0; i < copy.length; i += 1) {
      const m = copy[i];
      const ma = getFiniteOrderValue(m?.messageOrder);

      if (ma != null && messageOrderNew < ma) {
        copy.splice(i, 0, message);
        inserted = true;
        break;
      }
    }

    if (!inserted) copy.push(message);
    return copy;
  }

  return [...messages, message];
};

const CAMPAIGN_INITIATION_LIMIT_CODE = 'campaign_initiation_limit_reached';
const CAMPAIGN_INITIATION_LIMIT_MESSAGE =
  'Plan limit reached for campaign_initiation';
const AI_SUGGESTION_LIMIT_CODE = 'ai_suggestion_limit_reached';
const AI_SUGGESTION_LIMIT_MESSAGE =
  'Plan limit reached for ai_suggestion';
const STRATEGY_APPROVAL_STORAGE_KEY = 'strategyApprovalByCampaign';
const ART_STEPS_STORAGE_KEY = 'artSteps';

const appendStrategyReadyReviewMessageIfMissing = (messages = []) => {
  // if (hasBackendMessage(messages, STRATEGY_READY_REVIEW_MESSAGE)) {
  //   return messages;
  // }

  // return appendMessagesWithSequentialOrder([{
  //   backend: STRATEGY_READY_REVIEW_MESSAGE,
  //   frontend: '',
  // }]);

  return messages;
};

const getPersistedStrategyApprovalMap = () => {
  try {
    const raw = window.localStorage.getItem(STRATEGY_APPROVAL_STORAGE_KEY);
    if (!raw) {
      return {};
    }

    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const isStrategyApprovedPersisted = (campaignId) => {
  const normalizedCampaignId = normalizeCampaignUuid(campaignId);
  if (!normalizedCampaignId) {
    return false;
  }

  const approvalsMap = getPersistedStrategyApprovalMap();
  return !!approvalsMap[normalizedCampaignId];
};

const persistStrategyApproval = (campaignId, isApproved = true) => {
  const normalizedCampaignId = normalizeCampaignUuid(campaignId);
  if (!normalizedCampaignId) {
    return;
  }

  try {
    const approvalsMap = getPersistedStrategyApprovalMap();

    if (isApproved) {
      approvalsMap[normalizedCampaignId] = true;
    } else {
      delete approvalsMap[normalizedCampaignId];
    }

    window.localStorage.setItem(
      STRATEGY_APPROVAL_STORAGE_KEY,
      JSON.stringify(approvalsMap),
    );
  } catch {
    // noop
  }
};

const buildArtSteps = (totalPieces = 0, initialPendingIndex = 0, tFn = null) => {
  const safeTotal = Number.isFinite(Number(totalPieces))
    ? Math.max(0, Math.floor(Number(totalPieces)))
    : 0;

  if (safeTotal === 0) {
    return [];
  }

  return Array.from({ length: safeTotal }, (_, idx) => ({
    text: tFn ? tFn('chatsocket.art_step_fallback', { num: idx + 1 }) : `Peça ${idx + 1}`,
    status: idx === initialPendingIndex ? 'pending' : 'idle',
  }));
};


const getCampaignProgressIndex = ({ campaignStatus, currentQuestion }) => {
  const stepOrder = currentQuestion?.stepOrder;
  const messageOrder = currentQuestion?.messageOrder;
  const component = currentQuestion?.component;

  if (campaignStatus === 'completed') {
    return 7;
  }

  if (campaignStatus === 'arts_approved') {
    if (
      component === 'SocialMediaSelector' ||
      component === 'ScheduleSelector' ||
      component === 'SchedulingProgress' ||
      (stepOrder === 1 && messageOrder >= 11)
    ) {
      return 7;
    }

    if (
      component === 'DownloadZipCard' ||
      (stepOrder === 1 && (messageOrder === 9 || messageOrder === 10))
    ) {
      return 6;
    }

    return 5;
  }

  if (campaignStatus === 'arts_ready') {
    return 4;
  }

  if (campaignStatus === 'art_generation_in_progress') {
    return 3;
  }

  if (campaignStatus === 'strategy_approved') {
    return 3;
  }

  if (
    campaignStatus === 'strategy_ready' ||
    campaignStatus === 'strategy_processing' ||
    campaignStatus === 'questions_completed'
  ) {
    return 2;
  }

  if (stepOrder === 0) {
    return messageOrder <= 1 ? 0 : 1;
  }

  return 0;
};

export const ChatSocketPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { clientData } = useClient();
  const {
    openPaywall: _openPaywall,
    closePaywall,
  } = usePaywall();
  const {
    campaigns,
    firstCampaignIdRef,
    refreshCampaigns,
    updateCampaignInList,
    setSelectedCampaignId,
    registerCampaignHandlers,
    unregisterCampaignHandlers,
    pendingSelectId,
    pendingCreate,
    clearPendingActions,
    syncArtLimits,
  } = useCampaignSidebar();
  const openPaywall = (config) => {
    persistActiveCampaignForReturn(campaignArtGenerationIdRef.current);
    _openPaywall(config);
  };

  const socketRef = useRef();
  const latestDownloadDataRef = useRef({
    campaignArtGenerationId: null,
    imgsArray: [],
    markdownContent: '',
  });
  const consumedCampaignArtOperationKeysRef = useRef(new Set());
  const NEW_CAMPAIGN_DRAFT_KEY = '__new_campaign__';
  const getCampaignDraftKey = (campaignId) =>
    normalizeCampaignUuid(campaignId) || NEW_CAMPAIGN_DRAFT_KEY;
  const [message, setMessage] = useState('');
  const [messageDraftByCampaign, setMessageDraftByCampaign] = useState({});
  const messageDraftByCampaignRef = useRef({});
  const pendingCampaignPromptRef = useRef(null);
  const pendingCampaignPromptSentRef = useRef(false);

  const isBrandNameQuestion = (question) =>
    Number(question?.stepOrder) === -1 && Number(question?.messageOrder) === -1;

  const getPendingCampaignPrompt = () => {
    const pendingFromRef = pendingCampaignPromptRef.current?.trim();
    if (pendingFromRef) {
      return pendingFromRef;
    }

    const pendingFromStorage = getStoredCampaignPrompt()?.trim();
    if (pendingFromStorage) {
      pendingCampaignPromptRef.current = pendingFromStorage;
    }

    return pendingFromStorage || null;
  };

  const finalizePendingCampaignPrompt = () => {
    pendingCampaignPromptRef.current = null;
    clearStoredCampaignPrompt();
  };

  const clearInputMessage = (campaignId = campaignArtGenerationIdRef.current) => {
    const draftKey = getCampaignDraftKey(campaignId);
    const nextDrafts = {
      ...messageDraftByCampaignRef.current,
      [draftKey]: '',
      [NEW_CAMPAIGN_DRAFT_KEY]: '',
    };

    messageDraftByCampaignRef.current = nextDrafts;
    setMessageDraftByCampaign(nextDrafts);
    setMessage('');
  };



  const [isColorSelectorOpen, setIsColorSelectorOpen] = useState(false);
  const [isNicheSelectorOpen, setIsNicheSelectorOpen] = useState(false);
  const [isProductImageSelectorOpen, setIsProductImageSelectorOpen] =
    useState(false);
  const [isVisualStyleSelectorOpen, setIsVisualStyleSelectorOpen] =
    useState(false);

  const [campaignArtGenerationId, setCampaignArtGenerationId] = useState(null);
  const campaignArtGenerationIdRef = useRef(null);
  const campaignLangRef = useRef(localStorage.getItem('i18nextLng') || 'ptBR');
  const [campaignLanguage, setCampaignLanguage] = useState(localStorage.getItem('i18nextLng') || 'ptBR');
  const tc = useCallback((key, options = {}) => {
    const lng = campaignLanguage || i18n.language;
    return t(key, { ...options, lng });
  }, [t, i18n.language, campaignLanguage]);
  const strategyProcessingRequestByCampaignRef = useRef(new Set());
  const firstCampaignQuantityAutoAnswerRef = useRef(new Set());
  const isRestoringCampaignSelectionRef = useRef(false);
  const campaignSelectSeqRef = useRef(0);
  const [isCampaignSwitching, setIsCampaignSwitching] = useState(false);
  const suppressTypingDuringRestoreRef = useRef(false);
  const isCreatingNewCampaignRef = useRef(false);
  const previousCampaignBeforeCreateRef = useRef(null);
  const [paletteConfirmed, setPaletteConfirmed] = useState(false);
  const requestedInitialQuestionCampaignIdRef = useRef(null);
  /** Peça da última edição confirmada — cobre resposta do socket sem `piece_number` confiável. */
  const lastEditPieceNumberRef = useRef(null);
  /** True após enviar edição única até processar `campaign_art_edited` — garante atualizar o preview. */
  const awaitingSingleArtEditRef = useRef(false);
  const isTransitioningRef = useRef(false);
  const [isBulkStep0Active, setIsBulkStep0Active] = useState(false);
  const [batchEditActionType, setBatchEditActionType] = useState(null);
  const [artLimitFallback, setArtLimitFallback] = useState(null);
  const [campaignStatus, setCampaignStatus] = useState('in_progress');
  const campaignStatusRef = useRef('in_progress');
  const [campaignName, setCampaignName] = useState(tc('chatsocket.default_campaign_name'));
  const [previewActiveTab, setPreviewActiveTab] = useState('ai');
  const [connectionStatus, setConnectionStatus] = useState('connected');
  const handleSelectCampaignRef = useRef(null);
  const handleCreateCampaignWithGateRef = useRef(null);


  const appendAndReturnSocketMessages = (socket, timeoutMs = 15000) => {
    if (!socket) {
      return [];
    }

    const socketFeedback = socket.on('feedback_message', (data) => {
      return data.message
    });
    socket.off('feedback_message', socketFeedback);

    return socketFeedback

  }

  const appendMessagesWithSequentialOrder = (items = []) => {
    setMessages((prev) => {
      const lastOrder = prev.reduce((max, item) => {
        const order = getFiniteOrderValue(item?.messageOrder);
        return order != null ? Math.max(max, order) : max;
      }, 0);

      return [
        ...prev,
        ...items.map((item, index) => ({
          ...item,
          messageOrder: Number((lastOrder + (index + 1) * 0.1).toFixed(1)),
        })),
      ];
    });
  };

  const renderConnectionBanner = () => {
    if (connectionStatus === 'connected') return null;

    let msg = '';
    let statusClass = '';

    if (connectionStatus === 'disconnected') {
      msg = tc('chatsocket.connection.disconnected');
      statusClass = styles.disconnected;
    } else if (connectionStatus === 'reconnecting') {
      msg = tc('chatsocket.connection.reconnecting');
      statusClass = styles.reconnecting;
    } else if (connectionStatus === 'error') {
      msg = tc('chatsocket.connection.connection_error');
      statusClass = styles.error;
    }

    return (
      <div className={`${styles.connectionBanner} ${statusClass}`}>
        <span className={styles.connectionBannerText}>{msg}</span>
      </div>
    );
  };

  const replaceBrandPlaceholder = (text = '') => {
    try {
      if (!text || typeof text !== 'string') return text;

      const finalBrand = brandNameFromDbRef.current || brandNameFromDb || '';
      return text.replace(/\[(?:Nome|Nombre) da Marca\]|\[Nombre de la Marca\]/g, finalBrand);
    } catch (e) {
      return text;
    }
  };

  const handleCheckoutRefreshRef = useRef(() => {});

  useEffect(() => {
    const handleCheckoutReturn = async () => {
      const url = new URL(window.location.href);
      const sessionId = url.searchParams.get('session_id');
      const hasAddonCheckoutSuccess =
        url.searchParams.get('addonCheckoutSuccess') === 'true' &&
        !!sessionId;
      const hasDownloadCheckoutSuccess =
        url.searchParams.get('downloadCheckoutSuccess') === 'true' &&
        !!sessionId;
      const hasSubscriptionCheckoutSuccess =
        !!sessionId &&
        (url.searchParams.get('subscriptionCheckoutSuccess') === 'true' ||
          (!url.searchParams.get('addonCheckoutSuccess') &&
            !url.searchParams.get('downloadCheckoutSuccess')));

      if (
        !hasAddonCheckoutSuccess &&
        !hasDownloadCheckoutSuccess &&
        !hasSubscriptionCheckoutSuccess
      ) {
        return;
      }

      const campaignIdFromUrl = url.searchParams.get('campaignArtGenerationId');
      if (campaignIdFromUrl) {
        persistActiveCampaignForReturn(campaignIdFromUrl);
        campaignArtGenerationIdRef.current = campaignIdFromUrl;
      }

      const upsellSuccess = resolveUpsellCheckoutSuccess(url.search);
      const activeCampaignId =
        campaignIdFromUrl || campaignArtGenerationIdRef.current || null;

      const checkoutRefreshPayload = {
        subscription: hasSubscriptionCheckoutSuccess,
        addon: hasAddonCheckoutSuccess,
        download: hasDownloadCheckoutSuccess,
        campaignId: activeCampaignId,
      };

      let openedUpsellSuccess = false;

      if (upsellSuccess) {
        if (upsellSuccess.flow === UPSELL_FLOWS.CAMPAIGN_DOWNLOAD) {
          const hasDownloadAccess = await waitForCampaignDownloadAccess({
            campaignId: activeCampaignId,
            syncUserCanDownloadCampaigns: async () => {
              const userResponse = await UserAccountsService.getUserAccountsByLogin();
              const canDownloadAll = Boolean(userResponse?.data?.canDownloadCampaigns);

              canDownloadCampaignsRef.current = canDownloadAll;
              setCanDownloadCampaigns(canDownloadAll);

              return canDownloadAll;
            },
            getCampaignDownloadPurchased: (campaignId) =>
              EventMapService.getCampaignDownloadPurchased(campaignId),
            onCampaignPurchased: () => {
              applyCampaignSpecificDownloadAccess(activeCampaignId);
            },
          });

          if (hasDownloadAccess) {
            openedUpsellSuccess = openUpsellCheckoutSuccess(upsellSuccess, {
              campaignDownloadRef: campaignUpsellFlowRef,
              artsLimitRef: artsLimitUpsellFlowRef,
              subscribedArtsLimitRef: subscribedArtsLimitUpsellFlowRef,
            });
          } else {
            notifyWarningCustom(
              'Pagamento recebido. Estamos liberando o download da campanha — aguarde alguns instantes e tente novamente.',
            );
            handleCheckoutRefreshRef.current(checkoutRefreshPayload);
          }
        } else if (
          upsellSuccess.flow !== UPSELL_FLOWS.ARTS_LIMIT &&
          upsellSuccess.flow !== UPSELL_FLOWS.SUBSCRIBED_ARTS_LIMIT
        ) {
          openedUpsellSuccess = openUpsellCheckoutSuccess(upsellSuccess, {
            campaignDownloadRef: campaignUpsellFlowRef,
            artsLimitRef: artsLimitUpsellFlowRef,
            subscribedArtsLimitRef: subscribedArtsLimitUpsellFlowRef,
          });
        }
      }

      // Start credit/download sync immediately so the user can download while
      // the success modal is open. Defer only the success toasts to modal close.
      if (openedUpsellSuccess) {
        handleCheckoutRefreshRef.current({
          ...checkoutRefreshPayload,
          showToasts: false,
        });
        savePendingCheckoutRefresh({
          ...checkoutRefreshPayload,
          showToastsOnly: true,
        });
      } else if (
        !upsellSuccess ||
        upsellSuccess.flow !== UPSELL_FLOWS.CAMPAIGN_DOWNLOAD
      ) {
        handleCheckoutRefreshRef.current(checkoutRefreshPayload);
      }

      url.searchParams.delete('addonCheckoutSuccess');
      url.searchParams.delete('downloadCheckoutSuccess');
      url.searchParams.delete('subscriptionCheckoutSuccess');
      url.searchParams.delete('upsellFlow');
      url.searchParams.delete('upsellStep');
      url.searchParams.delete('campaignArtGenerationId');
      url.searchParams.delete('session_id');
      window.history.replaceState(
        {},
        '',
        `${url.pathname}${url.search}${url.hash}`,
      );
    };

    handleCheckoutReturn();
  }, []);

  useEffect(() => {
    campaignArtGenerationIdRef.current = campaignArtGenerationId;
  }, [campaignArtGenerationId]);

  useEffect(() => {
    messageDraftByCampaignRef.current = messageDraftByCampaign;
  }, [messageDraftByCampaign]);

  useEffect(() => {
    const draftKey = getCampaignDraftKey(campaignArtGenerationId);
    const draft = messageDraftByCampaignRef.current[draftKey] || '';

    // When a new campaign gets its definitive id, keep typed text isolated in that campaign.
    if (draftKey !== NEW_CAMPAIGN_DRAFT_KEY && !draft) {
      const pendingNewCampaignDraft =
        messageDraftByCampaignRef.current[NEW_CAMPAIGN_DRAFT_KEY] || '';

      if (pendingNewCampaignDraft) {
        setMessageDraftByCampaign((prev) => ({
          ...prev,
          [draftKey]: pendingNewCampaignDraft,
          [NEW_CAMPAIGN_DRAFT_KEY]: '',
        }));
        setMessage(pendingNewCampaignDraft);
        return;
      }
    }

    if (draft) {
      setMessage(draft);
      return;
    }

    if (draftKey !== NEW_CAMPAIGN_DRAFT_KEY) {
      setMessage('');
    }
  }, [campaignArtGenerationId]);

  useEffect(() => {
    const draftKey = getCampaignDraftKey(campaignArtGenerationId);
    setMessageDraftByCampaign((prev) => {
      if ((prev[draftKey] || '') === message) {
        return prev;
      }

      const next = {
        ...prev,
        [draftKey]: message,
      };
      messageDraftByCampaignRef.current = next;
      return next;
    });
  }, [campaignArtGenerationId, message]);

  const lastQuestionRef = useRef(null);
  const pendingSocialMediaChoiceLabelRef = useRef(null);

  const [currentQuestion, setCurrentQuestion] = useState(null);
  const currentQuestionRef = useRef(null);
  useEffect(() => {
    currentQuestionRef.current = currentQuestion;
  }, [currentQuestion]);

  useEffect(() => {
    if (!isBrandNameQuestion(currentQuestion)) {
      return;
    }

    const draftKey = getCampaignDraftKey(campaignArtGenerationId);
    const draft = messageDraftByCampaignRef.current[draftKey] || '';

    if (draft.trim()) {
      setMessage(draft);
    }
  }, [currentQuestion, campaignArtGenerationId]);

  const [pendingAnswerQuestion, setPendingAnswerQuestion] = useState(null);

  const [selectedProductImages, setSelectedProductImages] = useState([]);
  const [selectedTemplateImage, setSelectedTemplateImage] = useState(null);

  const withRestoreTypingSuppressed = (message = {}) => {
    if (!suppressTypingDuringRestoreRef.current) {
      return message;
    }

    return disableTypingForBackendMessage(message);
  };

  const isParameterizationQuestion = (question) =>
    Number(question?.stepOrder) === -1;

  const shouldShowQuestionInChat = (messages = []) =>
    messages.some((message) => !!message?.frontend) ||
    suppressTypingDuringRestoreRef.current;

  const buildQuestionMessageForChat = (question, options = {}) => {
    if (!question?.question && !question?.message) {
      return null;
    }

    return injectMockComponent({
      backend: replaceBrandPlaceholder(question.question || question.message || ''),
      frontend: '',
      stepOrder: question.stepOrder,
      messageOrder: question.messageOrder,
      options: question.options || [],
      component: question.component,
      placeholders: question.placeholders,
      disableTypingAnimation:
        options.disableTypingAnimation ??
        suppressTypingDuringRestoreRef.current,
    });
  };

  useEffect(() => {
    if (!currentQuestion?.question) {
      return;
    }

    if (shouldSuppressBackendQuestionMessage(currentQuestion)) {
      return;
    }

    setMessages((prev) => {
      // Primeira pergunta: não renderiza no chat, só placeholder no input
      if (!shouldShowQuestionInChat(prev)) {
        return prev.filter(
          (message) =>
            ![
              tc('chatsocket.loading.loading_conversation'),
              tc('chatsocket.loading.starting_campaign'),
            ].includes(message?.backend),
        );
      }

      if (hasBackendMessage(prev, replaceBrandPlaceholder(currentQuestion.question))) {
        return prev;
      }
      return [...prev];
    });
  }, [currentQuestion]);

  // eslint-disable-next-line no-unused-vars
  const [campaignArts, setCampaignArts] = useState([]);
  const campaignArtsRef = useRef([]);
  const [showFullChatLayout, setShowFullChatLayout] = useState(false);
  const [markdownContent, setMarkdownContent] = useState('');
  const markdownContentRef = useRef('');
  const [strategyResult, setStrategyResult] = useState({});
  const strategyResultRef = useRef({});
  const [markdownEditSessionId] = useState(0);
  const [isStrategyLoading, setIsStrategyLoading] = useState(false);
  const isStrategyLoadingRef = useRef(false);

  useEffect(() => {
    markdownContentRef.current = markdownContent;
  }, [markdownContent]);

  useEffect(() => {
    strategyResultRef.current = strategyResult;
  }, [strategyResult]);

  useEffect(() => {
    isStrategyLoadingRef.current = isStrategyLoading;
  }, [isStrategyLoading]);

  const [activeTab, setActiveTab] = useState('estrategia');
  const [pendingApproval, setPendingApproval] = useState(null);
  const approvalActionInProgressRef = useRef(false);
  const messageSubmitInProgressRef = useRef(false);
  const [isSubmittingMessage, setIsSubmittingMessage] = useState(false);
  const strategyApprovalCallbacksRef = useRef(null);
  const pendingStrategySyncRef = useRef(null);
  const strategyRecoveryTimeoutRef = useRef(null);
  const managedTimeoutsRef = useRef(new Set());

  const releaseApprovalActionLock = () => {
    approvalActionInProgressRef.current = false;
  };

  const releaseMessageSubmitLock = () => {
    messageSubmitInProgressRef.current = false;
    setIsSubmittingMessage(false);
  };

  const lockMessageSubmit = () => {
    messageSubmitInProgressRef.current = true;
    setIsSubmittingMessage(true);
  };

  const disableCurrentApprovalOptions = () => {
    setCurrentQuestion((prev) => {
      if (!prev?.isApprovalQuestion) {
        return prev;
      }
      return {
        ...prev,
        optionsDisabled: true,
      };
    });
  };

  const restoreCurrentApprovalOptions = () => {
    setCurrentQuestion((prev) => {
      if (!prev?.isApprovalQuestion) {
        return prev;
      }
      return {
        ...prev,
        optionsDisabled: false,
      };
    });
  };

  const autoProgressAfterSkipRef = useRef(false);
  const [totalImagesArray, setTotalImagesArray] = useState(0)
  const [imgsArray, setImgsArray] = useState([])
  const imgsArrayRef = useRef([])
  const totalImagesArrayRef = useRef(0)
  const campaignPreviewCacheRef = useRef({})

  useEffect(() => {
    imgsArrayRef.current = imgsArray;
  }, [imgsArray]);



  useEffect(() => {
    totalImagesArrayRef.current = totalImagesArray;
  }, [totalImagesArray]);

  useEffect(() => {
    const campaignKey = normalizeCampaignUuid(campaignArtGenerationIdRef.current);
    if (!campaignKey) {
      return;
    }

    campaignPreviewCacheRef.current[campaignKey] = {
      imgsArray,
      totalImagesArray,
    };
  }, [imgsArray, totalImagesArray]);

  useEffect(() => {
    try {
      if (
        ['strategy_approved'].includes(
          campaignStatus,
        )
      ) {
        setActiveTab('artes');
      }
    } catch (_) {
      // noop
    }
  }, [campaignStatus]);

  latestDownloadDataRef.current = {
    campaignArtGenerationId,
    imgsArray,
    markdownContent,
  };

  const getInitialQueueSteps = () => {
    const lng = campaignLangRef.current || i18n.language || 'ptBR';
    return [
      { text: t('chatsocket.queue_steps.analyzing_data', { lng }), status: 'pending' },
      { text: t('chatsocket.queue_steps.briefing', { lng }), status: 'idle' },
      { text: t('chatsocket.queue_steps.concept', { lng }), status: 'idle' },
      { text: t('chatsocket.queue_steps.writing', { lng }), status: 'idle' },
      { text: t('chatsocket.queue_steps.visual_identity', { lng }), status: 'idle' },
      { text: t('chatsocket.queue_steps.final_strategy', { lng }), status: 'idle' },
    ];
  };
  const QUEUE_PROGRESS_INDEX_STORAGE_KEY =
    'queueStepsProgressIndexByCampaign';
  const ARTS_READY_MESSAGE_STORAGE_KEY = 'artsReadyMessageByCampaign';
  const ART_STEPS_PROGRESS_INDEX_STORAGE_KEY =
    'campaignArtStepsProgressIndexByCampaign';
  const getPersistedArtStepsProgressMap = () => {
    try {
      const raw = localStorage.getItem(ART_STEPS_PROGRESS_INDEX_STORAGE_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  };

  const getPersistedArtStepsProgress = (campaignId) => {
    const normalizedCampaignId = normalizeCampaignUuid(campaignId);
    if (!normalizedCampaignId) return null;

    const progressMap = getPersistedArtStepsProgressMap();
    const stored = Number(progressMap[normalizedCampaignId]);
    if (!Number.isFinite(stored)) return null;

    return Math.max(0, Math.floor(stored));
  };

  const persistArtStepsProgress = (campaignId, progress) => {
    const normalizedCampaignId = normalizeCampaignUuid(campaignId);
    if (!normalizedCampaignId) return;

    const parsedProgress = Number(progress);
    if (!Number.isFinite(parsedProgress)) return;

    const normalizedProgress = Math.max(0, Math.floor(parsedProgress));

    try {
      const progressMap = getPersistedArtStepsProgressMap();
      progressMap[normalizedCampaignId] = normalizedProgress;
      localStorage.setItem(
        ART_STEPS_PROGRESS_INDEX_STORAGE_KEY,
        JSON.stringify(progressMap),
      );
    } catch {
      // noop
    }
  };

  const queueProgressIndexRef = useRef(0);
  const getPersistedQueueProgressMap = () => {
    try {
      const raw = localStorage.getItem(QUEUE_PROGRESS_INDEX_STORAGE_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  };

  const getPersistedQueueProgress = (campaignId) => {
    const normalizedCampaignId = normalizeCampaignUuid(campaignId);
    if (!normalizedCampaignId) {
      return null;
    }

    const progressMap = getPersistedQueueProgressMap();
    const storedProgress = Number(progressMap[normalizedCampaignId]);
    if (!Number.isFinite(storedProgress)) {
      return null;
    }

    return Math.max(0, Math.floor(storedProgress));
  };

  const persistQueueProgress = (campaignId, progress) => {
    const normalizedCampaignId = normalizeCampaignUuid(campaignId);
    if (!normalizedCampaignId) {
      return;
    }

    const parsedProgress = Number(progress);
    if (!Number.isFinite(parsedProgress)) {
      return;
    }

    const normalizedProgress = Math.max(0, Math.floor(parsedProgress));

    try {
      const progressMap = getPersistedQueueProgressMap();
      progressMap[normalizedCampaignId] = normalizedProgress;
      localStorage.setItem(
        QUEUE_PROGRESS_INDEX_STORAGE_KEY,
        JSON.stringify(progressMap),
      );
    } catch {
      // noop
    }
  };

  const clearPersistedQueueProgress = (campaignId) => {
    const normalizedCampaignId = normalizeCampaignUuid(campaignId);
    if (!normalizedCampaignId) {
      return;
    }

    try {
      const progressMap = getPersistedQueueProgressMap();
      if (!(normalizedCampaignId in progressMap)) {
        return;
      }

      delete progressMap[normalizedCampaignId];
      localStorage.setItem(
        QUEUE_PROGRESS_INDEX_STORAGE_KEY,
        JSON.stringify(progressMap),
      );
    } catch {
      // noop
    }
  };

  const buildQueueStepsFromProgress = (progressIndex = 0) => {
    const parsedProgress = Number(progressIndex);
    const safeProgress = Number.isFinite(parsedProgress)
      ? Math.max(0, Math.floor(parsedProgress))
      : 0;
    // pendingIndex is the step currently running (0-based)
    // e.g. safeProgress=1 → step 0 is pending (Analisando dados)
    //      safeProgress=2 → step 0 complete, step 1 pending (Briefing)
    const pendingIndex = Math.max(0, safeProgress - 1);

    return getInitialQueueSteps().map((step, index) => {
      if (index < pendingIndex) {
        return { ...step, status: 'complete' };
      }
      if (index === pendingIndex) {
        return { ...step, status: 'pending' };
      }
      return { ...step, status: 'idle' };
    });
  };
  const [queueSteps, setQueueSteps] = useState(getInitialQueueSteps());
  const [showQueueProgress, setShowQueueProgress] = useState(false);

  useEffect(() => {
    const activeCampaignId = campaignArtGenerationIdRef.current;
    const storedProgress = getPersistedQueueProgress(activeCampaignId);

    if (storedProgress == null) {
      return;
    }

    queueProgressIndexRef.current = storedProgress;
    setQueueSteps(buildQueueStepsFromProgress(storedProgress));
    setShowQueueProgress(true);
    setShowFullChatLayout(true);
    setActiveTab('estrategia');
  }, []);

  const [artSteps, setArtSteps] = useState([]);
  const [showArtQueueProgress, setShowArtQueueProgress] = useState(false);

  useEffect(() => {
    const currentProgress = queueProgressIndexRef.current;
    if (queueSteps.length > 0) {
      setQueueSteps(buildQueueStepsFromProgress(currentProgress));
    }
  }, [campaignLanguage]);

  useEffect(() => {
    if (artSteps.length > 0) {
      const totalPieces = artSteps.length;
      const currentPendingIndex = artSteps.findIndex((s) => s.status === 'pending');
      const initialPendingIndex = currentPendingIndex !== -1 ? currentPendingIndex : 0;
      setArtSteps(buildArtSteps(totalPieces, initialPendingIndex, tc));
    }
  }, [campaignLanguage]);

  // Removed: This useEffect was duplicating arts-ready messages.
  // The socket event 'campaign_art_generated' now handles adding the arts-ready
  // instruction message with proper deduplication. Campaign restoration also
  // handles it via the initial message construction.

  useEffect(() => {
    try {
      const imgsLen = Array.isArray(imgsArray) ? imgsArray.length : 0;
      const stepsLen = Array.isArray(artSteps) ? artSteps.length : 0;
      if (imgsLen > 0 && stepsLen > 0 && imgsLen === stepsLen) {
        const allComplete = artSteps.every((s) => s && s.status === 'complete');
        if (!allComplete) {
          const newSteps = artSteps.map((s) => ({ ...s, status: 'complete' }));
          setArtSteps(newSteps);
          try {
            const campaignKey = normalizeCampaignUuid(campaignArtGenerationIdRef.current);
            if (campaignKey) persistArtStepsProgress(campaignKey, newSteps.length + 1);
          } catch (_) { }
          try {
            localStorage.setItem(ART_STEPS_STORAGE_KEY, JSON.stringify(newSteps));
          } catch (_) { }
        }
      }
    } catch (_) { }
  }, [imgsArray, artSteps]);

  const [referenceArt, setReferenceArt] = useState(null);
  const [isSelectingFromPreview, setIsSelectingFromPreview] = useState(false);
  const [previewAttachedImages, setPreviewAttachedImages] = useState([]);

  const [imageSelectionContext, setImageSelectionContext] = useState(null);

  const [selectedArt, setSelectedArt] = useState(null);
  /** Incrementa após edição de arte para forçar remount do <img> (cache do browser). */
  const [artPreviewRenderKey, setArtPreviewRenderKey] = useState(0);
  const [campaignPreviewSession, setCampaignPreviewSession] = useState(null);
  const [isEditingArt, setIsEditingArt] = useState(false);
  const [isApprovingArts, setIsApprovingArts] = useState(false);
  const clearArtApprovalLoading = useCallback(() => {
    setIsApprovingArts(false);
    releaseApprovalActionLock();
    setMessages((prev) => prev.filter((m) => !m?.isApprovalLoadingMessage));
  }, []);

  const emitApproveCampaignArts = useCallback((approvalCampaignId) => {
    if (!approvalCampaignId || !socketRef.current) {
      return;
    }

    setHasSelectedSchedulePublicationItem(false);
    setSelectedArt(null);
    setIsApprovingArts(true);
    appendMessagesWithSequentialOrder([
      {
        backend: tc('chatsocket.loading.next_step'),
        frontend: '',
        isApprovalLoadingMessage: true,
        disableTypingAnimation: true,
      },
    ]);
    setCurrentQuestion(null);
    releaseApprovalActionLock();

    socketRef.current.emit('approve_campaign_arts', {
      campaignArtGenerationId: approvalCampaignId,
    });

    const approvalTimeoutId = setTimeout(() => {
      setIsApprovingArts((isLoading) => {
        if (!isLoading) {
          return isLoading;
        }

        setMessages((prev) => {
          const hasApprovalLoadingMessage = prev.some(
            (message) => message?.isApprovalLoadingMessage,
          );

          if (!hasApprovalLoadingMessage) {
            return prev;
          }

          return [
            ...prev.filter((message) => !message?.isApprovalLoadingMessage),
            {
              backend: tc('chatsocket.errors.generic_error'),
              frontend: '',
              color: 'red',
            },
          ];
        });

        return false;
      });
    }, 45000);

    const clearApprovalTimeout = () => {
      clearTimeout(approvalTimeoutId);
    };

    socketRef.current.once('next_question', clearApprovalTimeout);
    socketRef.current.once('error', clearApprovalTimeout);
  }, [appendMessagesWithSequentialOrder, tc]);

  const emitApproveCampaignArtsRef = useRef(emitApproveCampaignArts);
  emitApproveCampaignArtsRef.current = emitApproveCampaignArts;

  const clearArtApprovalLoadingRef = useRef(clearArtApprovalLoading);
  clearArtApprovalLoadingRef.current = clearArtApprovalLoading;

  const [isLoadingBatchEditArtPerCampaign, setIsLoadingBatchEditArtPerCampaign] = useState({});
  const [batchEditLimitNoticeVariant, setBatchEditLimitNoticeVariant] =
    useState(null);
  const [pendingEditArtData, setPendingEditArtData] = useState(null);

  const [artScheduleData, setArtScheduleData] = useState({});
  const artScheduleDataRef = useRef({});
  const mergeArtsWithCurrentScheduleData = useCallback(
    (arts) => mergeArtsWithScheduleData(arts, artScheduleDataRef.current),
    [],
  );
  const persistPieceLegendTimeoutRef = useRef(null);
  const persistPieceScheduleTimeoutRef = useRef(null);
  const latestScheduleFieldsRef = useRef({});
  const [previewInitialTab, setPreviewInitialTab] = useState('ai');

  const steps = useMemo(() => {
    const activeStepIndex = getCampaignProgressIndex({
      campaignStatus,
      currentQuestion,
    });

    const labels = [
      tc('chatsocket.campaign_progress.description'),
      tc('chatsocket.campaign_progress.briefing'),
      tc('chatsocket.campaign_progress.strategy'),
      tc('chatsocket.campaign_progress.first_post'),
      tc('chatsocket.campaign_progress.campaign_release'),
      tc('chatsocket.campaign_progress.post_generation'),
      tc('chatsocket.campaign_progress.execution_plan'),
      tc('chatsocket.campaign_progress.download_and_schedule'),
    ];
    return labels.map((text, index) => ({
      text,
      isActive: index <= activeStepIndex,
    }));
  }, [campaignStatus, currentQuestion]);

  const canAccessArtsTab = useMemo(() => {
    const activeStepIndex = getCampaignProgressIndex({
      campaignStatus,
      currentQuestion,
    });

    return activeStepIndex >= 3;
  }, [campaignStatus, currentQuestion]);

  const [authToken, setAuthToken] = useState(null);
  const authTokenRef = useRef(null);
  const [userId, setUserId] = useState(null);
  const userIdRef = useRef(null);
  const [brandNameFromDb, setBrandNameFromDb] = useState('');
  const brandNameFromDbRef = useRef('');
  const [isUserWithoutSubscription, setIsUserWithoutSubscription] =
    useState(false);
  const isUserWithoutSubscriptionRef = useRef(false);
  const [hasCampaignDownloadPurchased, setHasCampaignDownloadPurchased] =
    useState(false);
  const hasCampaignDownloadPurchasedRef = useRef(false);
  const [canDownloadCampaigns, setCanDownloadCampaigns] = useState(false);
  const canDownloadCampaignsRef = useRef(false);
  // Keeps optimistic download unlock after addon purchase while Stripe webhook settles.
  const optimisticDownloadUnlockUntilRef = useRef(0);
  const campaignUpsellFlowRef = useRef(null);
  const artsLimitUpsellFlowRef = useRef(null);
  const subscribedArtsLimitUpsellFlowRef = useRef(null);
  const artLimitDismissContextRef = useRef(null);
  const [socialMediaSelection, setSocialMediaSelection] = useState([]);
  const socialMediaSelectionRef = useRef([]);

  useEffect(() => {
    userIdRef.current = userId;
  }, [userId]);

  useEffect(() => {
    authTokenRef.current = authToken;
  }, [authToken]);

  useEffect(() => {
    isUserWithoutSubscriptionRef.current = isUserWithoutSubscription;
  }, [isUserWithoutSubscription]);

  useEffect(() => {
    hasCampaignDownloadPurchasedRef.current = hasCampaignDownloadPurchased;
  }, [hasCampaignDownloadPurchased]);

  useEffect(() => {
    canDownloadCampaignsRef.current = canDownloadCampaigns;
  }, [canDownloadCampaigns]);

  const shouldUseSubscribedArtsLimitFlow =
    !isUserWithoutSubscription || canDownloadCampaigns;

  const hasActiveSubscriptionResponse = (response) => {
    const data = response?.data;

    return (
      response?.responseStatus === 200 &&
      !!data &&
      (!Array.isArray(data) || data.length > 0)
    );
  };

  const syncUserCanDownloadCampaigns = async () => {
    const userResponse = await UserAccountsService.getUserAccountsByLogin();
    const canDownloadAll = Boolean(userResponse?.data?.canDownloadCampaigns);

    if (canDownloadAll) {
      canDownloadCampaignsRef.current = true;
      setCanDownloadCampaigns(true);
      optimisticDownloadUnlockUntilRef.current = 0;
      return true;
    }

    // Do not clear optimistic unlock while webhook is still settling.
    if (Date.now() < optimisticDownloadUnlockUntilRef.current) {
      return true;
    }

    canDownloadCampaignsRef.current = false;
    setCanDownloadCampaigns(false);
    return false;
  };

  const applyUserCanDownloadAllCampaigns = () => {
    canDownloadCampaignsRef.current = true;
    setCanDownloadCampaigns(true);
    optimisticDownloadUnlockUntilRef.current = Date.now() + 20000;
  };

  const setCampaignDownloadPurchasedState = (purchased) => {
    const nextValue = Boolean(purchased);
    hasCampaignDownloadPurchasedRef.current = nextValue;
    setHasCampaignDownloadPurchased(nextValue);
  };

  const applyCampaignSpecificDownloadAccess = (campaignId) => {
    const activeCampaignId =
      campaignId || campaignArtGenerationIdRef.current;

    if (
      activeCampaignId &&
      activeCampaignId === campaignArtGenerationIdRef.current
    ) {
      setCampaignDownloadPurchasedState(true);
    }
  };

  const refreshSubscriptionAccess = async ({ maxAttempts = 8 } = {}) => {
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      if (await syncUserCanDownloadCampaigns()) {
        return true;
      }

      const activeSubscriptionResponse =
        await SubscriptionService.getActiveSubscription();

      if (hasActiveSubscriptionResponse(activeSubscriptionResponse)) {
        isUserWithoutSubscriptionRef.current = false;
        setIsUserWithoutSubscription(false);
        return true;
      }

      await new Promise((resolve) => {
        setTimeout(resolve, 1500);
      });
    }

    return false;
  };

  handleCheckoutRefreshRef.current = ({
    subscription = false,
    addon = false,
    download = false,
    campaignId = null,
    showToasts = true,
    showToastsOnly = false,
  } = {}) => {
    if (showToastsOnly) {
      if (subscription && showToasts) {
        notifySuccessCustom('Assinatura confirmada! Você já pode baixar suas campanhas.');
      }

      if (addon && showToasts) {
        notifySuccessCustom(tc('chatsocket.subscription.additional_confirmed'));
      }

      if (download && showToasts) {
        notifySuccessCustom('Download da campanha liberado!');
      }

      return;
    }

    if (subscription) {
      if (showToasts) {
        notifySuccessCustom('Assinatura confirmada! Você já pode baixar suas campanhas.');
      }
      refreshSubscriptionAccess();
      emitUserPlanCountUpdated({ retry: true, checkoutType: 'subscription' });
    }

    if (addon) {
      applyUserCanDownloadAllCampaigns();
      emitUserPlanCountUpdated({ retry: true, checkoutType: 'addon' });

      EventMapService.postEventMap({
        event: 'purchase_origin',
        eventData: {
          campaignId: campaignId || campaignArtGenerationIdRef.current,
          source: 'campaign',
          origin: 'campaign_immediate',
          description: 'Compra feita de dentro da campanha (necessidade imediata)',
        },
      });

      if (showToasts) {
        notifySuccessCustom(tc('chatsocket.subscription.additional_confirmed'));
      }

      const refreshCampaignDownloadAccess = async () => {
        for (let attempt = 0; attempt < 10; attempt += 1) {
          if (await syncUserCanDownloadCampaigns()) {
            break;
          }

          await new Promise((resolve) => {
            setTimeout(resolve, 1500);
          });
        }
      };

      refreshCampaignDownloadAccess();
    }

    if (download) {
      const completeDownloadPurchase = async () => {
        applyCampaignSpecificDownloadAccess(
          campaignId || campaignArtGenerationIdRef.current,
        );

        EventMapService.postEventMap({
          event: 'purchase_origin',
          eventData: {
            campaignId: campaignId || campaignArtGenerationIdRef.current,
            source: 'campaign',
            origin: 'campaign_download_single',
            description: 'Compra de download avulso da campanha',
          },
        });

        if (showToasts) {
          notifySuccessCustom('Download da campanha liberado!');
        }

        for (let attempt = 0; attempt < 8; attempt += 1) {
          const activeCampaignId =
            campaignId || campaignArtGenerationIdRef.current;
          if (!activeCampaignId) {
            break;
          }

          const response = await EventMapService.getCampaignDownloadPurchased(
            activeCampaignId,
          );

          if (response?.data?.purchased) {
            applyCampaignSpecificDownloadAccess(activeCampaignId);

            if (
              socketRef.current &&
              latestDownloadDataRef.current?.campaignArtGenerationId
            ) {
              socketRef.current.emit('download_zip_document', {
                ...latestDownloadDataRef.current,
              });
            }
            break;
          }

          await new Promise((resolve) => {
            setTimeout(resolve, 1500);
          });
        }
      };

      completeDownloadPurchase();
    }
  };

  useEffect(() => {
    registerCheckoutRefreshRunner((payload) => {
      handleCheckoutRefreshRef.current({
        ...payload,
        showToasts: payload?.showToastsOnly ? true : false,
        showToastsOnly: Boolean(payload?.showToastsOnly),
      });
    });

    return () => {
      unregisterCheckoutRefreshRunner();
    };
  }, []);

  const resolveCampaignDownloadAccess = async () =>
    checkCampaignDownloadAccess({
      campaignId: campaignArtGenerationIdRef.current,
      syncUserCanDownloadCampaigns,
      getCampaignDownloadPurchased: (campaignId) =>
        EventMapService.getCampaignDownloadPurchased(campaignId),
      onCampaignPurchased: applyCampaignSpecificDownloadAccess,
    });

  const previousCampaignForUpsellRef = useRef(null);

  useEffect(() => {
    const previousCampaignId = previousCampaignForUpsellRef.current;
    previousCampaignForUpsellRef.current = campaignArtGenerationId;

    if (
      previousCampaignId &&
      campaignArtGenerationId &&
      previousCampaignId !== campaignArtGenerationId
    ) {
      campaignUpsellFlowRef.current?.close();
    }

    let cancelled = false;

    const loadDownloadAccess = async () => {
      await syncUserCanDownloadCampaigns();

      if (cancelled || canDownloadCampaignsRef.current) {
        return;
      }

      if (!campaignArtGenerationId) {
        if (!cancelled) {
          setCampaignDownloadPurchasedState(false);
        }
        return;
      }

      const response = await EventMapService.getCampaignDownloadPurchased(
        campaignArtGenerationId,
      );

      if (!cancelled) {
        setCampaignDownloadPurchasedState(Boolean(response?.data?.purchased));
      }
    };

    loadDownloadAccess();

    return () => {
      cancelled = true;
    };
  }, [campaignArtGenerationId]);

  const [lockedSections, setLockedSections] = useState({
    socialMedia: false,
    publications: false,
  });
  const [
    hasSelectedSchedulePublicationItem,
    setHasSelectedSchedulePublicationItem,
  ] = useState(false);
  const [schedulingProgressItems, setSchedulingProgressItems] = useState([]);
  const [schedulingSummary, setSchedulingSummary] = useState(null);
  const [isSchedulingRunning, setIsSchedulingRunning] = useState(false);
  const schedulingInFlightRef = useRef(false);
  const [isUserAvaliationOpen, setIsUserAvaliationOpen] = useState(false);
  const [isAvaliationRewardModalOpen, setIsAvaliationRewardModalOpen] =
    useState(false);
  const [isSubmittingCampaignEvaluation, setIsSubmittingCampaignEvaluation] =
    useState(false);
  const [campaingAvaliationStatus, setCampaingAvaliationStatus] =
    useState(null);
  const [availableArtsCount, setAvailableArtsCount] = useState(null);
  const [artesCriadasCount, setArtesCriadasCount] = useState(0);
  const [limiteArtesCount, setLimiteArtesCount] = useState(0);
  const limiteArtesCountRef = useRef(0);
  const lastArtAvailabilitySnapshotRef = useRef(null);
  const [postQuantitySelectorResetKey, setPostQuantitySelectorResetKey] = useState(0);
  const [isCheckingDownloadAccess, setIsCheckingDownloadAccess] = useState(false);
  const [isZipDownloading, setIsZipDownloading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [hasAvailableAISuggestions, setHasAvailableAISuggestions] = useState(true);

  useEffect(() => {
    if (!artLimitFallback) {
      return;
    }

    const requiredArts = Number(artLimitFallback.requiredArts ?? 0);
    const availableArts = Number(availableArtsCount);

    if (!Number.isFinite(requiredArts) || requiredArts <= 0) {
      return;
    }

    if (Number.isFinite(availableArts) && availableArts >= requiredArts) {
      setArtLimitFallback(null);
    }
  }, [artLimitFallback, availableArtsCount]);

  useEffect(() => {
    limiteArtesCountRef.current = limiteArtesCount;
  }, [limiteArtesCount]);

  const applyArtAvailability = useCallback((artAvailability) => {
    warnIfArtLimitTotalDroppedWithoutUsageIncrease(
      lastArtAvailabilitySnapshotRef.current,
      artAvailability,
      'ChatSocketPage',
    );
    lastArtAvailabilitySnapshotRef.current =
      snapshotArtAvailability(artAvailability);

    setAvailableArtsCount(artAvailability?.available ?? null);
    setArtesCriadasCount(artAvailability?.used ?? 0);
    setLimiteArtesCount(artAvailability?.total ?? 0);
    syncArtLimits(artAvailability);
  }, [syncArtLimits]);

  const artLimitsRequestIdRef = useRef(0);
  const updateArtLimitsRef = useRef(async () => null);

  const updateArtLimits = useCallback(async () => {
    const requestId = artLimitsRequestIdRef.current + 1;
    artLimitsRequestIdRef.current = requestId;

    try {
      const artAvailability = await getArtGenerationAvailability(1, {
        allowCampaignGeneration: true,
      });

      // Ignore outdated responses so mid-generation fetches cannot overwrite
      // the real balance after debit on the final generate/edit event.
      if (requestId !== artLimitsRequestIdRef.current) {
        return artAvailability;
      }

      applyArtAvailability(artAvailability);
      return artAvailability;
    } catch (error) {
      console.error('Error updating art limits:', error);
      return null;
    }
  }, [applyArtAvailability]);

  updateArtLimitsRef.current = updateArtLimits;

  useEffect(() => {
    const handlePlanUpdate = async (event) => {
      const shouldRetry = Boolean(event?.detail?.retry);

      if (!shouldRetry) {
        await updateArtLimits();
        return;
      }

      await pollArtCreditsUntilUpdated({
        getBaselineTotal: () => limiteArtesCountRef.current,
        refresh: async () => {
          const artAvailability = await getArtGenerationAvailability(1, {
            allowCampaignGeneration: true,
          });
          applyArtAvailability(artAvailability);
          return artAvailability;
        },
      });
    };
    window.addEventListener('user-plan-count-updated', handlePlanUpdate);
    return () => {
      window.removeEventListener('user-plan-count-updated', handlePlanUpdate);
    };
  }, [applyArtAvailability, updateArtLimits]);

  const isSchedulingContextActive =
    currentQuestion?.component === 'ScheduleSelector' ||
    currentQuestion?.component === 'SchedulingProgress';

  const hasAuthenticatedSocialMedia =
    Array.isArray(socialMediaSelection) && socialMediaSelection.length > 0;

  const isSocialMediaReadOnly =
    isSchedulingRunning || lockedSections.publications;

  const canEditPublicationSchedule =
    hasAuthenticatedSocialMedia && !isSchedulingRunning;

  const isPublicationReadOnly =
    !canEditPublicationSchedule;

  const isCalendarPreviewEnabled =
    hasAuthenticatedSocialMedia &&
    hasSelectedSchedulePublicationItem &&
    (isSchedulingContextActive || isSchedulingRunning || canEditPublicationSchedule);

  const shouldRenderUserAvaliationModal =
    campaingAvaliationStatus !== true &&
    !isCampaignEvaluationDismissedInSession();

  const isArtsApproved = useMemo(() => {
    return campaignStatus === 'arts_approved' || campaignStatus === 'completed';
  }, [campaignStatus]);

  const canUseAI = useMemo(() => {
    return campaignStatus === 'arts_ready';
  }, [campaignStatus]);

  useEffect(() => {
    socialMediaSelectionRef.current = socialMediaSelection;
  }, [socialMediaSelection]);

  const isCampaignStarted = useMemo(() => {
    return !!campaignArtGenerationId || messages.some((m) => !!m.frontend);
  }, [campaignArtGenerationId, messages]);

  const showAISuggestionBanner = useMemo(() => {
    const hasUserMessages = messages.some((m) => !!m.frontend);
    const isAtCampaignStartStep =
      currentQuestion?.stepOrder === 0 &&
      currentQuestion?.messageOrder === 0;

    const showBanner =
      !hasUserMessages &&
      isAtCampaignStartStep &&
      campaignStatus === 'in_progress' &&
      hasAvailableAISuggestions;

    return showBanner;
  }, [messages, currentQuestion, campaignStatus, hasAvailableAISuggestions]);

  useEffect(() => {
    if (!hasAuthenticatedSocialMedia) {
      setHasSelectedSchedulePublicationItem(false);
    }
  }, [hasAuthenticatedSocialMedia]);

  const handleOpenStepsImageSelector = (context) => {
    if (selectedArt) {
      setIsSelectingFromPreview(true);
    }
    setImageSelectionContext(context);
    setIsProductImageSelectorOpen(true);
  };

  const handleReferenceArtSelect = (imageData) => {
    if (imageData) {
      setReferenceArt({
        ...imageData,
        fileDataBase64: imageData.base64,
      });
    } else {
      setReferenceArt(null);
    }
  };

  const handleUpdateStrategy = (e) => {
    setMarkdownContent(e);
    setIsStrategyLoading(true);

    const updatedStrategyResult = {
      ...strategyResult,
      strategyHeadlinesAndIdentity: {
        ...(typeof strategyResult?.strategyHeadlinesAndIdentity === 'object'
          ? strategyResult.strategyHeadlinesAndIdentity
          : {}),
        markdown: e,
      },
    };

    setStrategyResult(updatedStrategyResult);

    // Show processing backend message immediately when user updates markdown
    setMessages((prev) =>
      appendBackendMessageIfMissing(prev, {
        backend: tc('chatsocket.loading.strategy_processing'),
        frontend: '',
        isStrategyProcessingMessage: true,
      }),
    );

    socketRef.current.emit('update_strategy', {
      userAccountId: userId,
      authToken,
      userInput: message,
      strategyResult: updatedStrategyResult,
      campaignArtGenerationId,
    });
    scheduleStrategyRecovery('update_strategy', campaignArtGenerationId);
  };

  const resolveStrategyPayload = (value = {}) => {
    if (!value || typeof value !== 'object') {
      return {};
    }

    if (value?.strategyHeadlinesAndIdentity) {
      return value;
    }

    if (value?.strategyResult && typeof value.strategyResult === 'object') {
      return value.strategyResult;
    }

    if (
      value?.results &&
      typeof value.results === 'object' &&
      !Array.isArray(value.results)
    ) {
      return value.results;
    }

    return value;
  };

  const getStrategyDocument = (value = {}) => {
    const strategyPayload = resolveStrategyPayload(value);
    const rawDocument = strategyPayload?.strategyHeadlinesAndIdentity;

    if (typeof rawDocument === 'string') {
      return {
        markdown: rawDocument,
        pieces: [],
      };
    }

    return {
      markdown: rawDocument?.markdown || strategyPayload?.markdown || '',
      pieces: Array.isArray(rawDocument?.pieces) ? rawDocument.pieces : [],
    };
  };

  const getStrategyMarkdown = (value = {}) =>
    getStrategyDocument(value).markdown;
  const getStrategyPieces = (value = {}) => getStrategyDocument(value).pieces;
  const resolveStrategyTotalPieces = (strategyResult = {}) => {
    const strategyPiecesCount = Array.isArray(getStrategyPieces(strategyResult))
      ? getStrategyPieces(strategyResult).length
      : 0;
    const artDirectorPiecesCount = Array.isArray(
      strategyResult?.artDirector?.pieces_execution,
    )
      ? strategyResult.artDirector.pieces_execution.length
      : 0;
    const directTotalPieces = Number(strategyResult?.totalPieces);

    const candidates = [
      strategyPiecesCount,
      artDirectorPiecesCount,
      Number.isFinite(directTotalPieces) ? directTotalPieces : 0,
    ].filter((value) => Number.isFinite(value) && value > 0);

    if (!candidates.length) {
      return 0;
    }

    return Math.max(...candidates);
  };
  const removeStrategySyncDelayMessages = (items = []) =>
    items.filter((item) => !item?.isStrategySyncDelayMessage);

  const setManagedTimeout = (callback, delay) => {
    const timeoutId = setTimeout(() => {
      managedTimeoutsRef.current.delete(timeoutId);
      callback();
    }, delay);

    managedTimeoutsRef.current.add(timeoutId);
    return timeoutId;
  };

  const clearManagedTimeout = (timeoutId) => {
    if (!timeoutId) {
      return;
    }

    clearTimeout(timeoutId);
    managedTimeoutsRef.current.delete(timeoutId);
  };

  const clearAllManagedTimeouts = () => {
    managedTimeoutsRef.current.forEach((timeoutId) => {
      clearTimeout(timeoutId);
    });

    managedTimeoutsRef.current.clear();
  };

  const syncStrategyFromSocket = (nextStrategyResult) => {
    if (!nextStrategyResult) {
      return;
    }

    clearPendingStrategySync();
    setIsStrategyLoading(false);
    releaseMessageSubmitLock();
    setStrategyResult(nextStrategyResult);
    setMarkdownContent(getStrategyMarkdown(nextStrategyResult));
  };

  const clearPendingStrategySync = () => {
    pendingStrategySyncRef.current = null;

    if (strategyRecoveryTimeoutRef.current) {
      clearManagedTimeout(strategyRecoveryTimeoutRef.current);
      strategyRecoveryTimeoutRef.current = null;
    }
  };

  const scheduleStrategyRecovery = (
    stepName,
    resolvedCampaignId = campaignArtGenerationIdRef.current,
    baselineMarkdown = markdownContentRef.current,
  ) => {
    clearPendingStrategySync();

    if (!resolvedCampaignId || !userId || !authToken) {
      return;
    }

    pendingStrategySyncRef.current = {
      campaignArtGenerationId: resolvedCampaignId,
      stepName,
      attempts: 0,
      baselineMarkdown,
    };

  };

  const applyStrategyReadyState = (data) => {
    const strategyData = resolveStrategyPayload(data);
    const isStrategyAlreadyApproved = isStrategyApprovedPersisted(
      data?.campaignArtGenerationId,
    );

    setShowFullChatLayout(true);
    setQueueSteps(
      getInitialQueueSteps()?.map((s) => ({ ...s, status: 'complete' })),
    );
    setShowQueueProgress(true);
    setMarkdownContent(getStrategyMarkdown(strategyData));
    setStrategyResult(strategyData);
    setIsStrategyLoading(false);
    releaseMessageSubmitLock();
    setActiveTab('estrategia');

    // appendMessagesWithSequentialOrder([
    //   {
    //     backend:STRATEGY_READY_REVIEW_MESSAGE,
    //     frontend:''
    //   }
    // ])

    if (
      data.campaignArtGenerationId &&
      socketRef.current &&
      !isStrategyAlreadyApproved
    ) {
      setStrategyApprovalState(data.campaignArtGenerationId, strategyData);
    } else if (isStrategyAlreadyApproved) {
      setPendingApproval(null);
      setCurrentQuestion(null);
    }
  };

  const openStrategyEditQuestion = () => {
    const question = tc('chatsocket.strategy.edit_question');
    setActiveTab('estrategia');
    appendMessagesWithSequentialOrder([
      {
        backend: question,
        frontend: '',
      },
    ]);

    setCurrentQuestion({
      stepOrder: null,
      stepName: 'edit_strategy',
      messageOrder: 7.3,
      question,
      options: [],
    });
  };

  const openStrategyRejectQuestion = () => {
    const question = tc('chatsocket.strategy.reject_question');
    setActiveTab('estrategia');

    // appendMessagesWithSequentialOrder([{
    //   backend: question,
    //   frontend: '',
    // },])

    setCurrentQuestion({
      stepOrder: null,
      stepName: 'reject_strategy',
      question,
      options: [{ name: tc('chatsocket.buttons.regenerate_from_scratch'), type: 'MOCK_REGERAR_AGORA' }],
      isMockOption: true,
    });
  };

  const openBatchArtEditQuestion = (question, actionType = 'edit') => {
    setActiveTab('artes');
    setBatchEditActionType(actionType);
    setSelectedArt(null);
    setArtLimitFallback(null);
    setBatchEditLimitNoticeVariant(null);

    appendMessagesWithSequentialOrder([
      {
        backend: question,
        frontend: '',
      },
    ]
    )
    ;
    setCurrentQuestion({
      stepOrder: null,
      stepName: 'batch_edit_art',
      messageOrder: 7.3,
      question,
      options: [],
    });
  };

  const buildStrategyPdfMessage = (strategyData) => ({
    backend: tc('chatsocket.strategy.approved_pdf'),
    frontend: '',
    component: 'StrategyDownloader',
    isStrategyPdfMessage: true,
    children: (
      <div style={{ marginTop: 10 }}>
        <DownloadButton
          onClickDownload={async () =>
            await handleDownloadPdf(
              getStrategyMarkdown(
                strategyResultRef.current || strategyData || {},
              ),
            )
          }
          fileName={tc('chatsocket.pdf_filename')}
        />
      </div>
    ),
  });

  const getNextTransientMessagePosition = (items = []) => {
    const sortedItems = Array.isArray(items) ? [...items] : [];

    sortedItems.sort((a, b) => {
      const orderA = getFiniteOrderValue(a?.messageOrder);
      const orderB = getFiniteOrderValue(b?.messageOrder);

      if (orderA != null && orderB != null && orderA !== orderB) {
        return orderA - orderB;
      }

      return 0;
    });

    const lastMessage = sortedItems[sortedItems.length - 1];
    const messageOrder = getFiniteOrderValue(lastMessage?.messageOrder) ?? 0;

    return {
      stepOrder: 0,
      messageOrder: messageOrder + 0.1,
    };
  };

  const buildArtsReadyInstructionMessage = (totalArts, mode = 'generated') => ({
    backend: `${mode === 'updated'
      ? tc('chatsocket.strategy.arts_batch_edit_success', { totalArts })
      : mode === 'regenerated'
        ? tc('chatsocket.strategy.arts_regenerated_success', { totalArts })
        : tc('chatsocket.strategy.arts_generated_success', { totalArts })
    }\n\n${tc('chatsocket.strategy.arts_instruction_complete')}\n${tc('chatsocket.strategy.arts_instruction_visual')}\n${tc('chatsocket.strategy.arts_instruction_regenerate')}\n${tc('chatsocket.strategy.arts_instruction_individual')}`,
    frontend: '',
    // Prevent re-typing when transient loading messages are appended/removed.
    disableTypingAnimation: true,
    isArtsReadyInstructionMessage: true,
    mode,
  });

  const buildStrategyApproval = (approvalCampaignId, currentStrategyResult, isResumeCampaign) => {
    return {
      type: 'strategy',
      data: {
        campaignArtGenerationId: approvalCampaignId,
        processedData: currentStrategyResult,
      },
      onApprove: async (approvalData) => {
        const totalPieces = resolveStrategyTotalPieces(currentStrategyResult);
        const hasBalance = await checkArtBalance(totalPieces);
        if (!hasBalance) {
          releaseApprovalActionLock();
          restoreCurrentApprovalOptions();
          openArtLimitPaywall({
            source: 'strategy_approval',
            requiredArts: totalPieces,
            eventContext: 'strategy_approval',
          });
          return;
        }

        if (campaignArtGenerationIdRef.current !== approvalCampaignId) {
          releaseApprovalActionLock();
          return;
        }

        persistStrategyApproval(approvalData?.campaignArtGenerationId, true);

        setImgsArray([]);
        setTotalImagesArray(totalPieces);
        setCampaignStatus('art_generation_in_progress');
        const initialArtSteps = buildArtSteps(totalPieces, 0, tc);
        setArtSteps(initialArtSteps);
        localStorage.setItem(
          ART_STEPS_STORAGE_KEY,
          JSON.stringify(initialArtSteps),
        );

        setShowArtQueueProgress(true);
        appendMessagesWithSequentialOrder(
          [
            {
              ...buildStrategyPdfMessage(currentStrategyResult),
            },

          ]
        );
        setCurrentQuestion(null);
        releaseApprovalActionLock();

        setTimeout(() => {
          if (campaignArtGenerationIdRef.current !== approvalCampaignId) {
            return;
          }
          appendMessagesWithSequentialOrder([
            {
              backend: tc('chatsocket.loading.generating_arts'),
              frontend: '',
              isArtProgressMessage: true,
              component: 'ArtQueueProgress',
            },
          ]
          )
        }, 2000);

        if (!isResumeCampaign) {
          const approvedCampaignName = String(
            approvalData?.processedData?.strategyHeadlinesAndIdentity?.campaignName ||
            currentStrategyResult?.strategyHeadlinesAndIdentity?.campaignName ||
            campaignName ||
            '',
          ).trim();
          if (approvedCampaignName && approvalData?.campaignArtGenerationId) {
            updateCampaignInList(approvalData.campaignArtGenerationId, {
              name: approvedCampaignName,
              campaignName: approvedCampaignName,
            });
          }

          socketRef.current.emit('generate_campaign_art', {
            campaignArtGenerationId: approvalData.campaignArtGenerationId,
            authToken,
            messages: [
              {
                frontend: tc('chatsocket.buttons.approve_strategy'),
              },
              {
                backend: tc('chatsocket.strategy.approved_pdf'),
                component: 'StrategyDownloader'
              }
            ]
          });
        }
      },
      onEdit: () => {
        openStrategyEditQuestion();
      },
      onReject: () => {
        openStrategyRejectQuestion();
      },
    };
  };

  const setStrategyApprovalState = (approvalCampaignId, nextStrategyResult) => {
    if (isStrategyApprovedPersisted(approvalCampaignId)) {
      setPendingApproval(null);
      setCurrentQuestion(null);
      return;
    }

    strategyApprovalCallbacksRef.current = {
      campaignId: approvalCampaignId,
      strategyResult: nextStrategyResult,
    };
    setPendingApproval(
      buildStrategyApproval(approvalCampaignId, nextStrategyResult),
    );

    setCurrentQuestion(
      createApprovalQuestion({
        approve: tc('chatsocket.buttons.approve_strategy'),
        edit: tc('chatsocket.buttons.edit'),
        reject: tc('chatsocket.buttons.reject'),
      }),
    );
  };

  const selectedItems = useMemo(
    () => ({
      ProductImageSelector: {
        items: selectedProductImages,
        onRemove: (index) =>
          setSelectedProductImages((prev) =>
            prev.filter((_, i) => i !== index),
          ),
        onEdit: () => {
          if (currentQuestion?.component === 'ProductImageSelector') {
            setIsProductImageSelectorOpen(true);
          }
        },
      },
      VisualStyleSelector: {
        items: selectedTemplateImage ? [selectedTemplateImage] : [],
        onRemove: () => {
          setSelectedTemplateImage(null);
          setPendingAnswerQuestion(null);
          setMessage('');
        },
        onEdit: () => {
          if (currentQuestion?.component === 'VisualStyleSelector') {
            setIsVisualStyleSelectorOpen(true);
          }
        },
      },
      ReferenceArt: {
        items: referenceArt ? [referenceArt] : [],
        onRemove: () => setReferenceArt(null),
      },
    }),
    [
      selectedProductImages,
      selectedTemplateImage,
      currentQuestion,
      referenceArt,
    ],
  );

  const applyScheduleToCampaignArt = useCallback((pieceNumber, schedulePatch = {}) => {
    const updater = (art) => {
      if (!art || Number(art.piece_number) !== pieceNumber) {
        return art;
      }
      return {
        ...art,
        ...schedulePatch,
      };
    };

    if (Array.isArray(campaignArtsRef.current)) {
      campaignArtsRef.current = campaignArtsRef.current.map(updater);
      setCampaignArts(campaignArtsRef.current);
    }

    setImgsArray((prev) => prev.map(updater));
    setSelectedArt((prev) =>
      prev && Number(prev.piece_number) === pieceNumber ? updater(prev) : prev,
    );
  }, []);

  const handleUpdateArtSchedule = (artKey, field, value) => {
    setArtScheduleData((prev) => {
      const updated = {
        ...prev,
        [artKey]: { ...prev[artKey], [field]: value },
      };
      artScheduleDataRef.current = updated;
      return updated;
    });

    if (field === 'caption') {
      const pieceNumber = Number(artKey);
      if (!Number.isFinite(pieceNumber) || pieceNumber < 1) {
        return;
      }
      if (persistPieceLegendTimeoutRef.current) {
        clearTimeout(persistPieceLegendTimeoutRef.current);
      }
      persistPieceLegendTimeoutRef.current = setTimeout(() => {
        persistPieceLegendTimeoutRef.current = null;
        const socket = socketRef.current;
        const campaignId = campaignArtGenerationIdRef.current;
        const uid = userIdRef.current;
        const token = authTokenRef.current;
        if (!socket || !campaignId || !uid || !token) {
          return;
        }
        socket.emit('persist_piece_legend', {
          campaignArtGenerationId: campaignId,
          userAccountId: uid,
          authToken: token,
          pieceNumber,
          legend: typeof value === 'string' ? value : '',
        });
      }, 650);
      return;
    }

    if (field !== 'publishDate' && field !== 'publishTime') {
      return;
    }

    const pieceNumber = Number(artKey);
    if (!Number.isFinite(pieceNumber) || pieceNumber < 1) {
      return;
    }

    latestScheduleFieldsRef.current[pieceNumber] = {
      ...(latestScheduleFieldsRef.current[pieceNumber] || {}),
      [field]: value,
    };

    if (persistPieceScheduleTimeoutRef.current) {
      clearTimeout(persistPieceScheduleTimeoutRef.current);
    }

    persistPieceScheduleTimeoutRef.current = setTimeout(async () => {
      persistPieceScheduleTimeoutRef.current = null;

      const { publishDate, publishTime } =
        latestScheduleFieldsRef.current[pieceNumber] || {};

      if (!isCompleteScheduleDateTimeFields(publishDate, publishTime)) {
        return;
      }

      const scheduleDate = parseDateFromArt({ publishDate, publishTime });
      if (!scheduleDate) {
        return;
      }

      const publicationDateTime = scheduleDate.toISOString();
      const currentArt = (campaignArtsRef.current || []).find(
        (art) => Number(art.piece_number) === pieceNumber,
      );

      applyScheduleToCampaignArt(pieceNumber, {
        publishDate,
        publishTime,
        publicationDateTime,
        scheduledAt: publicationDateTime,
        publicationId: currentArt?.publicationId,
        scheduleStatus: currentArt?.scheduleStatus,
        socialMedias: currentArt?.socialMedias,
        socialMediaSettings: currentArt?.socialMediaSettings,
        legend: currentArt?.legend,
        headline: currentArt?.headline,
        imageUrl: currentArt?.imageUrl || currentArt?.url,
        url: currentArt?.url || currentArt?.imageUrl,
      });

      const socket = socketRef.current;
      const campaignId = campaignArtGenerationIdRef.current;
      const uid = userIdRef.current;
      const token = authTokenRef.current;

      if (socket && campaignId && uid && token) {
        socket.emit('persist_piece_schedule', {
          campaignArtGenerationId: campaignId,
          userAccountId: uid,
          authToken: token,
          pieceNumber,
          publishDate,
          publishTime,
        });
      }
    }, 650);
  };

  const getArtCaption = (art = {}) => {
    const candidates = [
      art?.legend,
      art?.legenda,
      art?.caption,
      art?.postCaption,
    ];

    const found = candidates.find(
      (value) => typeof value === 'string' && value.trim().length > 0,
    );

    return found ? found.trim() : '';
  };

  const ensureArtScheduleDataInitialized = (art) => {
    if (!art) return null;
    const artKey = art.piece_number;
    const existing = artScheduleDataRef.current[artKey];
    const artCaption = getArtCaption(art);

    if (!existing) {
      const fromArt = art.publishDate && art.publishTime
        ? { publishDate: art.publishDate, publishTime: art.publishTime }
        : formatDateToScheduleFields(art.publicationDateTime);

      const entry = {
        publishDate: fromArt.publishDate || '',
        publishTime: fromArt.publishTime || '',
        caption: artCaption,
        originalCaption: artCaption,
        selectedNetworks: normalizeNetworkList(art.socialMedias || []),
      };

      artScheduleDataRef.current = {
        ...artScheduleDataRef.current,
        [artKey]: entry,
      };
      setArtScheduleData((prev) => ({ ...prev, [artKey]: entry }));
    } else {
      const updates = {};
      if (!existing.caption && artCaption) updates.caption = artCaption;
      if (
        !isCompleteScheduleDateTimeFields(existing.publishDate, existing.publishTime)
      ) {
        if (art.publishDate || art.publishTime) {
          updates.publishDate = art.publishDate || existing.publishDate || '';
          updates.publishTime = art.publishTime || existing.publishTime || '';
        } else if (art.publicationDateTime) {
          const fromArt = formatDateToScheduleFields(art.publicationDateTime);
          updates.publishDate = fromArt.publishDate;
          updates.publishTime = fromArt.publishTime;
        }
      }
      if (Object.keys(updates).length > 0) {
        const updated = { ...existing, ...updates };
        artScheduleDataRef.current = {
          ...artScheduleDataRef.current,
          [artKey]: updated,
        };
        setArtScheduleData((prev) => ({ ...prev, [artKey]: updated }));
      }
    }
    return art;
  };

  const handleOpenPreview = (art) => {
    ensureArtScheduleDataInitialized(art);

    const artAsImage = { ...art, url: art.imageUrl, title: art.piece_name };
    setHasSelectedSchedulePublicationItem(true);
    setSelectedArt(artAsImage);
    setActiveTab('artes');
    setPreviewInitialTab('edit');
  };

  const updateSchedulingProgressItem = (pieceNumber, data) => {
    setSchedulingProgressItems((previous) =>
      previous.map((item) =>
        item.pieceNumber === pieceNumber ? { ...item, ...data } : item,
      ),
    );
  };

  const buildInitialSchedulingItems = (arts = []) =>
    arts.map((art, index) => ({
      pieceNumber: art?.piece_number ?? index + 1,
      pieceName: art?.piece_name || `Post ${art?.piece_number ?? index + 1}`,
      status:
        art?.publicationId && art?.scheduleStatus === 'scheduled'
          ? 'scheduled'
          : 'pending',
      errorMessage: art?.errorMessage || null,
    }));

  const runRealScheduling = async () => {
    if (
      !currentQuestion ||
      currentQuestion.component !== 'SchedulingProgress'
    ) {
      return;
    }

    if (schedulingInFlightRef.current) {
      return;
    }

    const activeCampaignId = campaignArtGenerationIdRef.current;
    const isSchedulingForActiveCampaign = () =>
      normalizeCampaignUuid(campaignArtGenerationIdRef.current) ===
      normalizeCampaignUuid(activeCampaignId);

    const artsToSchedule = mergeArtsWithCurrentScheduleData(
      Array.isArray(campaignArtsRef.current) ? campaignArtsRef.current : [],
    );

    if (!artsToSchedule.length) {
      if (!isSchedulingForActiveCampaign()) {
        return;
      }
      handleSelectOption('MOCK_SCHEDULING_COMPLETED', []);
      return;
    }

    schedulingInFlightRef.current = true;
    setIsSchedulingRunning(true);
    setSchedulingSummary(null);
    setSchedulingProgressItems(buildInitialSchedulingItems(artsToSchedule));

    try {
      const schedulingResult = await scheduleCampaignPublications({
        arts: artsToSchedule,
        socialSelection: socialMediaSelectionRef.current,
        clientData,
        campaignId: activeCampaignId,
        onStatusChange: (pieceNumber, statusData) => {
          if (!isSchedulingForActiveCampaign()) {
            return;
          }
          updateSchedulingProgressItem(pieceNumber, {
            status: statusData?.status || 'failed',
            errorMessage: statusData?.errorMessage || null,
          });
        },
      });

      if (!isSchedulingForActiveCampaign()) {
        return;
      }

      const updatedArts = schedulingResult?.updatedArts || artsToSchedule;
      const summary = schedulingResult?.summary || {
        total: updatedArts.length,
        scheduled: 0,
        failed: updatedArts.length,
      };

      campaignArtsRef.current = updatedArts;
      setCampaignArts(updatedArts);
      setSchedulingSummary(summary);

      if (socketRef.current && activeCampaignId) {
        socketRef.current.emit('update_campaign_arts', {
          campaignArtGenerationId: activeCampaignId,
          arts: updatedArts,
        });
      }

      handleSelectOption('MOCK_SCHEDULING_COMPLETED', updatedArts);
    } catch (error) {
      if (!isSchedulingForActiveCampaign()) {
        return;
      }

      const errorMessage =
        error?.message || 'Erro ao executar o agendamento das publicacoes.';
      setSchedulingSummary({
        total: artsToSchedule.length,
        scheduled: 0,
        failed: artsToSchedule.length,
      });

      setSchedulingProgressItems((previous) =>
        previous.map((item) =>
          item.status === 'scheduled'
            ? item
            : { ...item, status: 'failed', errorMessage },
        ),
      );

      handleSelectOption(
        'MOCK_SCHEDULING_COMPLETED',
        artsToSchedule.map((art) => ({
          ...art,
          scheduleStatus:
            art?.scheduleStatus === 'scheduled' ? 'scheduled' : 'failed',
          errorMessage:
            art?.scheduleStatus === 'scheduled' ? null : errorMessage,
        })),
      );
    } finally {
      if (isSchedulingForActiveCampaign()) {
        setIsSchedulingRunning(false);
      }
      schedulingInFlightRef.current = false;
    }
  };

  useEffect(() => {
    if (currentQuestion?.component !== 'SchedulingProgress') {
      return;
    }

    runRealScheduling();
  }, [currentQuestion]);

  const renderDynamicComponent = (msg) => {
    if (msg.component === 'ScheduleSelector') {
      return (
        <ScheduleSelector
          arts={campaignArts}
          artScheduleData={artScheduleData}
          onPreviewArt={handleOpenPreview}
          onFinalize={(selectedArts) =>
            handleSelectOption('MOCK_SCHEDULE_FINALIZED', selectedArts)
          }
          isReadOnly={isPublicationReadOnly}
          language={campaignLanguage}
        />
      );
    }
    if (msg.component === 'SchedulingProgress') {
      return (
        <SchedulingProgress
          items={schedulingProgressItems}
          summary={schedulingSummary}
          isRunning={isSchedulingRunning}
          language={campaignLanguage}
        />
      );
    }

    if (msg.component === 'DownloadZipCard') {
      return <DownloadZipCard onClick={handleDownloadZipDocument} disabled={isZipDownloading} />;
    }

    if (msg.component === 'SocialMediaSelector') {
      return (
        <SocialMediaSelector
          campaignArtGenerationId={campaignArtGenerationId}
          onFinalize={(selectedNetworks) =>
            handleSelectOption('MOCK_SOCIAL_MEDIA_FINALIZED', selectedNetworks)
          }
          isReadOnly={isSocialMediaReadOnly}
          initialSelection={socialMediaSelection}
          language={campaignLanguage}
        />
      );
    }

    if (msg.component === 'StrategyStepsList') {
      return (

        <StepsList
          title={tc('chatsocket.chat.creating_strategy')}
          steps={queueSteps}
        />
      )
    }

    if (msg.component === 'PostQuantitySelector') {
      return (
        <PostQuantitySelector
          key={postQuantitySelectorResetKey}
          selectedValue={msg.answer}
          availableArts={availableArtsCount}
          onFinalize={(quantity) => handleSelectOption('MOCK_POST_QUANTITY_FINALIZED', quantity)}
          language={campaignLanguage}
        />
      );
    }

    if (msg.component === 'ColorPaletteSelector') {
      const paletteAnswerRaw = msg.answer;
      const paletteAnswer =
        typeof paletteAnswerRaw === 'string' && paletteAnswerRaw.includes(',')
          ? paletteAnswerRaw.split(',').map((c) => c.trim()).filter(Boolean)
          : Array.isArray(paletteAnswerRaw)
            ? paletteAnswerRaw
            : [];
      const paletteInitialData =
        paletteAnswer.length >= 3
          ? {
            primaryColor: paletteAnswer[0],
            secondaryColor: paletteAnswer[1],
            tertiaryColor: paletteAnswer[2],
            complementaryColor1: paletteAnswer[3] || '',
            complementaryColor2: paletteAnswer[4] || '',
          }
          : null;
      // If palette was confirmed, always render read-only regardless of cached children
      if (paletteConfirmed) {
        return (
          <PaletteSelector
            isReadOnly
            initialData={paletteInitialData}
            onComplete={() => { }}
          />
        );
      }
      // Before confirmation, preserve interactive children if available
      if (msg.children) {
        return msg.children;
      }
      return (
        <PaletteSelector
          isReadOnly
          initialData={paletteInitialData}
          onComplete={() => { }}
        />
      );
    }

    return msg.children || null;
  };

  const renderDynamicComponentRef = useRef(renderDynamicComponent);
  renderDynamicComponentRef.current = renderDynamicComponent;

  const stableRenderDynamicComponent = useCallback((msg) => {
    return renderDynamicComponentRef.current(msg);
  }, [isZipDownloading]);

  const isFirstCampaignQuantityFlow = (
    questionOrCampaignId = null,
    campaignId = campaignArtGenerationIdRef.current,
  ) => {
    const maybeFirstCampaignFlag =
      questionOrCampaignId && typeof questionOrCampaignId === 'object'
        ? questionOrCampaignId?.isFirstCampaign
        : null;

    if (maybeFirstCampaignFlag === true) {
      return true;
    }

    if (maybeFirstCampaignFlag === false) {
      return false;
    }

    const normalizedCampaignId = normalizeCampaignUuid(
      typeof questionOrCampaignId === 'string'
        ? questionOrCampaignId
        : campaignId,
    );
    const normalizedFirstCampaignId = normalizeCampaignUuid(firstCampaignIdRef.current);
    if (
      normalizedCampaignId &&
      normalizedFirstCampaignId &&
      normalizedCampaignId === normalizedFirstCampaignId
    ) {
      return true;
    }

    return false;
  };

  const shouldSuppressBackendQuestionMessage = (message = {}) =>
    message?.component === 'PostQuantitySelector' &&
    isFirstCampaignQuantityFlow(message);

  const emitFixedQuantityForFirstCampaign = (
    question,
    campaignId = campaignArtGenerationIdRef.current,
  ) => {
    if (!question || question.component !== 'PostQuantitySelector') {
      return false;
    }

    const resolvedCampaignId = normalizeCampaignUuid(campaignId);
    if (!resolvedCampaignId || !isFirstCampaignQuantityFlow(question, resolvedCampaignId)) {
      return false;
    }

    if (!socketRef.current) {
      return false;
    }

    if (resolvedCampaignId && firstCampaignQuantityAutoAnswerRef.current.has(resolvedCampaignId)) {
      return true;
    }

    if (resolvedCampaignId) {
      firstCampaignQuantityAutoAnswerRef.current.add(resolvedCampaignId);
    }

    socketRef.current.emit('answer_question', {
      campaignArtGenerationId: resolvedCampaignId,
      stepOrder: question.stepOrder,
      messageOrder: question.messageOrder,
      answer: ['4'],
      lang: localStorage.getItem('i18nextLng') || 'ptBR',
    });

    return true;
  };

  const injectMockComponent = (messageObj) => {
    switch (messageObj.component) {
    case 'StrategyDownloader':
      messageObj.children = (
        <div style={{ marginTop: 10 }}>
          <DownloadButton
            onClickDownload={async () =>
              await handleDownloadPdf(
                getStrategyMarkdown(strategyResultRef.current || strategyResult || {}),
              )
            }
            fileName={tc('chatsocket.pdf_filename')}
          />
        </div>
      );
      break;
    case 'DownloadZipCard':
      messageObj.children = <DownloadZipCard onClick={handleDownloadZipDocument} disabled={isZipDownloading} />;
      break;
    case 'SocialMediaSelector':
      messageObj.children = null;
      break;
    case 'ScheduleSelector':
      messageObj.children = (
        <ScheduleSelector
          arts={campaignArts}
          artScheduleData={artScheduleData}
          onPreviewArt={handleOpenPreview}
          onFinalize={(selectedArts) => handleSelectOption('MOCK_SCHEDULE_FINALIZED', selectedArts)}
          language={campaignLanguage}
        />
      );
      break;
    case 'SchedulingProgress':
      messageObj.children = (
        <SchedulingProgress
          items={schedulingProgressItems}
          summary={schedulingSummary}
          isRunning={isSchedulingRunning}
          language={campaignLanguage}
        />
      );
      break;
    case 'ColorPaletteSelector':
      {
        const paletteAnswerRaw = messageObj.answer;
        const paletteAnswer =
            typeof paletteAnswerRaw === 'string' && paletteAnswerRaw.includes(',')
              ? paletteAnswerRaw.split(',').map((c) => c.trim()).filter(Boolean)
              : Array.isArray(paletteAnswerRaw)
                ? paletteAnswerRaw
                : [];
        const paletteInitialData =
            paletteAnswer.length >= 3
              ? {
                primaryColor: paletteAnswer[0],
                secondaryColor: paletteAnswer[1],
                tertiaryColor: paletteAnswer[2],
                complementaryColor1: paletteAnswer[3] || '',
                complementaryColor2: paletteAnswer[4] || '',
              }
              : null;
        const paletteStepOrder = messageObj.stepOrder;
        const paletteMessageOrder = messageObj.messageOrder;
        const hasExistingPaletteAnswer = paletteAnswer.length >= 3;

        messageObj.children = (
          <PaletteSelector
            skipBackendSave
            isReadOnly={paletteConfirmed || hasExistingPaletteAnswer}
            initialData={paletteInitialData}
            onComplete={(colors) => {
              const resolvedCampaignId = campaignArtGenerationIdRef.current;
              if (
                !socketRef.current ||
                paletteStepOrder == null ||
                paletteMessageOrder == null
              ) {
                console.warn(
                  '[ChatSocketPage] PaletteSelector onComplete aborted - missing socket or palette message metadata',
                );
                return;
              }
              const nextPaletteAnswer = [
                colors.primaryColor,
                colors.secondaryColor,
                colors.tertiaryColor,
                colors.complementaryColor1,
                colors.complementaryColor2,
              ].filter(Boolean);
              setMessages((prev) =>
                prev.map((msg) =>
                  msg?.component === 'ColorPaletteSelector' &&
                  Number(msg?.stepOrder) === Number(paletteStepOrder) &&
                  Number(msg?.messageOrder) === Number(paletteMessageOrder)
                    ? { ...msg, answer: nextPaletteAnswer.join(', ') }
                    : msg,
                ),
              );
              setPaletteConfirmed(true);
              appendMessagesWithSequentialOrder([
                {
                  backend: tc('chatsocket.loading.generating_parameterization'),
                  isLoadingMessage: true,
                },
              ]);
              socketRef.current.emit('answer_question', {
                campaignArtGenerationId: resolvedCampaignId,
                stepOrder: paletteStepOrder,
                messageOrder: paletteMessageOrder,
                answer: nextPaletteAnswer,
                lang: localStorage.getItem('i18nextLng') || 'ptBR',
              });
              const resolvedCurrentQuestion = currentQuestionRef.current;
              const isActivePaletteQuestion =
                resolvedCurrentQuestion?.component === 'ColorPaletteSelector' &&
                Number(resolvedCurrentQuestion?.stepOrder) === Number(paletteStepOrder) &&
                Number(resolvedCurrentQuestion?.messageOrder) ===
                  Number(paletteMessageOrder);
              if (isActivePaletteQuestion) {
                setCurrentQuestion(null);
                setMessage('');
              }
            }}
          />
        );
      }
      break;
    case 'StrategyStepsList':
      <StepsList
        title={tc('chatsocket.chat.creating_strategy')}
        steps={queueSteps}
      />
      break;
      // case 'PostQuantitySelector':
      //   if (
      //     isUserWithoutSubscriptionRef.current &&
      //       Number(messageObj?.messageOrder) === 7.1
      //   ) {
      //     messageObj.children = null;
      //     break;
      //   }
      //   messageObj.children = (
      //     <PostQuantitySelector
      //       selectedValue={messageObj.answer}
      //       availableArts={availableArtsCount}
      //       onFinalize={(quantity) => handleSelectOption('MOCK_POST_QUANTITY_FINALIZED', quantity)}
      //     />
      //   );
      //   break;
    default:
      break;
    }
    return messageObj;
  };

  const triggerAutoProgression = (
    nextQuestion,
    campaignId,
    socket,
    authToken,
  ) => {
    const shouldRunImmediately = autoProgressAfterSkipRef.current;
    autoProgressAfterSkipRef.current = false;

    const isAgendamentoStep = nextQuestion.stepOrder === 1;

    const mockComponents = [
      'SocialMediaSelector',
      'ScheduleSelector',
      'SchedulingProgress',
    ];

    const hasMockComponent = mockComponents.includes(nextQuestion.component);

    if (isAgendamentoStep && !hasMockComponent && socket && authToken) {
      if (!nextQuestion.options?.length) {
        setManagedTimeout(() => {
          socket.emit('answer_question', {
            campaignArtGenerationId: campaignId,
            stepOrder: nextQuestion.stepOrder,
            messageOrder: nextQuestion.messageOrder,
            answer: [''],
            lang: localStorage.getItem('i18nextLng') || 'ptBR',
          });
        }, shouldRunImmediately ? 0 : 1500);
      }
    } else if (hasMockComponent && nextQuestion.component === 'DownloadZipCard' && socket && authToken) {
      setManagedTimeout(() => {
        socket.emit('answer_question', {
          campaignArtGenerationId: campaignId,
          stepOrder: nextQuestion.stepOrder,
          messageOrder: nextQuestion.messageOrder,
          answer: [''],
          lang: localStorage.getItem('i18nextLng') || 'ptBR',
        });
      }, shouldRunImmediately ? 0 : 3000);
    }
  };

  const restoreLayoutState = async (data, selectSeq = null, language = null) => {
    const isStaleCampaignSelect = () =>
      selectSeq != null && selectSeq !== campaignSelectSeqRef.current;

    if (isStaleCampaignSelect()) {
      return [];
    }

    // Força linguagem explicitamente se fornecida
    if (language) {
      campaignLangRef.current = language;
    }

    setShowFullChatLayout(true);
    if (data.strategyResult) {
      setMarkdownContent(getStrategyMarkdown(data.strategyResult));
      setStrategyResult(data.strategyResult);
      setIsStrategyLoading(false);
    }

    const hasArts = Array.isArray(data?.arts) && data.arts.length > 0;
    const allowsArtsTabByStatus = [
      'art_generation_in_progress',
      'arts_ready',
      'arts_approved',
      'completed',
    ].includes(data?.status);

    setActiveTab(hasArts || allowsArtsTabByStatus ? 'artes' : 'estrategia');

    setQueueSteps(getInitialQueueSteps().map((s) => ({ ...s, status: 'complete' })));
    setShowQueueProgress(true);

    let generatedArts = [];
    if (data.arts) {
      const sortedArts = sortArtsByPieceNumber(data.arts).map((art) =>
        normalizeCampaignArtEntry(art),
      );
      const artsWithStatus = await enrichArtsWithPublicationStatus(sortedArts);

      if (isStaleCampaignSelect()) {
        return [];
      }

      campaignArtsRef.current = artsWithStatus;
      setCampaignArts(artsWithStatus);
      setSchedulingProgressItems(buildInitialSchedulingItems(artsWithStatus));
      setSchedulingSummary(null);

      generatedArts = Array.isArray(artsWithStatus)
        ? artsWithStatus.map((art) => ({
          ...art,
          url: art.imageUrl,
          title: art.piece_name || tc('chatsocket.piece_fallback', { num: art.piece_number }),
        }))
        : [];

      setImgsArray(generatedArts);
      setTotalImagesArray(generatedArts.length);
      setArtSteps(
        (() => {
          const campaignKey = normalizeCampaignUuid(data.campaignArtGenerationId);
          const totalArts = generatedArts.length;
          const persisted = getPersistedArtStepsProgress(campaignKey);
          if (persisted != null) {
            const pendingIndex = Math.max(0, persisted - 1);
            return Array.from({ length: totalArts }, (_, idx) => ({
              text: tc('chatsocket.art_step_fallback', { num: idx + 1 }),
              status: idx < pendingIndex ? 'complete' : idx === pendingIndex ? 'pending' : 'idle',
            }));
          }
          return Array.from({ length: generatedArts.length }, (_, idx) => ({
            text: tc('chatsocket.art_step_fallback', { num: idx + 1 }),
            status: 'complete',
          }));
        })(),
      );
      setShowArtQueueProgress(true);
    } else {
      // Evita reaproveitar artes da campanha anterior quando a campanha atual não possui imagens.
      campaignArtsRef.current = [];
      setCampaignArts([]);
      setImgsArray([]);
      setTotalImagesArray(0);
      setSelectedArt(null);
      setArtScheduleData({});
      artScheduleDataRef.current = {};
      setSchedulingProgressItems([]);
      setSchedulingSummary(null);
      setShowArtQueueProgress(false);
      setHasSelectedSchedulePublicationItem(false);
    }
    return generatedArts;
  };

  const injectArtificialStatusMessages = (
    baseMessages,
    data,
    artsArray,
    disableTypingAnimation = false,
  ) => {
    if (!baseMessages.some((m) => m.isQueueProgressMessage)) {
      [
        {
          backend: tc('chatsocket.loading.processing_strategy'),
          frontend: '',
          isQueueProgressMessage: true,
        },
      ].map((message) =>
        disableTypingAnimation
          ? disableTypingForBackendMessage(message)
          : message,
      );
    }

  };

  const filterHistoryForDisplay = (
    history,
    includeAutoFilledParameterization = suppressTypingDuringRestoreRef.current,
  ) => {
    if (!Array.isArray(history) || includeAutoFilledParameterization) {
      return history;
    }

    return history.filter(
      (item) =>
        !(
          Number(item?.stepOrder) === -1 &&
          item?.isAutoFilledFromDatabase === true
        ),
    );
  };

  const mapHistoryToMessages = (history, options = {}) => {
    const includeAutoFilledParameterization =
      options.includeAutoFilledParameterization ??
      suppressTypingDuringRestoreRef.current;
    const visibleHistory = filterHistoryForDisplay(
      history,
      includeAutoFilledParameterization,
    );

    // Se existe um bulk answer no step 0, não mostrar a pergunta 0 individual
    const hasBulkStep0Answer = Array.isArray(visibleHistory) && visibleHistory.some(
      (item) => item?.stepOrder === 0 && Number(item?.messageOrder) === -0.1 && !!item?.answer,
    );
    const bulkSkippedOrders = new Set([0]);

    const mapped = visibleHistory.flatMap((item) => {
      // const shouldSkipMessageOrderEightInRecovery =
      //   suppressTypingDuringRestoreRef.current === true &&
      //   Number(item?.messageOrder) === 8;

      // if (shouldSkipMessageOrderEightInRecovery) {
      //   return [];
      // }

      // Pular messages 0-3 do step 0 quando existe bulk answer
      if (
        hasBulkStep0Answer &&
        item?.stepOrder === 0 &&
        bulkSkippedOrders.has(Number(item?.messageOrder))
      ) {
        return [];
      }

      if (shouldSuppressBackendQuestionMessage(item)) {
        return [];
      }

      // Mensagem do usuário (greeting inicial)
      if (item.isUserMessage) {
        return [
          {
            backend: '',
            frontend: item.question || '',
            stepOrder: item.stepOrder,
            messageOrder: item.messageOrder,
            disableTypingAnimation: true,
          },
        ];
      }

      const fallbackQuestionByComponent = {
        SocialMediaSelector: tc('chatsocket.scheduling.select_networks'),
        ScheduleSelector: tc('chatsocket.scheduling.review_schedule'),
        SchedulingProgress: tc('chatsocket.scheduling.progress_tracking'),
        StrategyDownloader: tc('chatsocket.strategy.approved_pdf'),
        // DownloadZipCard: 'Seu pacote final está pronto para download.',
      };

      let questionText =
        item.question ||
        item.message ||
        fallbackQuestionByComponent[item.component] ||
        '';

      questionText = replaceBrandPlaceholder(questionText);

      const questionMessage = injectMockComponent({
        backend: questionText,
        frontend: '',
        stepOrder: item.stepOrder,
        messageOrder: item.messageOrder,
        options: item.options || [],
        component: item.component,
        answer: item.answer,
        disableTypingAnimation: true,
      });

      const answerText =
        item.component === 'SocialMediaSelector' ||
          item.component === 'ScheduleSelector' ||
          item.component === 'SchedulingProgress' ||
          item.component === 'ColorPaletteSelector'
          ? ''
          : item.answer;

      const normalizedImageUrls = Array.isArray(item.imageUrl)
        ? item.imageUrl
          .map((i) => (typeof i === 'string' ? i : i?.image || i?.url || null))
          .filter(Boolean)
        : typeof item.imageUrl === 'string'
          ? [item.imageUrl]
          : [];

      const answerMessage = {
        backend: '',
        frontend: replaceBrandPlaceholder(answerText),
        stepOrder: item.stepOrder,
        messageOrder: item.messageOrder,
        imageUrls: normalizedImageUrls,
        component: ['DownloadZipCard', 'PostQuantitySelector', 'ColorPaletteSelector'].includes(item.component) ? null : item.component,
      };

      return [questionMessage, answerMessage];
    });

    const result = mapped
      .map((msg, index) => ({ msg, index }))
      .sort((left, right) => {
        const sa = left.msg?.stepOrder ?? 0;
        const sb = right.msg?.stepOrder ?? 0;
        if (sa !== sb) {
          return sa - sb;
        }
        return left.index - right.index;
      })
      .map(({ msg }) => msg);
    return result;
  };

  const getPendingQuestionFromHistory = (history = []) => {
    if (!Array.isArray(history) || history.length === 0) {
      return null;
    }

    // Se existe um bulk answer no step 0 (messageOrder: -0.1), considerar message 0 como respondida
    const hasBulkStep0Answer = history.some(
      (item) => item?.stepOrder === 0 && Number(item?.messageOrder) === -0.1 && !!item?.answer,
    );
    const bulkSkippedOrders = new Set([0]);

    const pendingItem = [...history].reverse().find((item) => {
      if (item?.component === 'StrategyStepsList') {
        return false;
      }

      // Pular messages 0-3 do step 0 quando existe bulk answer
      if (
        hasBulkStep0Answer &&
        item?.stepOrder === 0 &&
        bulkSkippedOrders.has(Number(item?.messageOrder))
      ) {
        return false;
      }

      const answer = item?.answer;

      if (Array.isArray(answer)) {
        return answer.length === 0;
      }

      return answer == null || String(answer).trim() === '';
    });

    if (!pendingItem) {
      return null;
    }

    return {
      stepOrder: pendingItem.stepOrder,
      stepName: pendingItem.stepName,
      messageOrder: pendingItem.messageOrder,
      question: pendingItem.question || pendingItem.message || '',
      options: pendingItem.options || [],
      component: pendingItem.component,
      isFirstCampaign: pendingItem.isFirstCampaign,
    };
  };

  const isEventFromActiveCampaign = (eventCampaignId) => {
    const normalizedEventCampaignId = normalizeCampaignUuid(eventCampaignId);
    const normalizedActiveCampaignId = normalizeCampaignUuid(
      campaignArtGenerationIdRef.current,
    );

    // Eventos sem ID de campanha são globais (ex.: erros, fluxo novo com id null).
    if (!normalizedEventCampaignId) {
      return true;
    }

    // Sem campanha ativa: aceitar evento que inicializa a sessão, exceto a campanha
    // anterior que acabou de ser abandonada (criação/troca).
    if (!normalizedActiveCampaignId) {
      const previousCampaignId = normalizeCampaignUuid(
        previousCampaignBeforeCreateRef.current,
      );
      if (
        previousCampaignId &&
        normalizedEventCampaignId === previousCampaignId
      ) {
        return false;
      }
      return true;
    }

    return normalizedEventCampaignId === normalizedActiveCampaignId;
  };

  const isStalePreviousCampaign = (eventCampaignId) => {
    if (!eventCampaignId) return false;
    if (campaignArtGenerationIdRef.current) return false;
    const prev = normalizeCampaignUuid(previousCampaignBeforeCreateRef.current);
    return prev && normalizeCampaignUuid(eventCampaignId) === prev;
  };

  const handleSelectCampaign = async (uuid) => {
    const selectSeq = campaignSelectSeqRef.current + 1;
    campaignSelectSeqRef.current = selectSeq;
    const isStaleCampaignSelect = () => selectSeq !== campaignSelectSeqRef.current;
    const normalizedTargetId = normalizeCampaignUuid(uuid) || uuid || null;

    // Pin the active campaign immediately so socket handlers ignore the previous one
    // while async loading is still in flight.
    if (normalizedTargetId) {
      campaignArtGenerationIdRef.current = normalizedTargetId;
      setCampaignArtGenerationId(normalizedTargetId);
      setSelectedCampaignId(normalizedTargetId);
    }

    setIsCampaignSwitching(true);

    try {
      isRestoringCampaignSelectionRef.current = true;
      isCreatingNewCampaignRef.current = false;
      suppressTypingDuringRestoreRef.current = true;
      // Reset immediately to avoid leaking previous campaign name while loading.
      setCampaignName(tc('chatsocket.new_campaign_name'));
      clearAllManagedTimeouts();
      clearPendingStrategySync();

      // Reseta estados visuais de artes/agendamento antes de carregar outra campanha.
      setPaletteConfirmed(false);
      setSocialMediaSelection([]);
      socialMediaSelectionRef.current = [];
      setLockedSections({ socialMedia: false, publications: false });
      schedulingInFlightRef.current = false;
      campaignStatusRef.current = 'in_progress';
      campaignArtsRef.current = [];
      setCampaignArts([]);
      setArtSteps([]);
      setImgsArray([]);
      setTotalImagesArray(0);
      setSelectedArt(null);
      setArtScheduleData({});
      artScheduleDataRef.current = {};
      setSchedulingProgressItems([]);
      setSchedulingSummary(null);
      setIsSchedulingRunning(false);
      setHasSelectedSchedulePublicationItem(false);
      setPreviewAttachedImages([]);
      setArtLimitFallback(null);
      setBatchEditLimitNoticeVariant(null);
      setIsApprovingArts(false);
      releaseApprovalActionLock();
      releaseMessageSubmitLock();
      setCurrentQuestion(null);
      setPendingApproval(null);
      setMarkdownContent('');
      markdownContentRef.current = '';
      setStrategyResult({});
      setIsStrategyLoading(false);
      setMessages([
        {
          backend: tc('chatsocket.loading.loading_conversation'),
          frontend: '',
        },
      ]);

      // try to find the campaign in the already loaded list
      let selectedCampaign = campaigns?.find(
        (c) => c?.uuid === uuid || c?.campaignArtGenerationId === uuid || c?.id === uuid,
      );

      if (!selectedCampaign) {
        const reloaded = await refreshCampaigns();
        if (isStaleCampaignSelect()) {
          return;
        }
        const listToSearch = Array.isArray(reloaded) ? reloaded : campaigns;
        selectedCampaign = listToSearch?.find((c) => c?.uuid === uuid || c?.campaignArtGenerationId === uuid || c?.id === uuid);
      }

      if (!selectedCampaign) {
        if (isStaleCampaignSelect()) {
          return;
        }
        setMessages([
          {
            backend: tc('chatsocket.errors.load_error'),
            frontend: '',
          },
        ]);
        return;
      }

      requestedInitialQuestionCampaignIdRef.current = null;

      // set basic campaign identifiers and status
      const resolvedCampaignId =
        selectedCampaign.campaignArtGenerationId ||
        selectedCampaign.uuid ||
        selectedCampaign.id;

      if (!resolvedCampaignId) {
        return;
      }

      if (isStaleCampaignSelect()) {
        return;
      }

      // Busca dados frescos da campanha para obter language correta
      let freshCampaignData = null;
      try {
        const freshResponse = await getCampaignArtById(resolvedCampaignId);
        freshCampaignData = freshResponse?.data;
      } catch (_) {
        // noop - fallback para selectedCampaign
      }

      const campaignDataSource = freshCampaignData || selectedCampaign;
      const resolvedCampaignLanguage =
        campaignDataSource?.language ||
        campaignDataSource?.data?.language ||
        campaignDataSource?.lang ||
        campaignDataSource?.campaignLanguage ||
        campaignLanguage;

      if (resolvedCampaignLanguage) {
        campaignLangRef.current = resolvedCampaignLanguage;
        setCampaignLanguage(resolvedCampaignLanguage);
      }

      setCampaignArtGenerationId(resolvedCampaignId);
      campaignArtGenerationIdRef.current = resolvedCampaignId;
      setSelectedCampaignId(resolvedCampaignId);
      setCampaignDownloadPurchasedState(
        Boolean(selectedCampaign?.isDownloadable),
      );
      const resolvedCampaignKey = normalizeCampaignUuid(resolvedCampaignId);
      if (resolvedCampaignKey) {
        delete campaignPreviewCacheRef.current[resolvedCampaignKey];
      }

      const buildNormalizedCampaignSnapshot = (campaign) => {
        const normalized = normalizeCampaignArtsPayload(campaign);
        const historyFromData = buildHistoryFromCampaignData(
          campaign.data ?? normalized.data,
          {
            strategyResult:
              normalized.strategyResult ?? campaign.strategy_result,
          },
        );

        if (
          !Array.isArray(normalized.history) ||
          normalized.history.length === 0
        ) {
          normalized.history = historyFromData;
        }

        const rawStatus =
          campaign?.status || campaign?.campaignStatus || 'in_progress';
        const effectiveStatus = resolveEffectiveCampaignStatus({
          ...normalized,
          status: rawStatus,
          history: normalized.history,
        });

        return { normalized, effectiveStatus };
      };

      let { normalized: normalizedCampaign, effectiveStatus: resolvedStatus } =
        buildNormalizedCampaignSnapshot(selectedCampaign);

      const restoredQueueProgress = getPersistedQueueProgress(resolvedCampaignId);
      queueProgressIndexRef.current = restoredQueueProgress ?? 0;
      setQueueSteps(
        restoredQueueProgress == null
          ? getInitialQueueSteps().map((s) => ({ ...s, status: 'idle' }))
          : buildQueueStepsFromProgress(restoredQueueProgress),
      );
      campaignStatusRef.current = resolvedStatus;
      setCampaignStatus(resolvedStatus);


      if (resolvedStatus === 'strategy_approved' && resolvedCampaignId) {
        try {
          const freshResponse = await getCampaignArtById(resolvedCampaignId);
          if (isStaleCampaignSelect()) {
            return;
          }
          const freshData = freshResponse?.data;
          if (freshData && typeof freshData === 'object') {
            selectedCampaign = {
              ...selectedCampaign,
              ...freshData,
            };
            setHasCampaignDownloadPurchased(Boolean(freshData.isDownloadable));
            setCampaignPreviewSession({
              updatedAt: freshData.updatedAt ?? null,
            });
          }
        } catch (fetchErr) {
          // noop
        }
      }

      const artsStatusesNeedingFreshFetch = [
        'art_generation_in_progress',
        'arts_ready',
        'arts_approved',
        'completed',
      ];
      if (
        resolvedCampaignId &&
        artsStatusesNeedingFreshFetch.includes(resolvedStatus)
      ) {
        try {
          const freshResponse = await getCampaignArtById(resolvedCampaignId);
          if (isStaleCampaignSelect()) {
            return;
          }
          const freshData = freshResponse?.data;
          if (freshData && typeof freshData === 'object') {
            selectedCampaign = {
              ...selectedCampaign,
              ...freshData,
            };
            setHasCampaignDownloadPurchased(Boolean(freshData.isDownloadable));
            setCampaignPreviewSession({
              updatedAt: freshData.updatedAt ?? null,
            });
          }
        } catch (fetchErr) {
          // noop
        }
      } else if (!isStaleCampaignSelect()) {
        setCampaignPreviewSession({
          updatedAt: selectedCampaign?.updatedAt ?? null,
        });
      }

      if (isStaleCampaignSelect()) {
        return;
      }

      ({ normalized: normalizedCampaign, effectiveStatus: resolvedStatus } =
        buildNormalizedCampaignSnapshot(selectedCampaign));
      campaignStatusRef.current = resolvedStatus;
      setCampaignStatus(resolvedStatus);

      const resolvedCampaignName = String(
        selectedCampaign?.campaignName || selectedCampaign?.name || '',
      ).trim();
      setCampaignName(
        resolvedCampaignName || tc('chatsocket.new_campaign_name'),
      );

      // Keep local strategy-approval persistence aligned with backend status on campaign switch.
      // This prevents stale localStorage from auto-advancing a campaign still awaiting approval.
      const approvedStatuses = ['strategy_approved', 'art_generation_in_progress', 'arts_ready', 'arts_approved', 'completed'];
      const preApprovalStatuses = ['in_progress', 'questions_completed', 'strategy_processing', 'strategy_ready'];
      if (approvedStatuses.includes(resolvedStatus)) {
        persistStrategyApproval(resolvedCampaignId, true);
      }
      if (preApprovalStatuses.includes(resolvedStatus)) {
        persistStrategyApproval(resolvedCampaignId, false);
      }

      if (socketRef.current && userId && authToken) {
        socketRef.current.emit('resume_campaign',
          {
            userAccountId: userId,
            campaignArtGenerationId: resolvedCampaignId,
            authToken,
            lang: localStorage.getItem('i18nextLng') || 'ptBR',
          });
      }

      // If payload already contains history and arts, restore them locally.
      if (
        normalizedCampaign.history &&
        Array.isArray(normalizedCampaign.history) &&
        normalizedCampaign.history.length
      ) {
        // clear any existing messages to avoid concatenation
        setMessages(mapHistoryToMessages(normalizedCampaign.history));
        // Check if palette was already confirmed in this campaign's history
        const paletteHistoryItem = normalizedCampaign.history.find(
          (h) => h?.component === 'ColorPaletteSelector' && h?.answer,
        );
        setPaletteConfirmed(Boolean(paletteHistoryItem));
        const socialSelectionFromHistory = extractSocialSelectionFromHistory(
          normalizedCampaign.history,
        );
        setSocialMediaSelection(socialSelectionFromHistory);
        socialMediaSelectionRef.current = socialSelectionFromHistory;

        const schedulingLock = deriveSchedulingLockFromHistory(
          normalizedCampaign.history,
          resolvedStatus,
        );
        setLockedSections({
          socialMedia: false,
          publications: schedulingLock.publications,
        });

        const pendingHistoryQuestion =
          getPendingQuestionFromHistory(normalizedCampaign.history) ||
          findPendingQuestionFromCampaignData(
            selectedCampaign.data ?? normalizedCampaign.data,
          );

        if (resolvedStatus === 'arts_ready') {
          setPendingApproval({
            type: 'art',
            data: {
              campaignArtGenerationId: resolvedCampaignId,
              results: normalizedCampaign.arts,
            },
            onApprove: () => {
              emitApproveCampaignArtsRef.current(resolvedCampaignId);
            },
            onEdit: () => {
              openBatchArtEditQuestion(
                tc('chatsocket.strategy.batch_edit_question'),
                'edit',
              );
            },
            onReject: () => {
              openBatchArtEditQuestion(
                tc('chatsocket.strategy.batch_edit_reject_question'),
                'regenerate',
              );
            },
          });
          setCurrentQuestion(buildArtsReadyApprovalQuestion(tc));
        } else if (resolvedStatus === 'completed') {
          setCurrentQuestion(buildCompletedCampaignQuestion(tc));
        } else if (
          ['in_progress', 'arts_approved', 'parametrization'].includes(
            resolvedStatus,
          ) &&
          pendingHistoryQuestion
        ) {
          if (
            emitFixedQuantityForFirstCampaign(
              pendingHistoryQuestion,
              resolvedCampaignId,
            )
          ) {
            setCurrentQuestion(null);
            setPendingAnswerQuestion(null);
            setMessage('');
          } else {
            setCurrentQuestion(pendingHistoryQuestion);
          }
        }
      } else {
        const schedulingLock = deriveSchedulingLockFromHistory(
          [],
          resolvedStatus,
        );
        setLockedSections({
          socialMedia: false,
          publications: schedulingLock.publications,
        });

        if (resolvedStatus === 'arts_ready') {
          setPendingApproval({
            type: 'art',
            data: {
              campaignArtGenerationId: resolvedCampaignId,
              results: normalizedCampaign.arts,
            },
            onApprove: () => {
              emitApproveCampaignArtsRef.current(resolvedCampaignId);
            },
            onEdit: () => {
              openBatchArtEditQuestion(
                tc('chatsocket.strategy.batch_edit_question'),
                'edit',
              );
            },
            onReject: () => {
              openBatchArtEditQuestion(
                tc('chatsocket.strategy.batch_edit_reject_question'),
                'regenerate',
              );
            },
          });
          setCurrentQuestion(buildArtsReadyApprovalQuestion(tc));
        } else if (resolvedStatus === 'completed') {
          setCurrentQuestion(buildCompletedCampaignQuestion(tc));
        }
      }

      const generated = (await restoreLayoutState(normalizedCampaign, selectSeq, resolvedCampaignLanguage)) || [];
      if (isStaleCampaignSelect()) {
        return;
      }
      if (generated.length) setImgsArray(generated);

      if (resolvedStatus === 'strategy_approved') {
        const totalPieces = resolveStrategyTotalPieces(normalizedCampaign.strategyResult);
        const generatedArtsCount = Array.isArray(normalizedCampaign?.arts)
          ? normalizedCampaign.arts.length
          : generated.length;
        const totalArts = Math.max(totalPieces, generatedArtsCount);

        if (totalArts > 0) {
          setArtSteps(
            Array.from({ length: totalArts }, (_, idx) => ({
              text: tc('chatsocket.art_step_fallback', { num: idx + 1 }),
              status:
                idx < generatedArtsCount
                  ? 'complete'
                  : idx === generatedArtsCount && generatedArtsCount < totalArts
                    ? 'pending'
                    : 'idle',
            })),
          );
          setTotalImagesArray(totalArts);
        }

        setShowArtQueueProgress(true);
      }

      if (resolvedStatus === 'in_progress' || resolvedStatus === 'parametrization') setShowFullChatLayout(false);
    } catch (err) {
      if (!isStaleCampaignSelect()) {
        console.error('[ChatSocketPage] handleSelectCampaign error:', err);
      }
    } finally {
      if (selectSeq === campaignSelectSeqRef.current) {
        isRestoringCampaignSelectionRef.current = false;
        setIsCampaignSwitching(false);
      }
    }
  }

  useEffect(() => {
    let cancelled = false;
    // getCampaingArt()

    const initializeSocket = async () => {
      try {
        appendMessagesWithSequentialOrder([
          {
            backend: '',
            frontend: '',
            placeholder: tc('chatsocket.loading.loading_conversation')
          },
        ]);
        // Restore last active campaign on return to this page
        const savedCampaignId = (() => {
          try {
            return localStorage.getItem(LAST_ACTIVE_CAMPAIGN_KEY);
          } catch {
            return null;
          }
        })();
        if (savedCampaignId) {
          campaignArtGenerationIdRef.current = savedCampaignId;
          suppressTypingDuringRestoreRef.current = true;
        }
        let userId = null;
        let userResponse = null;

        try {
          userResponse = await UserAccountsService.getUserAccountsByLogin();

          userId = userResponse?.data?.uuid;
          setCampaingAvaliationStatus(userResponse?.data?.campaingAvaliation);
          setUserId(userId);

          try {
            const businessData = await getBusinessByLogin();
            const brandName = businessData?.data?.[0]?.brandName || '';
            setBrandNameFromDb(brandName);
            brandNameFromDbRef.current = brandName;
          } catch (businessError) {
            console.warn('Erro ao buscar business para brandName:', businessError);
          }

          if (userResponse?.data?.canDownloadCampaigns) {
            applyUserCanDownloadAllCampaigns();
          }

          try {
            const activeSubscriptionResponse = await SubscriptionService.getActiveSubscription();
            const hasActiveSubscription =
              hasActiveSubscriptionResponse(activeSubscriptionResponse);
            const isWithoutSubscription = !hasActiveSubscription;

            isUserWithoutSubscriptionRef.current = isWithoutSubscription;
            setIsUserWithoutSubscription(
              isWithoutSubscription,
            );

            if (hasActiveSubscription) {
              await syncUserCanDownloadCampaigns();
            }
          } catch (planError) {
            isUserWithoutSubscriptionRef.current = false;
            setIsUserWithoutSubscription(false);
          }

          // Credits must refresh even if subscription lookup fails — same API
          // source of truth as the sidebar (getArtGenerationAvailability).
          await updateArtLimitsRef.current();
        } catch (error) {
          console.error('Erro ao buscar userAccountId:', error);
          console.error(
            'Detalhes do erro:',
            error.response?.data || error.message,
          );
          appendMessagesWithSequentialOrder([
            {
              backend: tc('chatsocket.errors.load_error'),
              frontend: '',
            },
          ]);
          return;
        }

        if (!userId) {
          console.error(
            'userAccountId não encontrado. Estrutura da resposta:',
            userResponse,
          );
          appendMessagesWithSequentialOrder([
            {
              backend:
                tc('chatsocket.errors.user_not_found'),
              frontend: '',
            },
          ]);
          return;
        }

        const authToken = getStorageToken();
        setAuthToken(authToken);
        if (!authToken) {
          appendMessagesWithSequentialOrder([
            {
              backend:
                tc('chatsocket.errors.token_not_found'),
              frontend: '',
            },
          ]);
          return;
        }

        const socketUrl = getSocketUrl();
        if (!socketUrl) {
          console.error('[ChatSocketPage] Socket URL is not configured');
          appendMessagesWithSequentialOrder([
            {
              backend: tc('chatsocket.errors.load_error'),
              frontend: '',
            },
          ]);
          return;
        }

        socketRef.current = io(socketUrl, getSocketClientOptions(authToken));

        if (cancelled) {
          socketRef.current.disconnect();
          socketRef.current = null;
          return;
        }

        socketRef.current.on('connect', () => {
          setConnectionStatus('connected');

          socketRef.current.emit('start_campaign_questions', {
            userAccountId: userId,
            authToken,
            lang: localStorage.getItem('i18nextLng') || 'ptBR',
          });

          socketRef.current.emit('start_campaing', {
            userAccountId: userId,
            authToken,
            campaignArtGenerationId: campaignArtGenerationIdRef.current,
          });

          const resumedId = campaignArtGenerationIdRef.current;
          if (userId && authToken && resumedId) {
            socketRef.current.emit('resume_campaign', {
              userAccountId: userId,
              campaignArtGenerationId: resumedId,
              authToken,
              lang: localStorage.getItem('i18nextLng') || 'ptBR',
            });
          }
        });

        socketRef.current.on('disconnect', () => {
          setConnectionStatus('disconnected');
        });

        socketRef.current.on('connect_error', (error) => {
          console.error('[ChatSocketPage] socket connection error:', error);
          setConnectionStatus('error');
        });

        socketRef.current.on('reconnect_attempt', () => {
          setConnectionStatus('reconnecting');
        });

        socketRef.current.on('art_credits_updated', () => {
          // Same as CampaignChat: one canonical refresh path via user-plan-count-updated.
          emitUserPlanCountUpdated();
        });

        socketRef.current.on('campaign_state', async (data) => {
          // Só atualiza language do socket se ainda não temos uma definida
          // (ex: nova campanha). Para campanhas existentes, a language vem do DB.
          if (data.language && !campaignArtGenerationIdRef.current) {
            campaignLangRef.current = data.language;
            setCampaignLanguage(data.language);
          }
          const incomingCampaignId = data?.campaignArtGenerationId
          const selectedCampaignId = campaignArtGenerationIdRef.current


          if (selectedCampaignId && !incomingCampaignId) return;
          if (
            selectedCampaignId &&
            incomingCampaignId &&
            normalizeCampaignUuid(incomingCampaignId) !==
              normalizeCampaignUuid(selectedCampaignId)
          ) {
            return;
          }

          if (isStalePreviousCampaign(incomingCampaignId)) {
            return;
          }

          const isRestoringCampaignSelection =
            isRestoringCampaignSelectionRef.current &&
            (!selectedCampaignId || incomingCampaignId === selectedCampaignId);
          if (isRestoringCampaignSelection) {
            isRestoringCampaignSelectionRef.current = false;
          }

          const resolvedIncomingCampaignId =
            data.campaignArtGenerationId ||
            data.uuid ||
            data.id ||
            selectedCampaignId;

          const incomingStatus = data.status || 'in_progress';
          const socketStatus = data.status;
          const localStatus = campaignStatusRef.current;
          const postStrategyStatuses = ['strategy_approved', 'art_generation_in_progress', 'arts_ready', 'arts_approved', 'completed'];
          const localIsPostStrategy = postStrategyStatuses.includes(localStatus);
          const socketIsPreStrategy = !postStrategyStatuses.includes(socketStatus) && socketStatus !== 'strategy_approved';
          if (localIsPostStrategy && socketIsPreStrategy) {
            return;
          }

          setCampaignArtGenerationId(resolvedIncomingCampaignId);
          campaignArtGenerationIdRef.current = resolvedIncomingCampaignId;
          campaignStatusRef.current = incomingStatus;
          setCampaignStatus(incomingStatus);
          const incomingCampaignName = String(data?.campaignName || '').trim();
          if (incomingCampaignName) {
            setCampaignName(incomingCampaignName);
          } else if (isRestoringCampaignSelectionRef.current) {
            setCampaignName(tc('chatsocket.new_campaign_name'));
          }
          const pendingStrategySync = pendingStrategySyncRef.current;
          const isStrategyRecoverySync =
            data.status === 'strategy_ready' &&
            pendingStrategySync?.campaignArtGenerationId ===
            resolvedIncomingCampaignId;

          const hasHistory = data.history && data.history.length > 0;
          const pendingHistoryQuestion = getPendingQuestionFromHistory(
            data.history,
          );

          const previousCampaignBeforeCreate = normalizeCampaignUuid(
            previousCampaignBeforeCreateRef.current,
          );

          if (
            isCreatingNewCampaignRef.current &&
            previousCampaignBeforeCreate &&
            incomingCampaignId === previousCampaignBeforeCreate
          ) {
            return;
          }

          if (
            isCreatingNewCampaignRef.current &&
            resolvedIncomingCampaignId &&
            data.status === 'in_progress' &&
            !data.nextQuestion &&
            !pendingHistoryQuestion &&
            !hasHistory &&
            socketRef.current &&
            userId &&
            authToken
          ) {
            const normalizedRequestedCampaignId = normalizeCampaignUuid(
              resolvedIncomingCampaignId,
            );

            if (
              requestedInitialQuestionCampaignIdRef.current !==
              normalizedRequestedCampaignId
            ) {
              requestedInitialQuestionCampaignIdRef.current =
                normalizedRequestedCampaignId;

              socketRef.current.emit('start_campaign_questions', {
                userAccountId: userId,
                authToken,
                campaignArtGenerationId: resolvedIncomingCampaignId,
                forceNewCampaign: true,
                lang: localStorage.getItem('i18nextLng') || 'ptBR',
              });
            }
          }

          if (
            isCreatingNewCampaignRef.current &&
            (data.nextQuestion || pendingHistoryQuestion)
          ) {
            isCreatingNewCampaignRef.current = false;
            previousCampaignBeforeCreateRef.current = null;
            requestedInitialQuestionCampaignIdRef.current = null;
          }

          if (hasHistory && !isStrategyRecoverySync) {
            const visibleHistoryMessages = mapHistoryToMessages(data.history);
            if (visibleHistoryMessages.length > 0) {
              setMessages(visibleHistoryMessages);
            }
            // Check if palette was already confirmed in this campaign's history
            const paletteHistoryItem = data.history.find(
              (h) => h?.component === 'ColorPaletteSelector' && h?.answer,
            );
            if (paletteHistoryItem) {
              setPaletteConfirmed(true);
            }
            const socialSelectionFromHistory =
              extractSocialSelectionFromHistory(data.history);
            setSocialMediaSelection(socialSelectionFromHistory);
          }

          if (data.status === 'parametrization') {
            const incomingQuestion = data.nextQuestion || pendingHistoryQuestion;

            if (incomingQuestion) {
              setCurrentQuestion({
                stepOrder: incomingQuestion.stepOrder,
                stepName: incomingQuestion.stepName,
                messageOrder: incomingQuestion.messageOrder,
                question:
                  incomingQuestion.question || incomingQuestion.message,
                options: incomingQuestion.options || [],
                component: incomingQuestion.component,
                placeholders: incomingQuestion.placeholders,
                isFirstCampaign: incomingQuestion.isFirstCampaign,
              });

              setMessages((prev) => {
                if (!shouldShowQuestionInChat(prev)) {
                  return prev.filter(
                    (message) =>
                      ![
                        tc('chatsocket.loading.loading_conversation'),
                        tc('chatsocket.loading.starting_campaign'),
                      ].includes(message?.backend),
                  );
                }

                const hasLoadingMessage =
                  prev.length === 1 &&
                  [
                    tc('chatsocket.loading.loading_conversation'),
                    tc('chatsocket.loading.starting_campaign'),
                  ].includes(prev[0].backend);

                const questionMessage = buildQuestionMessageForChat(
                  incomingQuestion,
                  { disableTypingAnimation: true },
                );

                if (!questionMessage) {
                  return prev;
                }

                if (hasLoadingMessage) {
                  return [questionMessage];
                }

                return appendMessageWithTailOrderForSocialMediaSelector(
                  prev,
                  questionMessage,
                );
              });

              triggerAutoProgression(
                incomingQuestion,
                resolvedIncomingCampaignId,
                socketRef.current,
                authToken,
              );
            }
            return;
          }

          if (data.status === 'in_progress') {
            const incomingQuestion = data.nextQuestion || pendingHistoryQuestion;

            if (incomingQuestion) {
              if (emitFixedQuantityForFirstCampaign(incomingQuestion, resolvedIncomingCampaignId)) {
                setCurrentQuestion(null);
                setPendingAnswerQuestion(null);
                setMessage('');
                return;
              }

              setCurrentQuestion({
                stepOrder: incomingQuestion.stepOrder,
                stepName: incomingQuestion.stepName,
                messageOrder: incomingQuestion.messageOrder,
                question:
                  incomingQuestion.question || incomingQuestion.message,
                options: incomingQuestion.options || [],
                component: incomingQuestion.component,
                placeholders: incomingQuestion.placeholders,
                isFirstCampaign: incomingQuestion.isFirstCampaign,
              });

              setMessages(prev => {
                // Primeira pergunta: não renderiza no chat, só placeholder no input
                const hasUserMessage = prev.some((m) => !!m.frontend);
                if (!hasUserMessage) {
                  return [];
                }

                if (shouldSuppressBackendQuestionMessage(incomingQuestion)) {
                  return prev;
                }

                const hasLoadingMessage =
                  prev.length === 1 &&
                  [
                    tc('chatsocket.loading.loading_conversation'),
                    tc('chatsocket.loading.starting_campaign'),
                  ].includes(prev[0].backend);

                const questionMessage = injectMockComponent({
                  backend:
                    incomingQuestion.question ||
                    incomingQuestion.message ||
                    '',
                  frontend: '',
                  stepOrder: incomingQuestion.stepOrder,
                  messageOrder: incomingQuestion.messageOrder,
                  options: incomingQuestion.options || [],
                  component: incomingQuestion.component,
                  disableTypingAnimation: true,
                });

                if (hasLoadingMessage) {
                  return [questionMessage];
                }

                return appendMessageWithTailOrderForSocialMediaSelector(
                  prev,
                  questionMessage,
                );
              });

              triggerAutoProgression(
                incomingQuestion,
                resolvedIncomingCampaignId,
                socketRef.current,
                authToken,
              );
            }
            return;
          }

          if (data.status === 'questions_completed') {
            setCampaignStatus('questions_completed');
            setShowFullChatLayout(true);
            setShowQueueProgress(true);
            setActiveTab('estrategia');
            setPendingApproval(null);
            setCurrentQuestion(null);
            setIsStrategyLoading(true);

            const storedProgress = getPersistedQueueProgress(
              data.campaignArtGenerationId,
            );
            const effectiveProgress = Math.max(
              queueProgressIndexRef.current,
              storedProgress ?? 0,
              1,
            );
            queueProgressIndexRef.current = effectiveProgress;
            persistQueueProgress(
              data.campaignArtGenerationId,
              effectiveProgress,
            );
            setQueueSteps(buildQueueStepsFromProgress(effectiveProgress));

            // let queueStepsUpdated = getInitialQueueSteps()
            // const localStorageQueueSteps = localStorage.getItem('queueSteps');
            // setQueueSteps(localStorageQueueSteps ? JSON.parse(localStorageQueueSteps) : queueStepsUpdated);

            const normalizedCampaignId = normalizeCampaignUuid(
              data.campaignArtGenerationId,
            );

            if (
              normalizedCampaignId &&
              strategyProcessingRequestByCampaignRef.current.has(
                normalizedCampaignId,
              )
            ) {
              return;
            }

            if (normalizedCampaignId) {
              strategyProcessingRequestByCampaignRef.current.add(
                normalizedCampaignId,
              );
            }

            // getParametrizationsData()
            //   .then((businessParametrizationsData) => {
            if (data.campaignArtGenerationId && socketRef.current) {
              socketRef.current.emit('process_campaign_data', {
                campaignArtGenerationId: data.campaignArtGenerationId,
                //...businessParametrizationsData,
                authToken,
              });
            }
            // })
            // .catch(() => {
            //   if (normalizedCampaignId) {
            //     strategyProcessingRequestByCampaignRef.current.delete(
            //       normalizedCampaignId,
            //     );
            //   }
            // });
            return;
          }

          if (data.status === 'strategy_processing') {
            setCampaignStatus('strategy_processing');
            setShowFullChatLayout(true);
            setShowQueueProgress(true);
            setActiveTab('estrategia');
            setPendingApproval(null);
            setCurrentQuestion(null);
            setIsStrategyLoading(true);

            if (data.strategyResult) {
              setStrategyResult(data.strategyResult);
              setMarkdownContent(getStrategyMarkdown(data.strategyResult));
            }

            return;
          }

          if (data.status === 'strategy_ready') {
            const hasPersistedStrategyApproval = isStrategyApprovedPersisted(
              resolvedIncomingCampaignId,
            );

            // If strategy was already approved locally, don't downgrade status back to strategy_ready.
            // This happens when backend still returns strategy_ready on resume but the user already approved.
            if (hasPersistedStrategyApproval && !isRestoringCampaignSelection) {
              return;
            }

            if (hasPersistedStrategyApproval && isRestoringCampaignSelection) {
              persistStrategyApproval(
                resolvedIncomingCampaignId,
                false,
              );
            }

            setCampaignStatus('strategy_ready');
            // if (!hasHistory) {
            //   appendMessagesWithSequentialOrder([
            //     {
            //       backend: STRATEGY_READY_REVIEW_MESSAGE,
            //       frontend: '',
            //     },
            //   ]);
            // }

            if (isStrategyRecoverySync) {
              const recoveredMarkdown = getStrategyMarkdown(data);

              if (
                pendingStrategySync?.baselineMarkdown &&
                recoveredMarkdown === pendingStrategySync.baselineMarkdown
              ) {
                return;
              }

              clearPendingStrategySync();
              setMessages((prev) =>
                removeStrategySyncDelayMessages(prev).filter(
                  (item) => !item?.isStrategyProcessingMessage,
                ),
              );
            }

            applyStrategyReadyState(data, {
              appendMessages: !isStrategyRecoverySync,
            });


            return;
          }


          if (data.status === 'strategy_approved') {
            persistStrategyApproval(data.campaignArtGenerationId, true);

            if (data?.campaignName) {
              updateCampaignInList(data.campaignArtGenerationId, {
                name: data.campaignName,
                campaignName: data.campaignName,
              });
            }

            // Total pieces from artDirector execution plan (most reliable source)
            const totalPieces =
              (Array.isArray(data.artDirector?.pieces_execution) && data.artDirector.pieces_execution.length > 0)
                ? data.artDirector.pieces_execution.length
                : resolveStrategyTotalPieces(data.strategyResult);

            // Fetch fresh campaign data to get arts generated so far
            let freshArts = Array.isArray(data.arts) ? data.arts : [];
            try {
              const freshResponse = await getCampaignArtById(data.campaignArtGenerationId);
              const freshData = freshResponse?.data;
              const normalizedFresh = normalizeCampaignArtsPayload(freshData || {});
              if (normalizedFresh.arts.length > 0) {
                freshArts = normalizedFresh.arts;
              }
              await restoreLayoutState(data, null, data.language);
            } catch (fetchErr) {
              // noop
            }

            // Restore strategy markdown and arts; status overridden to art_generation_in_progress
            // so restoreLayoutState sets up partial art steps + skeletons correctly.
            await restoreLayoutState({
              ...data,
              arts: freshArts.length > 0 ? freshArts : undefined,
              status: 'art_generation_in_progress',
            }, null, data.language);

            // Override totalImagesArray and artSteps using the authoritative totalPieces
            const currentDone = freshArts.length;
            setTotalImagesArray(Math.max(totalPieces, currentDone));
            const artStepsFromTotal = Array.from({ length: Math.max(totalPieces, currentDone) }, (_, idx) => ({
              text: tc('chatsocket.art_step_fallback', { num: idx + 1 }),
              status:
                idx < currentDone
                  ? 'complete'
                  : idx === currentDone && currentDone < totalPieces
                    ? 'pending'
                    : 'idle',
            }));
            setArtSteps(artStepsFromTotal);
            localStorage.setItem(ART_STEPS_STORAGE_KEY, JSON.stringify(artStepsFromTotal));

            setCampaignStatus('strategy_approved');
            setShowArtQueueProgress(true);
            setActiveTab('artes');
            setShowFullChatLayout(true);
            return;
          }

          if (data.status === 'arts_ready') {
            setCampaignStatus('arts_ready');
            const generatedArts = await restoreLayoutState(data, null, data.language);

            setMessages((prev) => {
              const baseMessages = [...prev];
              injectArtificialStatusMessages(
                baseMessages,
                data,
                generatedArts,
                true,
              );
              return baseMessages;
            });

            setPendingApproval({
              type: 'art',
              data: {
                campaignArtGenerationId: data.campaignArtGenerationId,
                results: data.arts,
              },
              onApprove: () => {
                emitApproveCampaignArtsRef.current(data.campaignArtGenerationId);
              },
              onEdit: () => {
                openBatchArtEditQuestion(
                  tc('chatsocket.strategy.batch_edit_question'),
                  'edit',
                );
              },
              onReject: () => {
                openBatchArtEditQuestion(
                  tc('chatsocket.strategy.batch_edit_reject_question'),
                  'regenerate',
                );
              },
            });

            setCurrentQuestion(
              createApprovalQuestion({
                approve: tc('chatsocket.buttons.approve_arts'),
                edit: tc('chatsocket.buttons.batch_edit'),
                reject: tc('chatsocket.buttons.reject'),
              }),
            );

            return;
          }

          if (data.status === 'arts_approved') {
            setCampaignStatus('arts_approved');
            clearArtApprovalLoadingRef.current();
            const generatedArts = await restoreLayoutState(data, null, data.language);
            setMessages((prev) => {
              const baseMessages = [...prev];
              injectArtificialStatusMessages(
                baseMessages,
                data,
                generatedArts,
                true,
              );
              return baseMessages;
            });

            if (data.nextQuestion) {
              setCurrentQuestion({
                stepOrder: data.nextQuestion.stepOrder,
                stepName: data.nextQuestion.stepName,
                messageOrder: data.nextQuestion.messageOrder,
                question:
                  data.nextQuestion.question || data.nextQuestion.message,
                options: data.nextQuestion.options || [],
                component: data.nextQuestion.component,
                placeholders: data.nextQuestion.placeholders,
                isFirstCampaign: data.nextQuestion.isFirstCampaign,
              });

              const questionMessage = injectMockComponent({
                backend:
                  data.nextQuestion.question || data.nextQuestion.message || '',
                frontend: '',
                stepOrder: data.nextQuestion.stepOrder,
                messageOrder: data.nextQuestion.messageOrder,
                options: data.nextQuestion.options || [],
                component: data.nextQuestion.component,
                disableTypingAnimation: true,
              });

              setMessages((prev) =>
                appendMessageWithTailOrderForSocialMediaSelector(
                  prev,
                  questionMessage,
                ),
              );

              triggerAutoProgression(
                data.nextQuestion,
                data.campaignArtGenerationId,
                socketRef.current,
                authToken,
              );
            }
            return;
          }

          if (data.status === 'completed') {
            setCampaignStatus('completed');
            const generatedArts = await restoreLayoutState(data, null, data.language);

            setMessages((prev) => {
              const baseMessages = [...prev];
              injectArtificialStatusMessages(
                baseMessages,
                data,
                generatedArts,
                true,
              );
              return baseMessages;
            });

            if (data.nextQuestion) {
              setCurrentQuestion({
                stepOrder: data.nextQuestion.stepOrder,
                stepName: data.nextQuestion.stepName,
                messageOrder: data.nextQuestion.messageOrder,
                question:
                  data.nextQuestion.question || data.nextQuestion.message,
                options: data.nextQuestion.options || [],
                component: data.nextQuestion.component,
              });

              const questionMessage = injectMockComponent({
                backend:
                  data.nextQuestion.question || data.nextQuestion.message || '',
                frontend: '',
                stepOrder: data.nextQuestion.stepOrder,
                messageOrder: data.nextQuestion.messageOrder,
                options: data.nextQuestion.options || [],
                component: data.nextQuestion.component,
                disableTypingAnimation: true,
              });

              setMessages((prev) =>
                appendMessageWithTailOrderForSocialMediaSelector(
                  prev,
                  questionMessage,
                ),
              );

              triggerAutoProgression(
                data.nextQuestion,
                data.campaignArtGenerationId,
                socketRef.current,
                authToken,
              );
            }
            return;
          }
        });

        socketRef.current.on('next_question', (data) => {
          // Remove parameterization loading message if present
          setMessages((prev) => prev.filter((m) => !m?.isLoadingMessage));
          // Só atualiza language do socket se ainda não temos uma definida
          if (data.language && !campaignArtGenerationIdRef.current) {
            campaignLangRef.current = data.language;
            setCampaignLanguage(data.language);
          }
          const shouldIgnoreRestoredHistory = isCreatingNewCampaignRef.current;
          if (shouldIgnoreRestoredHistory) {
            isCreatingNewCampaignRef.current = false;
          }
          requestedInitialQuestionCampaignIdRef.current = null;

          const resolvedIncomingCampaignId =
            (data.campaignArtGenerationId != null && data.campaignArtGenerationId !== '')
              ? data.campaignArtGenerationId
              : (data.uuid || data.id || campaignArtGenerationIdRef.current);

          if (!isEventFromActiveCampaign(resolvedIncomingCampaignId)) {
            return;
          }

          if (resolvedIncomingCampaignId) {
            if (isStalePreviousCampaign(resolvedIncomingCampaignId)) {
              return;
            }
            setCampaignArtGenerationId(resolvedIncomingCampaignId);
            campaignArtGenerationIdRef.current = resolvedIncomingCampaignId;
          }
          clearArtApprovalLoadingRef.current();
          syncStrategyFromSocket(data.strategyResult);

          if (emitFixedQuantityForFirstCampaign(data, resolvedIncomingCampaignId)) {
            setCurrentQuestion(null);
            setPendingAnswerQuestion(null);
            setMessage('');
            return;
          }

          setCurrentQuestion({
            stepOrder: data.stepOrder,
            stepName: data.stepName,
            messageOrder: data.messageOrder,
            question: data.question || data.message,
            options: data.options || [],
            component: data.component,
            placeholders: data.placeholders,
          });
          lastQuestionRef.current = null;

          setPendingAnswerQuestion(null);
          clearInputMessage(resolvedIncomingCampaignId);

          if (
            data.history &&
            data.history.length > 0 &&
            !shouldIgnoreRestoredHistory
          ) {
            const historyMessages = mapHistoryToMessages(data.history);
            const socialSelectionFromHistory =
              extractSocialSelectionFromHistory(data.history);
            setSocialMediaSelection(socialSelectionFromHistory);
            const pendingSocialMediaChoiceLabel =
              pendingSocialMediaChoiceLabelRef.current;
            const shouldInjectPendingSocialMediaChoice =
              data?.component === 'SocialMediaSelector' &&
              typeof pendingSocialMediaChoiceLabel === 'string' &&
              pendingSocialMediaChoiceLabel.trim().length > 0;

            const finalMessage = injectMockComponent({
              backend: data.question || data.message || '',
              frontend: '',
              stepOrder: data.stepOrder,
              messageOrder: data.messageOrder,
              options: data.options || [],
              component: data.component,
              disableTypingAnimation: suppressTypingDuringRestoreRef.current,
            });

            const appendOrBumpFinalMessageWithTailOrder = (items = []) => {
              const lastOrder = items.reduce(
                (max, m) => Math.max(max, Number(m?.messageOrder ?? 0)),
                0,
              );
              const targetOrder = lastOrder + 0.1;

              const existingIndex = items.findLastIndex(
                (item) =>
                  item?.backend === finalMessage?.backend &&
                  item?.component === finalMessage?.component &&
                  !item?.frontend,
              );

              if (existingIndex !== -1) {
                const copy = [...items];
                copy[existingIndex] = {
                  ...copy[existingIndex],
                  messageOrder: targetOrder,
                };
                return copy;
              }

              return [
                ...items,
                {
                  ...finalMessage,
                  messageOrder: targetOrder,
                },
              ];
            };

            let nextMessages = historyMessages;

            if (shouldInjectPendingSocialMediaChoice) {
              const hasChoiceAlready = nextMessages.some(
                (item) => item?.frontend === pendingSocialMediaChoiceLabel,
              );

              if (!hasChoiceAlready) {
                nextMessages = [
                  ...nextMessages,
                  {
                    frontend: pendingSocialMediaChoiceLabel,
                    backend: '',
                    disableTypingAnimation: true,
                  },
                ];
              }

              pendingSocialMediaChoiceLabelRef.current = null;
            }

            setMessages((prev) => {
              if (!shouldShowQuestionInChat(prev)) {
                return prev.filter((message) => !message?.isLoadingMessage);
              }

              const mergedMessages = appendOrBumpFinalMessageWithTailOrder(
                nextMessages,
              );
              const lastOrder = prev.reduce(
                (max, message) => Math.max(max, Number(message?.messageOrder ?? 0)),
                0,
              );

              return [
                ...prev.filter((message) => !message?.isLoadingMessage),
                ...mergedMessages.map((message, index) => ({
                  ...message,
                  messageOrder: Number(
                    (lastOrder + (index + 1) * 0.1).toFixed(1),
                  ),
                })),
              ];
            });
          } else {
            setMessages((prev) => {
              if (!shouldShowQuestionInChat(prev)) {
                return prev.filter(
                  (message) =>
                    !message?.isLoadingMessage &&
                    ![
                      tc('chatsocket.loading.loading_conversation'),
                      tc('chatsocket.loading.starting_campaign'),
                    ].includes(message?.backend),
                );
              }

              if (shouldSuppressBackendQuestionMessage(data)) {
                return prev;
              }

              const hasLoadingMessage =
                prev.length === 1 &&
                [
                  tc('chatsocket.loading.loading_conversation'),
                  tc('chatsocket.loading.starting_campaign'),
                ].includes(prev[0].backend);
              const newMessage = buildQuestionMessageForChat(data);

              const pendingSocialMediaChoiceLabel =
                pendingSocialMediaChoiceLabelRef.current;
              const shouldInjectPendingSocialMediaChoice =
                data?.component === 'SocialMediaSelector' &&
                typeof pendingSocialMediaChoiceLabel === 'string' &&
                pendingSocialMediaChoiceLabel.trim().length > 0 &&
                !prev.some(
                  (item) => item?.frontend === pendingSocialMediaChoiceLabel,
                );

              if (shouldInjectPendingSocialMediaChoice) {
                const lastOrder = prev.reduce(
                  (max, m) => Math.max(max, Number(m?.messageOrder ?? 0)),
                  0,
                );
                const socialChoiceOrder = lastOrder + 0.1;
                const socialOrder = socialChoiceOrder + 0.1;

                pendingSocialMediaChoiceLabelRef.current = null;

                return [
                  ...prev,
                  {
                    frontend: pendingSocialMediaChoiceLabel,
                    backend: '',
                    disableTypingAnimation: true,
                    messageOrder: socialChoiceOrder,
                  },
                  {
                    ...newMessage,
                    messageOrder: socialOrder,
                  },
                ];
              }

              const lastOrder = prev.reduce(
                (max, m) => Math.max(max, Number(m?.messageOrder ?? 0)),
                0,
              );
              const orderedNewMessage = {
                ...newMessage,
                messageOrder: lastOrder + 0.1,
              };

              if (!newMessage) {
                return prev;
              }

              if (hasLoadingMessage) {
                return [orderedNewMessage];
              } else {
                return [...prev, orderedNewMessage];
              }
            });
          }

          triggerAutoProgression(
            data,
            resolvedIncomingCampaignId,
            socketRef.current,
            authToken,
          );
        });

        socketRef.current.on('flow_completed', async (data) => {
          if (!isEventFromActiveCampaign(data?.campaignArtGenerationId)) {
            return;
          }
          setCampaignStatus('questions_completed');

          const buildQueueProgressMessage = () => ({
            backend: tc('chatsocket.loading.processing_strategy'),
            frontend: '',
            isQueueProgressMessage: true,
            disableTypingAnimation: true,
            stepOrder: 0,
            messageOrder: 7.2,
          });

          if (data.history && data.history.length > 0) {
            const historyMessages = data.history.flatMap((item) => {
              if (shouldSuppressBackendQuestionMessage(item)) {
                return [];
              }

              return [
                {
                  backend: item.question,
                  frontend: '',
                  stepOrder: item.stepOrder,
                  messageOrder: item.messageOrder,
                  options: item.options || [],
                  component: item.component,
                },
                (() => {
                  const normalizedImageUrls = Array.isArray(item.imageUrl)
                    ? item.imageUrl
                      .map((i) => (typeof i === 'string' ? i : i?.image || i?.url || null))
                      .filter(Boolean)
                    : typeof item.imageUrl === 'string'
                      ? [item.imageUrl]
                      : [];

                  return {
                    backend: '',
                    frontend: item.answer,
                    stepOrder: item.stepOrder,
                    messageOrder: item.messageOrder,
                    imageUrls: normalizedImageUrls,
                    component: item.component,
                  };
                })(),
              ];
            });

            const hasQueueProgressMessage = historyMessages.some(
              (item) => item?.isQueueProgressMessage,
            );

            setMessages([
              ...historyMessages,
              withRestoreTypingSuppressed({
                backend: data.message,
                frontend: '',
              }),
              ...(!hasQueueProgressMessage ? [buildQueueProgressMessage()] : []),
            ]);
          } else {
            setMessages((prev) => {
              const alreadyHasQueueProgressMessage = prev.some(
                (item) => item?.isQueueProgressMessage,
              );

              return [
                ...prev,
                withRestoreTypingSuppressed({
                  backend: data.message,
                  frontend: '',
                }),
                ...(!alreadyHasQueueProgressMessage
                  ? [buildQueueProgressMessage()]
                  : []),
              ];
            });
          }

          setCurrentQuestion(null);

          setMarkdownContent('');
          queueProgressIndexRef.current = 0;
          clearPersistedQueueProgress(data.campaignArtGenerationId);
          setQueueSteps(buildQueueStepsFromProgress(0));
          setShowQueueProgress(true);
          setIsStrategyLoading(true);
          setShowFullChatLayout(true);
          setActiveTab('estrategia');

          const normalizedCampaignId = normalizeCampaignUuid(
            data.campaignArtGenerationId,
          );

          if (
            normalizedCampaignId &&
            strategyProcessingRequestByCampaignRef.current.has(
              normalizedCampaignId,
            )
          ) {
            return;
          }

          if (normalizedCampaignId) {
            strategyProcessingRequestByCampaignRef.current.add(
              normalizedCampaignId,
            );
          }

          try {
            //const businessParametrizationsData = await getParametrizationsData();

            if (data.campaignArtGenerationId && socketRef.current) {
              socketRef.current.emit('process_campaign_data', {
                campaignArtGenerationId: data.campaignArtGenerationId,
                //...businessParametrizationsData,
                authToken,
              });
            }
          } catch (_) {
            if (normalizedCampaignId) {
              strategyProcessingRequestByCampaignRef.current.delete(
                normalizedCampaignId,
              );
            }
          }
        });

        socketRef.current.on('bulk_step0_input_generated', (data) => {
          if (!isEventFromActiveCampaign(data?.campaignArtGenerationId)) {
            return;
          }
          setIsGeneratingSuggestion(false);
          if (data.bulkText) {
            setIsBulkStep0Active(true);
            setMessage(data.bulkText);
          }
        });

        socketRef.current.on('campaign_queue_progress', (data) => {
          if (!campaignArtGenerationIdRef.current) {
            return;
          }
          if (!isEventFromActiveCampaign(data?.campaignArtGenerationId)) {
            return;
          }

          setCampaignStatus('strategy_processing');
          const nextProgressRaw = Number(data.current);
          if (!Number.isFinite(nextProgressRaw)) {
            return;
          }

          const nextProgress = Math.max(0, Math.floor(nextProgressRaw));
          const previousProgress = queueProgressIndexRef.current;

          if (nextProgress < previousProgress) {
            return;
          }

          queueProgressIndexRef.current = nextProgress;
          // Persist progress only under the campaign id from the event
          persistQueueProgress(data.campaignArtGenerationId, nextProgress);

          // Activate loading UI on the very first progress event (current === 1)
          if (data.stepName === 'brandProfile' && nextProgress === 1) {
            setShowFullChatLayout(true);
            setShowQueueProgress(true);
          }

          setQueueSteps(buildQueueStepsFromProgress(nextProgress));
        });

        socketRef.current.on('campaign_processed', (data) => {
          if (!campaignArtGenerationIdRef.current) {
            return;
          }
          if (!isEventFromActiveCampaign(data?.campaignArtGenerationId)) {
            return;
          }

          setCampaignStatus('strategy_ready');
          if (!(Array.isArray(data?.history) && data.history.length > 0)) {
            const strategyReadyMessages = data.messages.map(message => ({ backend: message.backend, frontend: data.fronend }))
            appendMessagesWithSequentialOrder(strategyReadyMessages);
          }

          const strategyData = resolveStrategyPayload(data);
          const strategyMarkdown = getStrategyMarkdown(strategyData);
          queueProgressIndexRef.current = getInitialQueueSteps().length;
          clearPersistedQueueProgress(
            (data.campaignArtGenerationId != null && data.campaignArtGenerationId !== '')
              ? data.campaignArtGenerationId
              : campaignArtGenerationIdRef.current,
          );
          setQueueSteps((prev) => {
            return prev.map((step) => ({ ...step, status: 'complete' }));
          });
          setMarkdownContent(strategyMarkdown);
          setStrategyResult(strategyData);
          setIsStrategyLoading(false);
          if (data.campaignName) {
            setCampaignName(data.campaignName);
          }

          setActiveTab('estrategia');

          if (data.campaignArtGenerationId && socketRef.current) {
            setStrategyApprovalState(
              data.campaignArtGenerationId,
              strategyData,
            );
          }
        });

        socketRef.current.on('strategy_edited', (data) => {
          if (!isEventFromActiveCampaign(data?.campaignArtGenerationId)) {
            return;
          }

          appendMessagesWithSequentialOrder([
            {
              backend: data.message,
              frontend: '',
            },

            // {
            //   backend:'Estratégia atualizada com sucesso! Revise a nova versão e escolha uma ação.',
            //   frontend:'',
            //   messageOrder:9998
            // },


            // {
            //   backend: 'Revise a estratégia gerada e escolha uma ação:',
            //   frontend: '',
            //   messageOrder: 9999,
            // },
          ]);
          setCampaignStatus('strategy_ready');
          if (!(Array.isArray(data?.history) && data.history.length > 0)) {
            appendMessagesWithSequentialOrder([
              {
                backend: tc('chatsocket.strategy.review_prompt'),
                frontend: '',
              },
            ]);
          }
          clearPendingStrategySync();
          setIsStrategyLoading(false);

          if (!data?.strategyResult) {
            return;
          }

          setStrategyResult(data.strategyResult);
          setMarkdownContent(getStrategyMarkdown(data.strategyResult));
          if (data.strategyResult?.strategyHeadlinesAndIdentity?.campaignName) {
            setCampaignName(
              data.strategyResult.strategyHeadlinesAndIdentity.campaignName,
            );
          }


          setMessages((prev) => {
            const fallbackMessages = removeStrategySyncDelayMessages(prev);
            const nextMessages =
              data?.history && Array.isArray(data.history) && data.history.length > 0
                ? mapHistoryToMessages(data.history)
                : fallbackMessages;
            const successMessage =
              data?.message ||
              tc('chatsocket.strategy.update_success');

            if (!successMessage || hasBackendMessage(nextMessages, successMessage)) {
              return appendStrategyReadyReviewMessageIfMissing(nextMessages);
            }

            return appendStrategyReadyReviewMessageIfMissing([
              ...nextMessages,
              {
                backend: successMessage,
                frontend: '',
              },
            ]);
          });

          const campaignId =
            (data.campaignArtGenerationId != null && data.campaignArtGenerationId !== '')
              ? data.campaignArtGenerationId
              : strategyApprovalCallbacksRef.current?.campaignId;

          if (campaignId) {
            setStrategyApprovalState(campaignId, data.strategyResult);
          }
        });

        socketRef.current.on('strategy_regenerated', (data) => {
          if (!isEventFromActiveCampaign(data?.campaignArtGenerationId)) {
            return;
          }

          setCampaignStatus('strategy_ready');
          clearPendingStrategySync();
          setIsStrategyLoading(false);

          if (!data?.strategyResult) {
            return;
          }

          appendMessagesWithSequentialOrder(data.messages)
          setStrategyResult(data.strategyResult);
          setMarkdownContent(getStrategyMarkdown(data.strategyResult));
          if (data.strategyResult?.strategyHeadlinesAndIdentity?.campaignName) {
            setCampaignName(
              data.strategyResult.strategyHeadlinesAndIdentity.campaignName,
            );
          }

          const campaignId =
            (data.campaignArtGenerationId != null && data.campaignArtGenerationId !== '')
              ? data.campaignArtGenerationId
              : strategyApprovalCallbacksRef.current?.campaignId;

          if (campaignId) {
            setStrategyApprovalState(campaignId, data.strategyResult);
          }


        });

        socketRef.current.on('strategy_updated', (data) => {

          if (!isEventFromActiveCampaign(data?.campaignArtGenerationId)) {
            return;
          }

          setCampaignStatus('strategy_ready');
          if (!(Array.isArray(data?.history) && data.history.length > 0)) {
            appendMessagesWithSequentialOrder([
              {
                backend: tc('chatsocket.strategy.review_prompt'),
                frontend: '',
              },
            ]);
          }


          clearPendingStrategySync();
          setIsStrategyLoading(false);
          if (data?.strategyResult) {
            setStrategyResult(data.strategyResult);
            setMarkdownContent(getStrategyMarkdown(data.strategyResult));

            if (data.campaignArtGenerationId) {
              setStrategyApprovalState(
                data.campaignArtGenerationId,
                data.strategyResult,
              );
            }
          }

          appendMessagesWithSequentialOrder(data.messages)
        });

        socketRef.current.on('piece_legend_persisted', (data) => {
          if (!isEventFromActiveCampaign(data?.campaignArtGenerationId)) {
            return;
          }

          if (data?.strategyResult) {
            setStrategyResult(data.strategyResult);
            setMarkdownContent(getStrategyMarkdown(data.strategyResult));
          }
          const pn = Number(data?.pieceNumber);
          if (!Number.isFinite(pn) || pn < 1) {
            return;
          }
          const legend = typeof data?.legend === 'string' ? data.legend : '';
          const applyLegend = (art) =>
            art && Number(art.piece_number) === pn ? { ...art, legend } : art;
          setImgsArray((prev) => prev.map(applyLegend));
          if (Array.isArray(campaignArtsRef.current)) {
            campaignArtsRef.current = campaignArtsRef.current.map(applyLegend);
          }
          setSelectedArt((prev) => (prev ? applyLegend(prev) : prev));
        });

        socketRef.current.on('piece_schedule_persisted', (data) => {
          if (!isEventFromActiveCampaign(data?.campaignArtGenerationId)) {
            return;
          }

          if (data?.strategyResult) {
            setStrategyResult(data.strategyResult);
            setMarkdownContent(getStrategyMarkdown(data.strategyResult));
          }

          const pn = Number(data?.pieceNumber);
          if (!Number.isFinite(pn) || pn < 1) {
            return;
          }

          const schedulePatch = {
            publishDate: data?.publishDate || '',
            publishTime: data?.publishTime || '',
            publicationDateTime: data?.publicationDateTime || '',
            scheduledAt: data?.publicationDateTime || '',
          };

          const applySchedule = (art) =>
            art && Number(art.piece_number) === pn
              ? { ...art, ...schedulePatch, ...(data?.art || {}) }
              : art;

          setImgsArray((prev) => prev.map(applySchedule));
          if (Array.isArray(campaignArtsRef.current)) {
            campaignArtsRef.current = campaignArtsRef.current.map(applySchedule);
            setCampaignArts(campaignArtsRef.current);
          }
          setSelectedArt((prev) => (prev ? applySchedule(prev) : prev));
        });

        socketRef.current.on('campaign_art_progress', (data) => {
          if (!isEventFromActiveCampaign(data?.campaignArtGenerationId)) {
            return;
          }

          setCampaignStatus('art_generation_in_progress');
          const formattedArt = {
            ...data.art,
            url: data.art.imageUrl,
            title: data.art.piece_name || tc('chatsocket.piece_fallback', { num: data.art.piece_number }),
          };

          setImgsArray((prev) =>
            mergeArtsByPieceNumber(prev, [formattedArt]).map((art) => ({
              ...art,
              url: art.url || art.imageUrl,
              title:
                art.title ||
                art.piece_name ||
                tc('chatsocket.piece_fallback', { num: art.piece_number }),
            })),
          );

          if (data.current === 1) {
            setActiveTab('artes');
            // Credits are debited upfront when generation starts; refresh limits on first progress.
            void updateArtLimitsRef.current();
          }


          setShowFullChatLayout(true)
          setArtSteps((prev) => {

            const updatedArr = prev.map((step, index) => {
              const totalPieces = prev.length;

              if (index < data.current) {
                return { ...step, status: 'complete' };
              }
              if (index === data.current && data.current < totalPieces) {
                return { ...step, status: 'pending' };
              }

              return { ...step, status: 'idle' };
            });



            localStorage.setItem(
              ART_STEPS_STORAGE_KEY,
              JSON.stringify(updatedArr),
            );
            try {
              const campaignKey = normalizeCampaignUuid(data?.campaignArtGenerationId);
              const pendingIndex = updatedArr.findIndex((s) => s.status === 'pending');
              if (pendingIndex !== -1) {
                persistArtStepsProgress(campaignKey, pendingIndex + 1);
              } else if (updatedArr.every((s) => s.status === 'complete')) {
                // store length+1 so restore logic treats all as complete (no pending index)
                persistArtStepsProgress(campaignKey, updatedArr.length + 1);
              } else {
                persistArtStepsProgress(campaignKey, 0);
              }
            } catch (e) {
              // noop
            }
            return updatedArr;
          }
          );

          // Balance is only debited after all pieces finish — refresh on
          // campaign_art_generated / batch_edited instead of every progress tick.
        });

        socketRef.current.on('campaign_art_generated', async (data) => {
          if (!isEventFromActiveCampaign(data?.campaignArtGenerationId)) {
            return;
          }


          setCampaignStatus('arts_ready');
          const sortedResults = sortArtsByPieceNumber(
            Array.isArray(data.results) ? data.results : [],
          ).map((art) => normalizeCampaignArtEntry(art));
          const artsArray = sortedResults.map((art) => ({
            ...art,
            url: art.imageUrl,
            title: art.piece_name || tc('chatsocket.piece_fallback', { num: art.piece_number }),
          }));

          // await consumeCampaignArtIfNeeded(
          //   `campaign_art_generated:${data.campaignArtGenerationId}:${artsArray
          //     .map((art) => `${art.piece_number}:${art.imageUrl}`)
          //     .join(',')}`,
          //   artsArray.length
          // );

          const artsReadyMsg = buildArtsReadyInstructionMessage(
            data.totalGenerated || artsArray.length,
          );

          // Do not store arts-ready instruction in localStorage — render immediately only.
          setMessages((prev) => {
            const already = prev.some((m) => m && m.isArtsReadyInstructionMessage && m.mode === 'generated');
            if (already) return prev;
            const nextPosition = getNextTransientMessagePosition(prev);
            return [...prev, { ...artsReadyMsg, ...nextPosition }];
          });

          campaignArtsRef.current = sortedResults;
          setCampaignArts(sortedResults);
          setImgsArray(artsArray);
          setTotalImagesArray(artsArray.length);
          setActiveTab('artes');

          if (data.campaignArtGenerationId && socketRef.current) {
            setPendingApproval({
              type: 'art',
              data: {
                campaignArtGenerationId: data.campaignArtGenerationId,
                results: sortedResults,
              },
              onApprove: () => {
                setCampaignStatus('arts_approved');
                emitApproveCampaignArtsRef.current(data.campaignArtGenerationId);
              },
              onEdit: () => {
                openBatchArtEditQuestion(
                  tc('chatsocket.strategy.batch_edit_question'),
                  'edit',
                );
              },
              onReject: () => {
                // Ao reprovar as artes, resetar artSteps para primeira pendente e persistir o progresso por campanha
                try {
                  const totalPieces = resolveStrategyTotalPieces(strategyResultRef.current);
                  const resetArtSteps = buildArtSteps(totalPieces, 0, tc);
                  setArtSteps(resetArtSteps);
                  localStorage.setItem(ART_STEPS_STORAGE_KEY, JSON.stringify(resetArtSteps));
                  persistArtStepsProgress(data.campaignArtGenerationId, 1);
                } catch (e) {
                  // noop
                }
                openBatchArtEditQuestion(
                  tc('chatsocket.strategy.batch_edit_reject_question'),
                  'regenerate',
                );
              },
            });

            setCurrentQuestion(
              createApprovalQuestion({
                approve: tc('chatsocket.buttons.approve_arts'),
                edit: tc('chatsocket.buttons.batch_edit'),
                reject: tc('chatsocket.buttons.reject'),
              }),
            );
          }

          await updateArtLimitsRef.current();
        });

        socketRef.current.on('campaign_art_batch_edited', async (data) => {
          if (!isEventFromActiveCampaign(data?.campaignArtGenerationId)) {
            return;
          }

          setCampaignStatus('arts_ready');
          setIsEditingArt(false);
          setIsLoadingBatchEditArtPerCampaign((prev) => ({
            ...prev,
            [data?.campaignArtGenerationId]: false,
          }));

          const sortedResults = sortArtsByPieceNumber(
            Array.isArray(data?.results) ? data.results : [],
          ).map((art) => normalizeCampaignArtEntry(art));
          const artsArray = sortedResults.map((art) => ({
            ...art,
            url: art.imageUrl,
            title: art.piece_name || tc('chatsocket.piece_fallback', { num: art.piece_number }),
          }));

          updateCampaignInList(data.campaignArtGenerationId, {
            results: sortedResults,
          });
          const campaignKey = normalizeCampaignUuid(data?.campaignArtGenerationId);
          if (campaignKey) {
            delete campaignPreviewCacheRef.current[campaignKey];
          }

          // await consumeCampaignArtIfNeeded(
          //   `campaign_art_batch_edited:${data?.campaignArtGenerationId}:${artsArray
          //     .map((art) => `${art.piece_number}:${art.imageUrl}`)
          //     .join(',')}`,
          //   artsArray.length
          // );

          setImgsArray(artsArray);
          setTotalImagesArray(artsArray.length);
          campaignArtsRef.current = sortedResults;
          setCampaignArts(sortedResults);
          setArtPreviewRenderKey((k) => k + 1);
          setSelectedArt((prev) => {
            if (!prev) return prev;
            const prevPiece =
              normalizeCampaignPieceNumber(prev.piece_number) ??
              normalizeCampaignPieceNumber(prev.pieceNumber);
            if (prevPiece == null) return prev;
            const updated = artsArray.find(
              (a) => normalizeCampaignPieceNumber(a.piece_number) === prevPiece,
            );
            return updated ? { ...updated } : prev;
          });
          setActiveTab('artes');

          // const batchArtsReadyMsg = buildArtsReadyInstructionMessage(
          //   data?.totalGenerated || artsArray.length,
          //   'updated',
          // );

          // Do not persist batch arts-ready instruction; show now only.
          // setMessages((prev) => {
          //   const nextPosition = getNextTransientMessagePosition(prev);
          //   return [
          //     ...prev,
          //     { ...batchArtsReadyMsg, ...nextPosition },
          //     {
          //       backend: STRATEGY_READY_REVIEW_MESSAGE,
          //       frontend: '',
          //       ...getNextTransientMessagePosition([
          //         ...prev,
          //         { ...batchArtsReadyMsg, ...nextPosition },
          //       ]),
          //     },
          //   ];
          // });

          if (data?.campaignArtGenerationId && socketRef.current) {
            setPendingApproval({
              type: 'art',
              data: {
                campaignArtGenerationId: data.campaignArtGenerationId,
                results: sortedResults,
              },
              onApprove: () => {
                setCampaignStatus('arts_approved');
                emitApproveCampaignArtsRef.current(data.campaignArtGenerationId);
              },
              onEdit: () => {
                openBatchArtEditQuestion(
                  tc('chatsocket.strategy.batch_edit_question'),
                  'edit',
                );
              },
              onReject: () => {
                openBatchArtEditQuestion(
                  tc('chatsocket.strategy.batch_edit_reject_question'),
                  'regenerate',
                );
              },
            });

            setCurrentQuestion(
              createApprovalQuestion({
                approve: tc('chatsocket.buttons.approve_arts'),
                edit: tc('chatsocket.buttons.batch_edit'),
                reject: tc('chatsocket.buttons.reject'),
              }),
            );
          }

          await updateArtLimitsRef.current();
        });

        socketRef.current.on('campaign_art_edited', async (data) => {
          if (!isEventFromActiveCampaign(data?.campaignArtGenerationId)) {
            return;
          }
          const nextUrlRaw = data?.imageUrl;

          const forcePreviewAfterSingleEdit = awaitingSingleArtEditRef.current;

          setCampaignStatus('arts_ready');

          const fromSocket = normalizeCampaignPieceNumber(data?.piece_number);
          const fromRef = normalizeCampaignPieceNumber(lastEditPieceNumberRef.current);
          const eventPiece = fromSocket ?? fromRef;
          lastEditPieceNumberRef.current = null;

          const artsBefore = campaignArtsRef.current || [];
          const imgsSnap = imgsArrayRef.current || [];
          const artsSource =
            artsBefore.length > 0 ? artsBefore : imgsSnap;

          const singleArtFallback =
            eventPiece == null && artsSource.length === 1;
          const pieceOf = (art) =>
            normalizeCampaignPieceNumber(art?.piece_number) ??
            normalizeCampaignPieceNumber(art?.pieceNumber);

          const matchesPiece = (art) => {
            if (singleArtFallback) return true;
            const p = pieceOf(art);
            return eventPiece != null && p === eventPiece;
          };

          const editRevision = new Date().toISOString();
          const normalizeEditedArt = (art) =>
            normalizeCampaignArtEntry({
              ...art,
              imageUrl: nextUrlRaw,
              revision: editRevision,
            });
          const matchedArtBefore = artsSource.find((art) => matchesPiece(art));
          const oldImageUrl =
            matchedArtBefore?.imageUrl || matchedArtBefore?.url || null;

          if (artsBefore.length > 0) {
            const updatedArts = artsBefore.map((art) =>
              matchesPiece(art) ? normalizeEditedArt(art) : art,
            );
            campaignArtsRef.current = updatedArts;
            setCampaignArts(updatedArts);
            updateCampaignInList(data.campaignArtGenerationId, {
              results: updatedArts,
            });
          } else if (imgsSnap.length > 0 && nextUrlRaw) {
            const rebuilt = imgsSnap.map((img) =>
              matchesPiece(img) ? normalizeEditedArt(img) : img,
            );
            campaignArtsRef.current = rebuilt.map((row) => ({
              ...row,
              imageUrl: row.imageUrl || row.url,
            }));
            setCampaignArts(campaignArtsRef.current);
            updateCampaignInList(data.campaignArtGenerationId, {
              results: campaignArtsRef.current,
            });
          } else {
            campaignArtsRef.current = [];
            setCampaignArts([]);
          }

          setImgsArray((prev) =>
            prev.map((img) =>
              matchesPiece(img)
                ? {
                  ...normalizeEditedArt(img),
                  title:
                      img.title ||
                      tc('chatsocket.piece_fallback', {
                        num: pieceOf(img) || eventPiece || 1,
                      }),
                }
                : img,
            ),
          );

          const campaignKey = normalizeCampaignUuid(data?.campaignArtGenerationId);
          if (campaignKey) {
            delete campaignPreviewCacheRef.current[campaignKey];
          }
          setArtPreviewRenderKey((key) => key + 1);
          setCampaignPreviewSession((prev) => ({
            ...prev,
            updatedAt: editRevision,
          }));

          setSelectedArt((prev) => {
            if (!prev || !nextUrlRaw) return prev;

            if (forcePreviewAfterSingleEdit) {
              return normalizeEditedArt({
                ...prev,
                ...(eventPiece != null ? { piece_number: eventPiece } : {}),
              });
            }

            let prevPiece = normalizeCampaignPieceNumber(prev.piece_number);
            if (prevPiece == null) {
              prevPiece = normalizeCampaignPieceNumber(prev.pieceNumber);
            }
            const prevUrl = prev.url || prev.imageUrl;

            let pieceOk =
              eventPiece != null
                ? prevPiece === eventPiece
                : singleArtFallback;

            if (
              !pieceOk &&
              eventPiece != null &&
              oldImageUrl &&
              prevUrl &&
              artUrlsLooselyMatch(oldImageUrl, prevUrl)
            ) {
              pieceOk = true;
            }

            if (!pieceOk && eventPiece != null && artsSource.length === 1) {
              pieceOk = true;
            }

            if (pieceOk) {
              return normalizeEditedArt({
                ...prev,
                ...(eventPiece != null ? { piece_number: eventPiece } : {}),
              });
            }
            return prev;
          });

          if (nextUrlRaw) {
            awaitingSingleArtEditRef.current = false;
            await updateArtLimitsRef.current();
            appendMessagesWithSequentialOrder([
              {
                backend: tc('chatsocket.errors.art_edit_completed'),
                frontend: '',
              },
            ]);
          } else {
            awaitingSingleArtEditRef.current = false;
            appendMessagesWithSequentialOrder([
              {
                backend: tc('chatsocket.errors.art_edit_completed_fail'),
                frontend: '',
                color: 'red',
              },
            ]);
          }

          setIsEditingArt(false);

          setCurrentQuestion(
            createApprovalQuestion({
              approve: tc('chatsocket.buttons.approve_arts'),
              edit: tc('chatsocket.buttons.batch_edit'),
              reject: tc('chatsocket.buttons.reject'),
            }),
          );
        });

        socketRef.current.on('campaign_completed', (data) => {
          if (!isEventFromActiveCampaign(data?.campaignArtGenerationId)) {
            return;
          }
          setCampaignStatus('completed');
          campaignStatusRef.current = 'completed';
          syncStrategyFromSocket(data?.strategyResult);

          if (isTransitioningRef.current) {
            return;
          }

          setMessages((prev) => {
            const lastOrder = prev.reduce(
              (max, m) => Math.max(max, Number(m?.messageOrder ?? 0)),
              0,
            );

            return [
              ...prev,
              // {
              //   backend: data.message || 'Campanha finalizada com sucesso!',
              //   frontend: '',
              //   messageOrder: lastOrder + 0.1,
              //   stepOrder: 10000,
              // },
              // {
              //   backend: ARTS_APPROVED_NEXT_STEP_MESSAGE,
              //   frontend: '',
              //   messageOrder: lastOrder + 0.2,
              //   stepOrder: 10000,
              // },
            ];
          });
          setCurrentQuestion({
            stepOrder: null,
            stepName: 'campaign_completed_actions',
            messageOrder: null,
            question: tc('chatsocket.completion.what_now'),
            options: [
              { name: tc('chatsocket.buttons.select_plan'), type: 'MOCK_SELECT_PLAN' },
              { name: tc('chatsocket.buttons.new_campaign'), type: 'MOCK_NEW_CAMPAIGN' }
              // { name: 'Finalizar', type: 'MOCK_FINISH' },
            ],
            isMockOption: true,
          });
        });

        socketRef.current.on('campaign_arts', (data) => {
          setCampaignStatus('arts_approved');
          setHasSelectedSchedulePublicationItem(false);
          setSelectedArt(null);
          setIsApprovingArts(false);
          clearArtApprovalLoadingRef.current();
          syncStrategyFromSocket(data?.strategyResult);
        });

        socketRef.current.on('zip_ready', (data) => {
          if (!isEventFromActiveCampaign(data?.campaignArtGenerationId)) {
            setIsZipDownloading(false);
            return;
          }
          try {
            appendMessagesWithSequentialOrder([
              {
                backend: tc('chatsocket.loading.downloading_zip'),
                frontend: '',
              },
            ]);
            const byteString = atob(data.zipBase64);
            const bytes = new Uint8Array(byteString.length);
            for (let i = 0; i < byteString.length; i++) {
              bytes[i] = byteString.charCodeAt(i);
            }
            const blob = new Blob([bytes], { type: 'application/zip' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = data.zipFileName || tc('chatsocket.download_zip_filename');
            a.click();
            URL.revokeObjectURL(url);
          } catch (err) {
            console.error('Erro ao baixar zip:', err);
          } finally {
            setIsZipDownloading(false);
          }
        });

        socketRef.current.on('error', async (data) => {
          clearPendingStrategySync();
          const wasSingleEditInFlight = awaitingSingleArtEditRef.current;
          const isCampaignInitiationLimit =
            data?.code === CAMPAIGN_INITIATION_LIMIT_CODE ||
            data?.message?.includes(CAMPAIGN_INITIATION_LIMIT_MESSAGE);

          if (isCampaignInitiationLimit) {
            const hasMinimumCampaignArts = await checkArtBalance(
              MIN_ARTS_TO_CREATE_CAMPAIGN,
              {
                failClosedOnError: true,
              },
            );

            console.error(
              hasMinimumCampaignArts
                ? 'Erro de campanha iniciado pelo backend, mas o saldo de artes está dentro do mínimo.'
                : 'Saldo mínimo de artes não atingido. Abrindo paywall.',
              data,
            );
            setIsStrategyLoading(false);
            setIsEditingArt(false);
            lastEditPieceNumberRef.current = null;
            awaitingSingleArtEditRef.current = false;

            if (hasMinimumCampaignArts) {
              if (lastQuestionRef.current) {
                appendMessagesWithSequentialOrder([
                  {
                    backend:
                      tc('chatsocket.errors.campaign_start_failed'),
                    frontend: '',
                  },
                ]);
                setCurrentQuestion(lastQuestionRef.current);
              }
              return;
            }

            openArtLimitPaywall({
              source: 'campaign_initiation',
              requiredArts: MIN_ARTS_TO_CREATE_CAMPAIGN,
              eventContext: 'campaign_initiation_limit',
            });
            return;
          }

          const isAiSuggestionLimit =
            data?.code === AI_SUGGESTION_LIMIT_CODE ||
            data?.message?.includes(AI_SUGGESTION_LIMIT_MESSAGE);

          if (isAiSuggestionLimit) {
            notifyWarningCustom(t('plans.ai_suggestion_limit_reached'));
            setIsGeneratingSuggestion(false);
            return;
          }

          if (lastQuestionRef.current) {
            openArtLimitPaywall({
              source: 'art_edit',
              eventContext: 'socket_error',
            });
            setCurrentQuestion(lastQuestionRef.current);
          }
          console.error('Erro no socket:', data);
          clearArtApprovalLoadingRef.current();
          setIsStrategyLoading(false);
          setIsEditingArt(false);
          lastEditPieceNumberRef.current = null;
          awaitingSingleArtEditRef.current = false;

          const isInvalidOrchestrator = data.message?.includes(
            'Resposta inválida do orquestrador',
          );

          setMessages((prev) => {
            const hasLoadingMessage =
              prev.length === 1 &&
              prev[0].backend === tc('chatsocket.loading.loading_conversation');
            const newMessage = {
              backend:
                wasSingleEditInFlight
                  ? data.message || tc('chatsocket.errors.art_edit_failed')
                  : data.message || tc('chatsocket.errors.generic_error'),
              frontend: '',
              color: 'red',
            };
            if (hasLoadingMessage) {
              return [newMessage];
            }
            return [
              ...prev.filter((item) => !item?.isStrategyProcessingMessage),
              newMessage,
            ];
          });

          if (isInvalidOrchestrator) {
            setCurrentQuestion({
              options: [
                {
                  id: 'retry-campaign',
                  name: tc('chatsocket.buttons.restart_campaign'),
                  type: 'mock_retry_campaign',
                },
              ],
              isMockOption: true,
            });
          }
        });

        // Restore last active campaign after socket listeners are set up
        if (savedCampaignId && socketRef.current && userId && authToken) {
          try {
            handleSelectCampaign(savedCampaignId);
          } catch (_) {
            // noop - restoration is best-effort
          }
          try {
            localStorage.removeItem(LAST_ACTIVE_CAMPAIGN_KEY);
          } catch (_) {
            // noop
          }
        }
      } catch (error) {
        console.error('Erro ao inicializar socket:', error);
        setMessages([
          {
            backend: tc('chatsocket.errors.connection_error_page'),
            frontend: '',
          },
        ]);
      }
    };

    initializeSocket();

    return () => {
      cancelled = true;
      clearAllManagedTimeouts();
      clearPendingStrategySync();
      if (persistPieceLegendTimeoutRef.current) {
        clearTimeout(persistPieceLegendTimeoutRef.current);
        persistPieceLegendTimeoutRef.current = null;
      }
      if (persistPieceScheduleTimeoutRef.current) {
        clearTimeout(persistPieceScheduleTimeoutRef.current);
        persistPieceScheduleTimeoutRef.current = null;
      }
      if (socketRef.current) {
        socketRef.current.off('connect');
        socketRef.current.off('disconnect');
        socketRef.current.off('connect_error');
        socketRef.current.off('reconnect_attempt');
        socketRef.current.off('art_credits_updated');
        socketRef.current.off('campaign_state');
        socketRef.current.off('next_question');
        socketRef.current.off('flow_completed');
        socketRef.current.off('campaign_queue_progress');
        socketRef.current.off('campaign_processed');
        socketRef.current.off('strategy_edited');
        socketRef.current.off('strategy_regenerated');
        socketRef.current.off('strategy_updated');
        socketRef.current.off('piece_legend_persisted');
        socketRef.current.off('piece_schedule_persisted');
        socketRef.current.off('campaign_art_progress');
        socketRef.current.off('campaign_art_generated');
        socketRef.current.off('campaign_art_batch_edited');
        socketRef.current.off('campaign_art_edited');
        socketRef.current.off('bulk_step0_input_generated');
        socketRef.current.off('zip_ready');
        socketRef.current.off('campaign_completed');
        socketRef.current.off('error');
        socketRef.current.disconnect();
      }
    };
  }, []);

  // const getParametrizationsData = async () => {
  //   const businessData = await getBusinessByLogin();
  //   const businessId = businessData.data[0]?.uuid;
  //   const businessDescription = businessData.data[0]?.description;
  //   const branding = await getBrandingByBusinessId(businessId);
  //   const niche = await getBusinessNichebyId(
  //     businessData.data[0]?.businessNicheId,
  //   );
  //   const colors = {
  //     primary: branding.data[0]?.primaryColor,
  //     secondary: branding.data[0]?.secondaryColor,
  //     tertiary: branding.data[0]?.tertiaryColor,
  //     complementaryColor1: branding.data[0]?.complementaryColor1,
  //     complementaryColor2: branding.data[0]?.complementaryColor2,
  //   };

  //   const data = {
  //     businessDescription: businessDescription,
  //     colors: colors,
  //     niche: niche,
  //   };

  //   return data;
  // };

  const checkArtBalance = async (
    required = 1,
    options = {},
  ) => {
    const {
      failClosedOnError = false,
    } = options;

    try {
      if (isArtCreditsSyncPending()) {
        await waitForArtCreditsSync();
      }

      const artAvailability = await getArtGenerationAvailability(
        required,
        {
          allowCampaignGeneration: true,
        },
      );
      applyArtAvailability(artAvailability);
      return artAvailability.hasAvailableArts;
    } catch (error) {
      console.error('Error checking art balance:', error);
      return failClosedOnError ? false : true;
    }
  };

  const resolveCampaignDownloadAccessWithRetry = async () => {
    if (canDownloadCampaignsRef.current) {
      return true;
    }

    if (hasCampaignDownloadPurchasedRef.current) {
      return resolveCampaignDownloadAccess();
    }

    const shouldWaitForWebhook =
      isArtCreditsSyncPending() ||
      Date.now() < optimisticDownloadUnlockUntilRef.current;

    if (!shouldWaitForWebhook) {
      return resolveCampaignDownloadAccess();
    }

    return waitForCampaignDownloadAccess({
      campaignId: campaignArtGenerationIdRef.current,
      syncUserCanDownloadCampaigns,
      getCampaignDownloadPurchased: (campaignId) =>
        EventMapService.getCampaignDownloadPurchased(campaignId),
      onCampaignPurchased: applyCampaignSpecificDownloadAccess,
    });
  };

  const openCampaignCreationPaywall = ({
    source = 'new_campaign',
    eventContext = source,
    requiredArts = MIN_ARTS_TO_CREATE_CAMPAIGN,
  } = {}) => {
    openArtLimitPaywall({
      source,
      requiredArts,
      eventContext,
    });
  };

  const ensureCampaignCreationAllowed = async ({
    source = 'new_campaign',
    eventContext = source,
    requiredArts = MIN_ARTS_TO_CREATE_CAMPAIGN,
  } = {}) => {
    const hasBalance = await checkArtBalance(requiredArts, {
      failClosedOnError: true,
    });
    if (!hasBalance) {
      openCampaignCreationPaywall({
        source,
        eventContext,
        requiredArts,
      });
      return false;
    }

    return true;
  };

  // const checkCampaignBalance = async (required = 1) => {
  //   try {
  //     const campaignAvailability = await getCampaignAvailability(required)
  //     return campaignAvailability.hasAvailableCampaigns
  //   } catch (error) {
  //     console.error('Error checking campaign balance:', error)
  //     return true
  //   }
  // }

  // const checkStrategyBalance = async (required = 1) => {
  //   try {
  //     const strategyAvailability = await getStrategyAvailability(required)
  //     return strategyAvailability.hasAvailableStrategies
  //   } catch (error) {
  //     console.error('Error checking strategy balance:', error)
  //     return true
  //   }
  // }

  const handleEditArt = async (selectedImage, editText, imageUrls) => {
    if (!socketRef.current || !campaignArtGenerationId || isEditingArt) return;

    if (artLimitFallback?.mode === 'freeze') {
      return;
    }

    const hasBalance = await checkArtBalance(1);
    if (!hasBalance) {
      openArtLimitPaywall({
        source: 'art_edit',
        requiredArts: 1,
        eventContext: 'art_edit',
      });
      return;
    }

    const imageUrl =
      selectedImage?.url || selectedImage?.imageUrl || selectedImage;
    if (!imageUrl) return;

    setActiveTab('artes');
    handleConfirmEditArt({ selectedImage, editText, imageUrls });
  };

  const handleConfirmEditArt = async (editArtData = pendingEditArtData) => {
    if (!socketRef.current || !campaignArtGenerationId || !editArtData)
      return;

    let { selectedImage, editText, imageUrls } = editArtData;

    // Converte e faz upload das base64 antes de emitir o socket
    if (imageUrls && Array.isArray(imageUrls)) {
      imageUrls = await uploadBase64Urls(imageUrls);
    }

    const selectedImageUrl =
      selectedImage?.url || selectedImage?.imageUrl || selectedImage;
    let pieceNumber = selectedImage?.piece_number ?? selectedImage?.pieceNumber;
    const arts = campaignArtsRef.current || [];
    const imgs = imgsArrayRef.current || [];
    const lookupPool = arts.length > 0 ? arts : imgs;

    if (pieceNumber == null && typeof selectedImage === 'object' && selectedImage != null) {
      const candidateUrl = selectedImage.url || selectedImage.imageUrl;
      if (candidateUrl) {
        const found = lookupPool.find(
          (a) =>
            artUrlsLooselyMatch(a.imageUrl, candidateUrl) ||
            artUrlsLooselyMatch(a.url, candidateUrl),
        );
        const resolved = found?.piece_number ?? found?.pieceNumber;
        if (resolved != null) {
          pieceNumber = resolved;
        }
      }
    }
    if (pieceNumber == null && lookupPool.length === 1) {
      const only = lookupPool[0];
      pieceNumber = only?.piece_number ?? only?.pieceNumber;
    }

    lastEditPieceNumberRef.current = normalizeCampaignPieceNumber(pieceNumber);
    awaitingSingleArtEditRef.current = true;

    setIsEditingArt(true);
    setPreviewAttachedImages([]);

    const updateArtMessages = [
      {
        frontend: editText,
        backend: '',
        imageUrls: imageUrls,
      },
      {
        frontend: '',
        backend: tc('chatsocket.chat.art_edit_ack'),
      },
      {
        frontend: '',
        backend: tc('chatsocket.loading.processing_art_edit'),
      },
    ]

    appendMessagesWithSequentialOrder(updateArtMessages)

    socketRef.current.emit('edit_campaign_art', {
      campaignArtGenerationId,
      pieceNumber,
      imageUrl: imageUrls || selectedImageUrl,
      editInstruction: editText,
      messages: updateArtMessages
    });

    if (editArtData === pendingEditArtData) {
      setPendingEditArtData(null);
    }
    setPreviewAttachedImages([]);
    setReferenceArt(null);
    setCurrentQuestion(null);
  };



  const getImageUrl = (img) => {
    if (img.fileDataBase64) return img.fileDataBase64;
    if (img.image) return img.image;
    if (img.base64) return `data:image/png;base64,${img.base64}`;
    if (img.url) return img.url;
    return null;
  };

  const getImageDataWithRoles = (images) => {
    if (!images || images.length === 0) return [];
    return images
      .map((img) => {
        const url = getImageUrl(img);
        if (!url) return null;
        return { role: (img.role || 'principal').toLowerCase(), image: url };
      })
      .filter(Boolean);
  };

  // Helper para fazer upload de array de urls base64 (retorna array misto de URLs S3 e URLs normais)
  const uploadBase64Urls = async (urls) => {
    if (!urls || !Array.isArray(urls)) return urls;
    const urlsToUpload = urls.filter(url => typeof url === 'string' && url.startsWith('data:image'));
    if (urlsToUpload.length === 0) return urls;

    setIsStrategyLoading(true);
    try {
      const formData = new FormData();
      urlsToUpload.forEach((url, index) => {
        const file = base64ToBlob(url, `upload-${index}.png`);
        if (file) formData.append('files', file);
      });
      
      const uploadRes = await postCampaignImagesUpload(formData);
      if (uploadRes && uploadRes.data && uploadRes.data.success) {
        let uploadedIndex = 0;
        const uploadedUrls = uploadRes.data.data;
        const result = urls.map(url => {
          if (typeof url === 'string' && url.startsWith('data:image')) {
            const newUrl = uploadedUrls[uploadedIndex]?.url;
            uploadedIndex++;
            return newUrl || url;
          }
          return url;
        });
        return result;
      }
    } catch (e) {
      console.error('Failed to upload base64 urls', e);
    } finally {
      setIsStrategyLoading(false);
    }
    return urls;
  };

  // Helper para objetos [{ role, image }]
  const uploadBase64ImagesData = async (imagesDataWithRoles) => {
    if (!imagesDataWithRoles || imagesDataWithRoles.length === 0) return imagesDataWithRoles;
    const urls = imagesDataWithRoles.map(d => d.image);
    const uploadedUrls = await uploadBase64Urls(urls);
    return imagesDataWithRoles.map((d, i) => ({ ...d, image: uploadedUrls[i] || d.image }));
  };

  const buildAnswerWithImages = (text, imageUrls) => {
    const answerText = text || '';
    if (!imageUrls || imageUrls.length === 0) {
      return [answerText];
    }
    const normalizedUrls = imageUrls
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object') {
          return item.image || item.url || null;
        }
        return null;
      })
      .filter((v) => typeof v === 'string' && v.trim().length > 0);
    return [answerText, ...normalizedUrls];
  };

  const createApprovalQuestion = (labels = {}, excludeTypes = []) => {
    const defaultLabels = {
      approve: tc('chatsocket.buttons.approve'),
      edit: tc('chatsocket.buttons.edit'),
      reject: tc('chatsocket.buttons.reject'),
    };

    const allOptions = [
      { name: labels.approve || defaultLabels.approve, type: 'approve' },
      { name: labels.edit || defaultLabels.edit, type: 'edit' },
      { name: labels.reject || defaultLabels.reject, type: 'reject' },
    ];

    return {
      stepOrder: null,
      stepName: null,
      messageOrder: null,
      question: null,
      options: allOptions.filter((opt) => !excludeTypes.includes(opt.type)),
      isApprovalQuestion: true,
    };
  };

  const runArtLimitDismiss = async ({
    source,
    requiredArts = 1,
    activeCampaignId,
  }) => {
    const normalizedRequiredArts = Math.max(
      1,
      Number(requiredArts) || 1,
    );

    try {
      const artAvailability = await getArtGenerationAvailability(1, {
        allowCampaignGeneration: true,
      });
      const availableArtsRaw = Number(artAvailability?.available ?? null);
      const availableArts = Number.isFinite(availableArtsRaw)
        ? availableArtsRaw
        : 0;
      const activeId =
        campaignArtGenerationIdRef.current || activeCampaignId;

      applyArtAvailability(artAvailability);
      setIsEditingArt(false);
      lastEditPieceNumberRef.current = null;
      awaitingSingleArtEditRef.current = false;

      if (source === 'batch_edit_art') {
        setBatchEditActionType(null);
        setPendingEditArtData(null);
        setPreviewAttachedImages([]);
        setReferenceArt(null);
        setMessage('');
        setActiveTab('artes');
        if (activeId) {
          setIsLoadingBatchEditArtPerCampaign((prev) => ({
            ...prev,
            [activeId]: false,
          }));
        }
        setCurrentQuestion(
          createApprovalQuestion({
            approve: tc('chatsocket.buttons.approve_arts'),
            edit: tc('chatsocket.buttons.batch_edit'),
            reject: tc('chatsocket.buttons.reject'),
          }),
        );
        setArtLimitFallback(null);

        if (availableArts < normalizedRequiredArts) {
          setBatchEditLimitNoticeVariant(
            availableArts <= 0 ? 'zero' : 'partial',
          );
        } else {
          setBatchEditLimitNoticeVariant(null);
        }

        return;
      }

      let nextMode = null;

      if (availableArts <= 0) {
        nextMode = 'freeze';
      } else if (
        source === 'batch_edit_art' &&
        availableArts < normalizedRequiredArts
      ) {
        nextMode = 'batch_unit';
      } else if (availableArts < normalizedRequiredArts) {
        nextMode = 'freeze';
      }

      if (nextMode) {
        setArtLimitFallback({
          mode: nextMode,
          requiredArts: normalizedRequiredArts,
          source,
        });
      } else {
        setArtLimitFallback(null);
      }

      if (source === 'batch_edit_art' || source === 'art_edit' || nextMode) {
        const fallbackMessage = [
          {
            frontend: '',
            backend: nextMode === 'freeze'
              ? tc('chatsocket.insufficient_balance.limit_reached')
              : source === 'batch_edit_art'
                ? tc('chatsocket.insufficient_balance.batch_edit_paused')
                : tc('chatsocket.insufficient_balance.individual_edit_available'),
          },
        ];

        appendMessagesWithSequentialOrder(fallbackMessage);
      }
    } catch (error) {
      console.error('Erro ao revalidar saldo após fechamento do paywall:', error);

      if (source === 'batch_edit_art') {
        setBatchEditActionType(null);
        setPendingEditArtData(null);
        setPreviewAttachedImages([]);
        setReferenceArt(null);
        setMessage('');
        setActiveTab('artes');
        if (activeCampaignId) {
          setIsLoadingBatchEditArtPerCampaign((prev) => ({
            ...prev,
            [activeCampaignId]: false,
          }));
        }
        setCurrentQuestion(
          createApprovalQuestion({
            approve: tc('chatsocket.buttons.approve_arts'),
            edit: tc('chatsocket.buttons.batch_edit'),
            reject: tc('chatsocket.buttons.reject'),
          }),
        );
        setArtLimitFallback(null);
        setBatchEditLimitNoticeVariant('zero');
        return;
      }

      setArtLimitFallback({
        mode: 'freeze',
        requiredArts: normalizedRequiredArts,
        source,
      });
      appendMessagesWithSequentialOrder([
        {
          backend: tc('chatsocket.insufficient_balance.limit_reached'),
          frontend: '',
        },
      ]);
    }
  };

  const handleArtLimitFlowDismiss = async () => {
    const context = artLimitDismissContextRef.current;
    if (!context) {
      return;
    }

    artLimitDismissContextRef.current = null;
    await runArtLimitDismiss(context);
  };

  const openArtLimitPaywall = ({
    source,
    requiredArts = 1,
    eventContext = source,
  }) => {
    const activeCampaignId =
      campaignArtGenerationIdRef.current || campaignArtGenerationId;

    if (activeCampaignId) {
      EventMapService.postEventMap({
        event: 'insufficient_balance_offer_view',
        eventData: {
          campaignId: activeCampaignId,
          source: 'campaign',
          context: eventContext,
        },
      });
    }

    const dismissContext = {
      source,
      requiredArts,
      activeCampaignId,
    };

    const artLimitFlowRef =
      !isUserWithoutSubscriptionRef.current ||
      canDownloadCampaignsRef.current
        ? subscribedArtsLimitUpsellFlowRef
        : artsLimitUpsellFlowRef;

    if (openUpsellFlow(artLimitFlowRef, true)) {
      artLimitDismissContextRef.current = dismissContext;
      return;
    }

    openPaywall({
      reason: 'art_generation_limit_reached',
      onDismiss: async () => {
        await runArtLimitDismiss(dismissContext);
        closePaywall();
      },
    });
  };

  async function sendMessage(text) {
    if (!socketRef.current || messageSubmitInProgressRef.current) {
      return;
    }

    if (text?.trim()) {
      finalizePendingCampaignPrompt();
    }

    // Gate: block campaign creation when the user has fewer than 5 arts.
    if (
      !campaignArtGenerationId &&
      !(await ensureCampaignCreationAllowed({
        source: 'new_campaign',
        eventContext: 'chat_message',
      }))
    ) {
      return;
    }

    suppressTypingDuringRestoreRef.current = false;

    if (!currentQuestion && selectedArt) {
      const editText = typeof text === 'string' ? text.trim() : '';
      if (!editText) return;

      const attachedUrls = previewAttachedImages
        .map((img) => getImageUrl(img))
        .filter(Boolean);

      const baseUrl = selectedArt?.url || selectedArt?.imageUrl || selectedArt;
      const imageUrls = attachedUrls.length ? [baseUrl, ...attachedUrls] : [baseUrl];

      handleEditArt(selectedArt, editText, imageUrls);
      clearInputMessage();
      return;
    }

    const isProductImage = currentQuestion.component === 'ProductImageSelector';
    const isVisualStyle = currentQuestion.component === 'VisualStyleSelector';
    const isSpecial = !!currentQuestion.component;
    const hasAttachedPreviewImages = previewAttachedImages.length > 0;

    const hasImagesToSend =
      (isProductImage && selectedProductImages.length > 0) ||
      (isVisualStyle && (selectedTemplateImage || referenceArt)) ||
      !!referenceArt ||
      hasAttachedPreviewImages;

    if (!text?.trim() && !hasImagesToSend) {
      return;
    }

    if (currentQuestion.stepName === 'edit_strategy') {
      lockMessageSubmit();
      const editStrategyMessages = [
        {
          frontend: tc('chatsocket.buttons.edit'),
        },
        {
          frontend: text,
          backend:
            tc('chatsocket.strategy.edit_question'),
        },
        {
          backend: tc('chatsocket.loading.strategy_processing'),
        },
      ];

      appendMessagesWithSequentialOrder([
        {
          frontend: text,
        },
        {
          backend: editStrategyMessages.at(-1)?.backend || '',
          isStrategyProcessingMessage: true,
        },
      ]);

      appendAndReturnSocketMessages(socketRef.current)


      socketRef.current.emit('edit_strategy', {
        campaignArtGenerationId,
        userInput: text,
        markdown: markdownContent,
        authToken,
        messages: editStrategyMessages,
      });
      queueProgressIndexRef.current = 0;
      clearPersistedQueueProgress(campaignArtGenerationId);
      setIsStrategyLoading(true);
      scheduleStrategyRecovery('edit_strategy', campaignArtGenerationId);


      // }


      setCurrentQuestion(null);
      clearInputMessage();
      return;
    }

    if (currentQuestion.stepName === 'reject_strategy') {
      lockMessageSubmit();
      const rejectMessages = [
        { backend: tc('chatsocket.strategy.reject_question') },
        { frontend: text.trim() || tc('chatsocket.buttons.regenerate_from_scratch'), backend: '' },
        { frontend: '', backend: tc('chatsocket.strategy.regenerating') },
      ]

      appendMessagesWithSequentialOrder(rejectMessages)

      socketRef.current.emit('reject_strategy', {
        campaignArtGenerationId,
        userInput: text.trim() || tc('chatsocket.buttons.regenerate_from_scratch'),
        authToken,
        messages: [{ frontend: tc('chatsocket.buttons.reject'), backend: '' }, ...rejectMessages]
      });

      queueProgressIndexRef.current = 0;
      clearPersistedQueueProgress(campaignArtGenerationId);
      setQueueSteps(getInitialQueueSteps().map((s) => ({ ...s, status: 'idle' })));
      setIsStrategyLoading(true);
      scheduleStrategyRecovery('reject_strategy', campaignArtGenerationId);
      setCurrentQuestion(null);
      clearInputMessage();
      return;
    }

    if (currentQuestion.stepName === 'batch_edit_art') {
      lockMessageSubmit();
      const currentArtsCount =
        campaignArtsRef.current?.length || imgsArray.length || 0;
      const requiredArts = Math.max(1, currentArtsCount);
      const hasBalance = await checkArtBalance(requiredArts);

      const batchEditArtMessages = [
        {
          frontend: text,
          backend: tc('chatsocket.strategy.batch_edit_question'),
        },
        { backend: tc('chatsocket.strategy.batch_edit_consumption', { count: requiredArts, s: requiredArts > 1 ? 's' : '' }) },
      ]


      appendMessagesWithSequentialOrder(batchEditArtMessages)

      if (!hasBalance) {
        releaseMessageSubmitLock();
        openArtLimitPaywall({
          source: 'batch_edit_art',
          requiredArts,
          eventContext: 'batch_edit_art',
        });
        return;
      }

      //  [{
      //         answer: ['Editar em lote'],
      //         options: [],
      //         question: '',
      //       },
      //       {
      //         answer:
      //           typeof data.userInput === 'string' && data.userInput.trim() !== ''
      //             ? [data.userInput.trim()]
      //             : [],
      //         options: [],
      //         question:
      //           'Entendido! O que você gostaria de ajustar no estilo visual das artes atuais? Pode digitar abaixo.',
      //       },
      //       {
      //         answer: ['noAnswer'],
      //         options: [],
      //         question: 'Esta edição consumirá 4 limites de arte do seu plano.',
      //       },
      //       {
      //         answer: ['Confirmar'],
      //         options: [],
      //         question: '',
      //       },
      //       {
      //         answer: ['noAnswer'],
      //         options: [],
      //         question: 'Processando edição em lote...',
      //       }
      // ]

      setPendingEditArtData({ text, actionType: batchEditActionType });
      setCurrentQuestion({
        stepOrder: null,
        stepName: 'confirm_batch_edit_art',
        messageOrder: 999,
        question: tc('chatsocket.strategy.batch_edit_consumption', { count: requiredArts, s: requiredArts > 1 ? 's' : '' }),
        options: [
          { name: 'Confirmar', type: 'MOCK_CONFIRM_BATCH_EDIT_ART' },
          { name: 'Cancelar', type: 'MOCK_CANCEL_BATCH_EDIT_ART' },
        ],
        isMockOption: true,
      });
      releaseMessageSubmitLock();

      clearInputMessage();
      return;
    }

    const answer = text?.trim() || '';

    let displayImageUrls = [];
    let socketImageData = [];

    if (isProductImage && selectedProductImages.length > 0) {
      socketImageData = getImageDataWithRoles(selectedProductImages);
      displayImageUrls = socketImageData.map((d) => d.image);
    }

    if (hasAttachedPreviewImages) {
      const attachedImageData = getImageDataWithRoles(previewAttachedImages);
      socketImageData = [...socketImageData, ...attachedImageData];
      displayImageUrls = [...displayImageUrls, ...attachedImageData.map((d) => d.image)];
    }

    if (referenceArt) {
      const refUrl =
        referenceArt.fileDataBase64 ||
        referenceArt.base64 ||
        referenceArt.url;
      if (refUrl) {
        socketImageData.push({ role: 'reference', image: refUrl });
        displayImageUrls.push(refUrl);
      }
    }

    // --- UPLOAD BASE64 ANTES DO SOCKET ---
    if (socketImageData.length > 0) {
      socketImageData = await uploadBase64ImagesData(socketImageData);
      displayImageUrls = socketImageData.map(d => d.image);
    }
    // -------------------------------------

    const templateLabel =
      isVisualStyle && selectedTemplateImage
        ? selectedTemplateImage.label
        : null;
    const referenceLabel =
      isVisualStyle && referenceArt ? tc('chatsocket.labels.reference_art_provided') : null;
    const answerText =
      answer ||
      (isProductImage
        ? tc('chatsocket.labels.product_images_provided')
        : templateLabel || referenceLabel || (hasAttachedPreviewImages ? tc('chatsocket.labels.preview_image_provided') : null)) ||
      '';

    if (isBulkStep0Active) {
      const bulkText = text?.trim() || '';
      appendMessagesWithSequentialOrder([
        {
          frontend: bulkText,
          stepOrder: 0,
          messageOrder: -0.1,
        }
      ]);
      const bulkPayload = {
        campaignArtGenerationId: campaignArtGenerationId,
        userAccountId: userId,
        bulkAnswer: bulkText,
        lang: localStorage.getItem('i18nextLng') || 'ptBR',
      };
      socketRef.current.emit('answer_bulk_step0', bulkPayload);
      setIsBulkStep0Active(false);
    } else {
      setMessages((prev) => {
        const messagesToAppend = [];
        const questionMessage = buildQuestionMessageForChat(currentQuestion);

        if (
          isParameterizationQuestion(currentQuestion) &&
          questionMessage &&
          !hasBackendMessage(prev, questionMessage.backend)
        ) {
          messagesToAppend.push(questionMessage);
        }

        messagesToAppend.push({
          frontend: answerText,
          imageUrls: displayImageUrls,
        });

        const lastOrder = prev.reduce((max, item) => {
          const order = getFiniteOrderValue(item?.messageOrder);
          return order != null ? Math.max(max, order) : max;
        }, 0);

        return [
          ...prev,
          ...messagesToAppend.map((item, index) => ({
            ...item,
            messageOrder: Number((lastOrder + (index + 1) * 0.1).toFixed(1)),
          })),
        ];
      });
      const payload = {
        campaignArtGenerationId: campaignArtGenerationId,
        userAccountId: userId,
        stepOrder: currentQuestion?.stepOrder ?? 0,
        messageOrder: currentQuestion?.messageOrder ?? 0,
        answer: buildAnswerWithImages(answerText, socketImageData),
        lang: localStorage.getItem('i18nextLng') || 'ptBR',
      };
      socketRef.current.emit('answer_question', payload);
    }
    refreshCampaigns();

    if (isSpecial) {
      setPendingAnswerQuestion(null);
      if (isProductImage) {
        setSelectedProductImages([]);
      }
      if (isVisualStyle) {
        setSelectedTemplateImage(null);
      }
      setReferenceArt(null);
    }

    setPreviewAttachedImages([]);

    lastQuestionRef.current = currentQuestion;
    setCurrentQuestion(null);
    clearInputMessage();
  }

  const handleSendMessage = (textOrEvent) => {
    if (textOrEvent?.preventDefault) {
      textOrEvent.preventDefault();
    }
    if (!currentQuestion || messageSubmitInProgressRef.current) {
      return;
    }

    const isProductImage = currentQuestion?.component === 'ProductImageSelector';
    const isVisualStyle = currentQuestion?.component === 'VisualStyleSelector';
    const hasAttachedPreviewImages = previewAttachedImages.length > 0;

    const hasImagesToSend =
      (isProductImage && selectedProductImages.length > 0) ||
      (isVisualStyle && (selectedTemplateImage || referenceArt)) ||
      !!referenceArt ||
      hasAttachedPreviewImages;

    const finalMessage =
      typeof textOrEvent === 'string' ? textOrEvent : message;

    if (finalMessage?.trim() || hasImagesToSend) {
      sendMessage(finalMessage);
    }
  };

  useEffect(() => {
    if (pendingCampaignPromptSentRef.current) {
      return;
    }

    if (!currentQuestion || !socketRef.current) {
      return;
    }

    const storedPrompt = getPendingCampaignPrompt();
    if (!storedPrompt) {
      return;
    }

    pendingCampaignPromptSentRef.current = true;
    finalizePendingCampaignPrompt();
    sendMessage(storedPrompt);
  }, [currentQuestion, campaignArtGenerationId]);

  const [isGeneratingSuggestion, setIsGeneratingSuggestion] = useState(false);

  useEffect(() => {
    getAISuggestionAvailability()
      .then((result) => setHasAvailableAISuggestions(result.hasAvailableArts))
      .catch(() => setHasAvailableAISuggestions(true));
  }, []);

  const handleGenerateBulkStep0 = () => {
    if (!showAISuggestionBanner || !socketRef.current || !userId || isGeneratingSuggestion) return;
    setIsGeneratingSuggestion(true);
    socketRef.current.emit('generate_bulk_step0_input', {
      userAccountId: userId,
      campaignArtGenerationId: campaignArtGenerationIdRef.current,
      authToken: authTokenRef.current,
      lang: localStorage.getItem('i18nextLng') || 'ptBR',
    });
  };

  const handleChatEditArtRequest = (editText) => {
    const baseImage = selectedArt?.url || selectedArt?.imageUrl || selectedArt;
    const attachedUrls = previewAttachedImages
      .map((img) => {
        if (img.fileDataBase64) return img.fileDataBase64;
        if (img.image) return img.image;
        if (img.base64) return img.base64;
        if (img.url) return img.url;
        if (img.imageUrl) return img.imageUrl;
        if (typeof img === 'string') return img;
        return null;
      })
      .filter(Boolean);

    if (referenceArt) {
      const refUrl =
        referenceArt.fileDataBase64 ||
        referenceArt.base64 ||
        referenceArt.url;
      if (refUrl) {
        attachedUrls.push(refUrl);
      }
    }

    if (!selectedArt || (!editText?.trim() && attachedUrls.length === 0)) return;
    const imageUrls = [baseImage, ...attachedUrls];

    handleEditArt(selectedArt, editText.trim(), imageUrls);
    setMessage('');
  };

  const handleApproval = async (optionName, optionType) => {
    if (
      !pendingApproval ||
      !socketRef.current ||
      approvalActionInProgressRef.current
    ) {
      return;
    }

    approvalActionInProgressRef.current = true;
    disableCurrentApprovalOptions();

    appendMessagesWithSequentialOrder(
      [
        {
          frontend: optionName,
          backend: '',
        },
      ]
    );

    const callbacks = {
      approve: pendingApproval.onApprove,
      edit: pendingApproval.onEdit,
      reject: pendingApproval.onReject,
    };

    const callback = callbacks[optionType];
    try {
      if (callback) {
        await callback(pendingApproval.data);
      }
    } catch (error) {
      releaseApprovalActionLock();
      restoreCurrentApprovalOptions();
      console.error('Erro ao processar aprovação:', error);
      return;
    }

    if (optionType === 'approve') {
      setPendingApproval(null);
      if (pendingApproval?.type === 'strategy') {
        setCampaignStatus('strategy_approved');
      }

      return;
    }

    if (optionType === 'reject') {
      setPendingApproval(null);
      releaseApprovalActionLock();
      return;
    }

    if (optionType === 'edit') {
      setPendingApproval(null);
      releaseApprovalActionLock();
    }
  };

  const handleDismissUserAvaliation = () => {
    dismissCampaignEvaluationForSession();
    setIsUserAvaliationOpen(false);
  };

  const handleSendUserAvaliation = async (rating) => {
    setIsSubmittingCampaignEvaluation(true);

    try {
      const result = await submitCampaignEvaluation({
        rating: rating || 0,
        origin: 'campaignChatSocket',
        campaignId: campaignArtGenerationId,
      });

      if (result.success) {
        appendMessagesWithSequentialOrder([
          { backend: tc('chatsocket.avaliation.feedback_thanks') },
        ]);
        setCampaingAvaliationStatus(true);
        setIsUserAvaliationOpen(false);
        setIsAvaliationRewardModalOpen(true);
        await updateArtLimitsRef.current();
      }
    } catch (error) {
      console.error('Erro ao enviar avaliacao do usuario:', error);
    } finally {
      setIsSubmittingCampaignEvaluation(false);
    }
  };

  const handleSelectOption = async (optionName, extraData = null) => {
    if (
      currentQuestion?.isApprovalQuestion &&
      approvalActionInProgressRef.current
    ) {
      return;
    }

    if (optionName === tc('chatsocket.strategy.schedule_option')) {
      pendingSocialMediaChoiceLabelRef.current = optionName;
    }

    const skipBalanceCheckOptions = [
      tc('chatsocket.buttons.select_plan'),
      tc('chatsocket.buttons.finalize_campaign'),
      tc('chatsocket.buttons.finalize_campaign'),
      tc('chatsocket.buttons.finalize'),
      tc('chatsocket.strategy.schedule_option'),
      'MOCK_SOCIAL_MEDIA_FINALIZED',
      'MOCK_SCHEDULE_FINALIZED',
      'MOCK_SCHEDULING_COMPLETED',
    ];
    const selectedPostQuantity =
      optionName === 'MOCK_POST_QUANTITY_FINALIZED'
        ? Number(extraData)
        : null;
    const requiredArtsForThisAction =
      Number.isFinite(selectedPostQuantity) && selectedPostQuantity > 0
        ? selectedPostQuantity
        : 1;
    const optionLabel =
      optionName === 'MOCK_POST_QUANTITY_FINALIZED' &&
        Number.isFinite(selectedPostQuantity) &&
        selectedPostQuantity > 0
        ? `${selectedPostQuantity}`
        : optionName;

    const currentQuestionOption = currentQuestion?.options?.find(
      (opt) => opt.type === extraData,
    );
    const isCampaignCreationAction =
      optionName === tc('chatsocket.buttons.new_campaign') ||
      extraData === 'MOCK_NEW_CAMPAIGN';
    const isFreezeBlockedCampaignRestart =
      artLimitFallback?.mode === 'freeze' &&
      isCampaignCreationAction;
    if (
      currentQuestionOption?.disabled ||
      isFreezeBlockedCampaignRestart ||
      (artLimitFallback?.mode &&
        currentQuestion?.isApprovalQuestion &&
        ['edit', 'reject'].includes(extraData))
    ) {
      return;
    }

    if (
      !skipBalanceCheckOptions.includes(optionName) &&
      optionName !== tc('chatsocket.buttons.restart_campaign') &&
      !isCampaignCreationAction &&
      // Skip balance check for approval questions (approve/edit/reject flows)
      !(currentQuestion?.isApprovalQuestion && pendingApproval)
    ) {
      const hasBalance = await checkArtBalance(requiredArtsForThisAction, {
        failClosedOnError: true,
      });
      if (!hasBalance) {
        appendMessagesWithSequentialOrder([
          {
            frontend: optionLabel,
            backend: '',
          },
          {
            backend:
              optionName === 'MOCK_POST_QUANTITY_FINALIZED'
                ? tc('chatsocket.insufficient_balance.quantity_exceeds', { requiredArts: requiredArtsForThisAction })
                : tc('chatsocket.insufficient_balance.no_arts_available'),
            frontend: '',
          },
        ]);
        openArtLimitPaywall({
          source: 'post_quantity',
          requiredArts: requiredArtsForThisAction,
          eventContext: 'post_quantity_limit',
        });
        if (optionName === 'MOCK_POST_QUANTITY_FINALIZED') {
          setPostQuantitySelectorResetKey((previous) => previous + 1);
        }
        return;
      }
    }

    if (optionName === tc('chatsocket.buttons.restart_campaign')) {
      appendMessagesWithSequentialOrder([
        {
          backend: tc('chatsocket.strategy.restarting'),
        },
      ]);

      if (campaignArtGenerationId && socketRef.current) {
        socketRef.current.emit('cancel_campaign', {
          campaignArtGenerationId: campaignArtGenerationId,
        });
        setManagedTimeout(() => {
          window.location.reload();
        }, 1000);
      }
      return;
    }

    const finalizeOptions = [
      tc('chatsocket.buttons.finalize_campaign'),
      tc('chatsocket.buttons.finalize_campaign'),
      tc('chatsocket.buttons.finalize'),
      tc('chatsocket.buttons.select_plan'),
      tc('chatsocket.buttons.new_campaign'),
    ];
    // const backendMessage1 = ARTS_APPROVED_NEXT_STEP_MESSAGE;
    const backendMessage2 = tc('chatsocket.strategy.final_message');

    if (finalizeOptions.includes(optionName) && campaignStatus !== 'completed') {
      let completeMessages = [
        {
          frontend: optionName,
        },
        {
          backend: backendMessage2,
          component: 'DownloadZipCard',
        },
      ]

      const isCompletionSpecific =
        optionName === tc('chatsocket.buttons.finalize_campaign') ||
        optionName === tc('chatsocket.buttons.finalize_campaign') ||
        optionName === tc('chatsocket.buttons.finalize');
      let finalMessageOrder = 0

      if (isCompletionSpecific) {
        appendMessagesWithSequentialOrder(
          completeMessages
        );
      } else {
        isTransitioningRef.current = true;
      }

      const resolvedId =
        campaignArtGenerationIdRef.current || campaignArtGenerationId;
      if (resolvedId && socketRef.current) {
        socketRef.current.emit('complete_campaign', {
          campaignArtGenerationId: resolvedId,
          userInput: optionName,
          message2: backendMessage2,
          messageOrder: finalMessageOrder,
          feedbackMessage: 'campanha finalizada com sucesso',
          component: 'DownloadZipCard',
          messages: completeMessages
        });
      }

      if (isCompletionSpecific) {
        if (shouldRenderUserAvaliationModal) {
          setIsUserAvaliationOpen(true);
        }

        setCurrentQuestion(null);
        return;
      }
    }

    if (
      currentQuestion?.isApprovalQuestion &&
      pendingApproval &&
      !approvalActionInProgressRef.current
    ) {
      handleApproval(optionName, extraData);
      return;
    }

    // Campaign completion buttons - always allow navigation regardless of campaign ID
    const campaignCompletionButtons = [
      tc('chatsocket.buttons.select_plan'),
      tc('chatsocket.buttons.new_campaign'),
      tc('chatsocket.buttons.finalize'),
    ];
    if (campaignCompletionButtons.includes(optionName)) {
      if (optionName === tc('chatsocket.buttons.select_plan')) {
        persistActiveCampaignForReturn(campaignArtGenerationIdRef.current);
        const returnTo = `${window.location.pathname}${window.location.search}`;
        navigate(
          `/PlanCheckoutUser?returnTo=${encodeURIComponent(returnTo)}`,
        );
        return;
      }

      const isMock =
        currentQuestion?.isMockOption || extraData?.startsWith('MOCK_');
      // Allow these buttons to work even without campaign ID (campaign is completed)
      if (isMock) {
        if (optionName === tc('chatsocket.buttons.new_campaign')) {
          const canCreateCampaign = await ensureCampaignCreationAllowed({
            source: 'campaign_completion',
            eventContext: 'campaign_completion',
          });

          if (!canCreateCampaign) {
            return;
          }

          appendMessagesWithSequentialOrder([
            {
              backend: '',
              frontend: optionName,
            },
          ]);

          await handleCreateNewCampaign();
        }

        // if (optionName === 'Finalizar') {
        //   setMessages((prev) => [
        //     ...prev,
        //     {
        //       backend: '',
        //       frontend: optionName,
        //     },
        //   ]);
        //   navigation('/ChatSocket');
        //   return;
        // }
      }
    }

    const isMock =
      optionName.startsWith('MOCK_') ||
      currentQuestion?.isMockOption ||
      extraData?.startsWith('MOCK_');
    const resolvedCampaignId = isMock
      ? campaignArtGenerationIdRef.current || campaignArtGenerationId
      : campaignArtGenerationId;
    if (!resolvedCampaignId || (!currentQuestion && !isMock)) return;

    const MOCK_QUESTION_MAP = {
      MOCK_POST_QUANTITY_FINALIZED: { stepOrder: 0, messageOrder: 7.1 },
      MOCK_SOCIAL_MEDIA_FINALIZED: { stepOrder: 1, messageOrder: 9 },
      MOCK_SCHEDULE_FINALIZED: { stepOrder: 1, messageOrder: 10 },
      MOCK_SCHEDULING_COMPLETED: { stepOrder: 1, messageOrder: 11 },
    };

    if (isMock) {
      if (
        optionName === tc('chatsocket.buttons.regenerate_from_scratch') ||
        optionName.startsWith('MOCK_REGERAR_AGORA')
      ) {

        let rejectMessages = [
          { backend: tc('chatsocket.strategy.reject_question') },
          { frontend: optionName || tc('chatsocket.buttons.regenerate_from_scratch'), backend: '' },
          { frontend: '', backend: tc('chatsocket.strategy.regenerating') },
        ]

        appendMessagesWithSequentialOrder(rejectMessages);

        rejectMessages = [{ frontend: tc('chatsocket.buttons.reject'), backend: '' }, ...rejectMessages];

        socketRef.current.emit('reject_strategy', {
          campaignArtGenerationId: resolvedCampaignId,
          userInput: '',
          authToken,
          messages: rejectMessages,
        });

        setIsStrategyLoading(true);
        scheduleStrategyRecovery('reject_strategy', resolvedCampaignId);
        // Remove current question immediately so options/buttons disappear
        setCurrentQuestion(null);
        return;
      }

      if ((optionName === 'Confirmar' || extraData === 'MOCK_CONFIRM_BATCH_EDIT_ART')
        && currentQuestion?.stepName === 'confirm_batch_edit_art') {

        // [{
        //   answer: ['Editar em lote'],
        //   options: [],
        //   question: '',
        // },
        // {
        //   answer:
        //         typeof data.userInput === 'string' && data.userInput.trim() !== ''
        //           ? [data.userInput.trim()]
        //           : [],
        //   options: [],
        //   question:
        //         'Entendido! O que você gostaria de ajustar no estilo visual das artes atuais? Pode digitar abaixo.',
        // },
        // {
        //   answer: ['noAnswer'],
        //   options: [],
        //   question: 'Esta edição consumirá 4 limites de arte do seu plano.',
        // },
        // {
        //   answer: ['Confirmar'],
        //   options: [],
        //   question: '',
        // },
        // {
        //   answer: ['noAnswer'],
        //   options: [],
        //   question: 'Processando edição em lote...',
        // }]

        const batchEditArtMessages = [
          {
            frontend: optionName,
          },
          {
            backend:
              pendingEditArtData?.actionType === 'regenerate'
                ? tc('chatsocket.loading.regenerating_arts')
                : tc('chatsocket.loading.editing_batch'),
          },
        ]

        appendMessagesWithSequentialOrder(batchEditArtMessages);

        const updatedMessagesBatchEditArt = [
          {
            frontend: tc('chatsocket.buttons.batch_edit'),
          },
          {
            backend:
              tc('chatsocket.strategy.batch_edit_question')
          },

          {
            frontend: pendingEditArtData?.text || '',
          },
          {
            backend:
              pendingEditArtData?.actionType === 'regenerate'
                ? tc('chatsocket.loading.regenerating_arts')
                : tc('chatsocket.loading.editing_batch'),
          },

          {
            backend: tc('chatsocket.strategy.batch_edit_consumption', { count: 4, s: 's' }),
          },
          {
            frontend: optionName,
          },
          {
            backend: tc('chatsocket.loading.editing_batch'),
          }
        ]
        setImgsArray([]);
        setCampaignStatus('art_generation_in_progress');
        setIsEditingArt(true);
        setIsLoadingBatchEditArtPerCampaign((prev) => ({
          ...prev,
          [resolvedCampaignId]: true,
        }));

        socketRef.current.emit('batch_edit_art', {
          campaignArtGenerationId: resolvedCampaignId,
          userInput: pendingEditArtData?.text || '',
          actionType: pendingEditArtData?.actionType,
          authToken,
          messages: updatedMessagesBatchEditArt,
        });

        setCurrentQuestion(null);
        setPendingEditArtData(null);
        setBatchEditActionType(null);
        setPreviewAttachedImages([]);
        setReferenceArt(null);
        setActiveTab('artes');
        return;
      }

      if (
        (optionName === 'Cancelar' || extraData === 'MOCK_CANCEL_BATCH_EDIT_ART') &&
        currentQuestion?.stepName === 'confirm_batch_edit_art'
      ) {
        appendMessagesWithSequentialOrder([
          {
            backend: '',
            frontend: 'Cancelar',
          },
        ]);
        setPendingEditArtData(null);
        setBatchEditActionType(null);
        setActiveTab('artes');

        setCurrentQuestion(
          createApprovalQuestion({
            approve: tc('chatsocket.buttons.approve_arts'),
            edit: tc('chatsocket.buttons.batch_edit'),
            reject: tc('chatsocket.buttons.reject'),
          }),
        );
        return;
      }

      if (
        optionName === 'MOCK_SOCIAL_MEDIA_FINALIZED' &&
        Array.isArray(extraData)
      ) {
        setSocialMediaSelection(extraData);

        const selectedNetworkKeys = extraData.map((net) => net.name);
        const normalizedNetworks = normalizeNetworkList(selectedNetworkKeys);

        setArtScheduleData((prev) => {
          const updated = { ...prev };
          const allArts = campaignArtsRef.current || [];

          allArts.forEach((art) => {
            const key = art.piece_number;
            const artCaption = getArtCaption(art);
            if (!updated[key]) {
              let publishDate = '';
              let publishTime = '';
              if (art.publicationDateTime) {
                const dt = new Date(art.publicationDateTime);
                publishDate = dt.toLocaleDateString('pt-BR', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                });
                publishTime = dt.toLocaleTimeString('pt-BR', {
                  hour: '2-digit',
                  minute: '2-digit',
                });
              }
              updated[key] = {
                publishDate,
                publishTime,
                caption: artCaption,
                originalCaption: artCaption,
              };
            } else if (!updated[key].caption && artCaption) {
              updated[key].caption = artCaption;
            }
            updated[key].selectedNetworks = normalizedNetworks;
          });

          artScheduleDataRef.current = updated;
          return updated;
        });
      }

      if (
        optionName === 'MOCK_SCHEDULE_FINALIZED' &&
        Array.isArray(extraData)
      ) {
        if (campaignStatus === 'completed') {
          setIsSchedulingRunning(true);
          setSchedulingProgressItems(buildInitialSchedulingItems(extraData));
          setSchedulingSummary(null);

          const artsToSchedule = mergeArtsWithCurrentScheduleData(extraData);

          try {
            const schedulingResult = await scheduleCampaignPublications({
              arts: artsToSchedule,
              socialSelection: socialMediaSelectionRef.current,
              clientData,
              campaignId: campaignArtGenerationId,
              onStatusChange: (pieceNumber, statusData) => {
                updateSchedulingProgressItem(pieceNumber, {
                  status: statusData?.status || 'failed',
                  errorMessage: statusData?.errorMessage || null,
                });
              },
            });

            const updatedArts = schedulingResult?.updatedArts || extraData;
            const summary = schedulingResult?.summary || {
              total: extraData.length,
              scheduled: 0,
              failed: extraData.length,
            };

            campaignArtsRef.current = updatedArts;
            setCampaignArts(updatedArts);
            setSchedulingSummary(summary);

            if (socketRef.current && campaignArtGenerationId) {
              socketRef.current.emit('update_campaign_arts', {
                campaignArtGenerationId,
                arts: updatedArts,
              });
            }

            const allFailed = summary.failed === summary.total;
            const someFailed = summary.failed > 0;

            if (allFailed) {
              appendMessagesWithSequentialOrder([
                {
                  backend: tc('chatsocket.errors.reschedule_failed_all'),
                  frontend: '',
                  color: 'red',
                },
              ]);
            } else if (someFailed) {
              appendMessagesWithSequentialOrder([
                {
                  backend: tc('chatsocket.errors.reschedule_partial', { scheduled: summary.scheduled, total: summary.total }),
                  frontend: '',
                  color: 'red',
                },
              ]);
            } else {
              appendMessagesWithSequentialOrder([
                {
                  backend: tc('chatsocket.errors.reschedule_success'),
                  frontend: '',
                },
              ]);
            }
          } catch (error) {
            const errorMessage =
              error?.message || tc('chatsocket.errors.reschedule_error');
            setSchedulingSummary({
              total: extraData.length,
              scheduled: 0,
              failed: extraData.length,
            });
            setSchedulingProgressItems((previous) =>
              previous.map((item) => ({
                ...item,
                status: 'failed',
                errorMessage,
              })),
            );
            appendMessagesWithSequentialOrder([
              {
                backend: errorMessage,
                frontend: '',
                color: 'red',
              },
            ]);
          } finally {
            setIsSchedulingRunning(false);
          }
          return;
        }

        setLockedSections((previous) => ({
          ...previous,
          publications: true,
        }));
        const mergedArts = mergeArtsWithCurrentScheduleData(extraData);
        campaignArtsRef.current = mergedArts;
        setCampaignArts(mergedArts);
      }

      if (
        optionName === 'MOCK_SCHEDULING_COMPLETED' &&
        Array.isArray(extraData)
      ) {
        setLockedSections((previous) => ({
          ...previous,
          publications: true,
        }));
        campaignArtsRef.current = extraData;
        setCampaignArts(extraData);
      }

      if (optionName === 'MOCK_POST_QUANTITY_FINALIZED') {
        const mockQuestion = MOCK_QUESTION_MAP[optionName];
        setMessages((prev) => {
          const newMessages = [...prev];
          const msgIndex = newMessages.findLastIndex((m) => m.component === 'PostQuantitySelector');
          if (msgIndex !== -1) {
            const updatedMsg = {
              ...newMessages[msgIndex],
              answer: extraData !== null ? extraData : optionName,
            };
            newMessages[msgIndex] = injectMockComponent(updatedMsg);
          }

          return newMessages;
        });
        if (mockQuestion && socketRef.current) {
          const answerPayload = extraData !== null
            ? (Array.isArray(extraData) || typeof extraData === 'object'
              ? [JSON.stringify(extraData)]
              : [String(extraData)])
            : [optionName];
          socketRef.current.emit('answer_question', {
            campaignArtGenerationId: resolvedCampaignId,
            stepOrder: mockQuestion.stepOrder,
            messageOrder: mockQuestion.messageOrder,
            answer: answerPayload,
            lang: localStorage.getItem('i18nextLng') || 'ptBR',
          });
        }
        return;
      }

      const mockQuestion = MOCK_QUESTION_MAP[optionName];
      if (mockQuestion && socketRef.current) {
        setMessages((prev) => {
          const newMessages = [...prev];
          const lastOrder = newMessages.reduce(
            (max, m) => Math.max(max, Number(m?.messageOrder ?? 0)),
            0,
          );
          const msgIndex = newMessages.findLastIndex((m) => {
            if (optionName === 'MOCK_POST_QUANTITY_FINALIZED') return m.component === 'PostQuantitySelector';
            if (optionName === 'MOCK_SOCIAL_MEDIA_FINALIZED') return m.component === 'SocialMediaSelector';
            if (optionName === 'MOCK_SCHEDULE_FINALIZED') return m.component === 'ScheduleSelector';
            if (optionName === 'MOCK_SCHEDULING_COMPLETED') return m.component === 'SchedulingProgress';
            return false;
          });
          if (msgIndex !== -1) {
            const currentOrder = getFiniteOrderValue(
              newMessages[msgIndex]?.messageOrder,
            );
            newMessages[msgIndex] = {
              ...newMessages[msgIndex],
              answer: extraData !== null ? extraData : optionName,
              messageOrder: currentOrder ?? lastOrder + 0.1,
            };
          }
          return newMessages;
        });

        let answerPayload = [optionName];
        if (extraData) {
          answerPayload =
            Array.isArray(extraData) || typeof extraData === 'object'
              ? [JSON.stringify(extraData)]
              : [String(extraData)];
        }
        socketRef.current.emit('answer_question', {
          campaignArtGenerationId: resolvedCampaignId,
          stepOrder: mockQuestion.stepOrder,
          messageOrder: mockQuestion.messageOrder,
          answer: answerPayload,
          lang: localStorage.getItem('i18nextLng') || 'ptBR',
        });
      }
      return;
    }

    appendMessagesWithSequentialOrder([
      {
        frontend: optionName,
        backend: '',
        stepOrder: currentQuestion?.stepOrder,
        messageOrder: currentQuestion?.messageOrder,
      },
    ]);

    let answerPayload = [optionName];
    if (extraData) {
      if (Array.isArray(extraData) || typeof extraData === 'object') {
        answerPayload = [JSON.stringify(extraData)];
      } else {
        answerPayload = [String(extraData)];
      }
    }

    socketRef.current.emit('answer_question', {
      campaignArtGenerationId,
      stepOrder: currentQuestion.stepOrder,
      messageOrder: currentQuestion.messageOrder,
      answer: answerPayload,
      lang: localStorage.getItem('i18nextLng') || 'ptBR',
    });

    setCurrentQuestion(null);
  };

  const handleRespond = () => {
    if (currentQuestion?.component === 'VisualStyleSelector') {
      setIsVisualStyleSelectorOpen(true);
    } else if (currentQuestion?.component === 'ProductImageSelector') {
      setIsProductImageSelectorOpen(true);
    }
  };

  const handleSkip = async () => {
    if (!currentQuestion || !campaignArtGenerationId) return;

    if (currentQuestion?.component === 'PostQuantitySelector') {
      return;
    }

    const shouldStartStrategyImmediately =
      Number(currentQuestion?.messageOrder) === 6 &&
      isUserWithoutSubscriptionRef.current &&
      isFirstCampaignQuantityFlow(currentQuestion);

    autoProgressAfterSkipRef.current = true;
    // provide immediate UI feedback so skipping doesn't feel blocked
    setMessages((prev) => {
      const alreadyHasQueue = prev.some((m) => m?.isQueueProgressMessage);
      const next = [
        ...prev,
        {
          frontend: tc('chatsocket.buttons.skip_question'),
          backend: '',
          stepOrder: currentQuestion.stepOrder,
          messageOrder: currentQuestion.messageOrder,
        },
      ];

      if (!alreadyHasQueue && shouldStartStrategyImmediately) {
        next.push({
          backend: tc('chatsocket.loading.processing_strategy'),
          frontend: '',
          isQueueProgressMessage: true,
          disableTypingAnimation: true,
          stepOrder: 0,
          messageOrder: 7.2,
        });
      }
      return next;
    });

    if (shouldStartStrategyImmediately) {
      setIsStrategyLoading(true);
      setShowQueueProgress(true);
      setActiveTab('estrategia');
      setShowFullChatLayout(true);
    }

    socketRef.current.emit('answer_question', {
      campaignArtGenerationId: campaignArtGenerationId,
      stepOrder: currentQuestion.stepOrder,
      messageOrder: currentQuestion.messageOrder,
      answer: [tc('chatsocket.buttons.skip_question')],
      lang: localStorage.getItem('i18nextLng') || 'ptBR',
    });

    setCurrentQuestion(null);
  };

  const handlePasteImage = (pastedImages) => {
    const isProductImage =
      currentQuestion?.component === 'ProductImageSelector';
    if (!isProductImage || !pastedImages || pastedImages.length === 0) return;

    const newImage = { ...pastedImages[0], role: 'Principal' };
    setSelectedProductImages((prev) => {
      const otherRoleImages = prev.filter((img) => img.role !== 'Principal');
      return [...otherRoleImages, newImage];
    });
  };

  const handleAnswerQuestion = (answerData, options = {}) => {
    const {
      shouldAddToMessages = true,
      shouldClearCurrentQuestion = true,
      shouldSetMessage = false,
      shouldSetPendingAnswer = false,
    } = options;

    if (!currentQuestion || !socketRef.current) {
      return false;
    }

    const isProductImage = currentQuestion.component === 'ProductImageSelector';
    const isVisualStyle = currentQuestion.component === 'VisualStyleSelector';
    const isSpecial = !!currentQuestion.component;

    const finalOptions = isSpecial
      ? {
        shouldAddToMessages: false,
        shouldClearCurrentQuestion: false,
        shouldSetMessage: true,
        shouldSetPendingAnswer: true,
      }
      : {
        shouldAddToMessages,
        shouldClearCurrentQuestion,
        shouldSetMessage,
        shouldSetPendingAnswer,
      };

    const answer = answerData?.label || JSON.stringify(answerData);
    let displayImageUrls = [];
    let socketImageData = [];

    if (isProductImage && Array.isArray(answerData)) {
      socketImageData = getImageDataWithRoles(answerData);
      displayImageUrls = socketImageData.map((d) => d.image);
    } else if (isVisualStyle && answerData?.imageUrl) {
      socketImageData = [{ role: 'template', image: answerData.imageUrl }];
      displayImageUrls = [answerData.imageUrl];
    }

    if (finalOptions.shouldAddToMessages) {
      appendMessagesWithSequentialOrder([
        {
          frontend: answer,
          backend: '',
          stepOrder: currentQuestion.stepOrder,
          messageOrder: currentQuestion.messageOrder,
          imageUrls: displayImageUrls,
        },
      ]);
    }

    if (finalOptions.shouldSetMessage) {
      setMessage(answer);
    }

    if (finalOptions.shouldSetPendingAnswer) {
      setPendingAnswerQuestion(answerData);
    }

    if (!isSpecial) {
      const payload = {
        campaignArtGenerationId: campaignArtGenerationId,
        stepOrder: currentQuestion.stepOrder,
        messageOrder: currentQuestion.messageOrder,
        answer: buildAnswerWithImages(answer, socketImageData),
        lang: localStorage.getItem('i18nextLng') || 'ptBR',
      };

      socketRef.current.emit('answer_question', payload);
    }

    if (finalOptions.shouldClearCurrentQuestion) {
      setCurrentQuestion(null);
    }

    return true;
  };

  const createSelectorSaveHandler = (closeSelector) => {
    return (data) => {
      // Se estiver selecionando a partir do PreviewArt, não envia para o chat
      if (isSelectingFromPreview) {
        const selectedImages = Array.isArray(data) ? data : [data];
        setPreviewAttachedImages((prev) => [...prev, ...selectedImages]);
        setIsSelectingFromPreview(false);
        closeSelector();
        return;
      }

      const component = currentQuestion?.component;

      if (component === 'ProductImageSelector') {
        const role = imageSelectionContext || 'Principal';
        const newImages = Array.isArray(data)
          ? data.map((img) => ({ ...img, role }))
          : [];

        setSelectedProductImages((prev) => {
          const otherRoleImages = prev.filter((img) => img.role !== role);
          return newImages.length > 0
            ? [...otherRoleImages, ...newImages]
            : otherRoleImages;
        });
        setImageSelectionContext(null);
        closeSelector();
      } else if (component === 'VisualStyleSelector') {
        setSelectedTemplateImage(data);
        setPendingAnswerQuestion(data);
        setMessage('');
        closeSelector();
      } else {
        if (handleAnswerQuestion(data)) {
          closeSelector();
        }
      }
    };
  };

  const downloadFilename = tc('chatsocket.pdf_filename');

  const defaultOptions = {
    breaks: true,
    gfm: true,
    headerIds: true,
    mangle: false,
  };

  // Limpa markdown de wrappers de code block
  const cleanMarkdownForPdf = (content) => {
    if (!content) return '';

    const stringContent =
      typeof content === 'string' ? content : content?.markdown || '';
    if (!stringContent) return '';

    let cleaned = stringContent.trim();

    // Remove ```markdown ... ``` wrapper se existir
    const codeBlockRegex = /^```(?:markdown|md)?\s*\n([\s\S]*?)\n```$/;
    const match = cleaned.match(codeBlockRegex);

    if (match) {
      cleaned = match[1];
    }

    return cleaned;
  };

  const handleDownloadPdf = async (content) => {
    const finalContent = content || markdownContent;

    if (!finalContent) {
      return;
    }

    try {
      const cleanedContent = cleanMarkdownForPdf(finalContent);

      await markdownToPdf(cleanedContent, {
        filename: downloadFilename,
        markedOptions: defaultOptions,
      });
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
    }
  };

  const handleDownloadZipDocument = async () => {
    setIsCheckingDownloadAccess(true);

    try {
      const canDownload = await resolveCampaignDownloadAccessWithRetry();

      if (!canDownload) {
        openUpsellFlow(campaignUpsellFlowRef, true);
        return;
      }

      setIsZipDownloading(true);
      socketRef.current.emit('download_zip_document', {
        ...latestDownloadDataRef.current,
      });
    } finally {
      setIsCheckingDownloadAccess(false);
    }
  };

  const resetCampaignState = (shouldResume = true) => {
    isTransitioningRef.current = false;
    setMessages([]);
    setCampaignArts([]);
    campaignArtsRef.current = [];
    setImgsArray([]);
    setTotalImagesArray(0);
    setMarkdownContent('');
    markdownContentRef.current = '';
    setStrategyResult({});
    setCampaignStatus('in_progress');
    setCurrentQuestion(null);
    setPendingApproval(null);
    setQueueSteps(getInitialQueueSteps()?.map((s) => ({ ...s, status: 'idle' })));
    queueProgressIndexRef.current = 0;
    clearPersistedQueueProgress(campaignArtGenerationIdRef.current);
    // No persisted arts-ready messages to clear (we stopped persisting them).
    setShowQueueProgress(false);
    setArtSteps([]);
    setShowArtQueueProgress(false);
    setSocialMediaSelection([]);
    setLockedSections({
      socialMedia: false,
      publications: false,
    });
    setHasSelectedSchedulePublicationItem(false);
    setArtScheduleData({});
    artScheduleDataRef.current = {};
    setSelectedArt(null);
    setShowFullChatLayout(false);
    setActiveTab('estrategia');
    setCampaignArtGenerationId(null);
    campaignArtGenerationIdRef.current = null;
    setIsStrategyLoading(false);
    setIsEditingArt(false);
    setPendingEditArtData(null);
    setPreviewAttachedImages([]);
    setReferenceArt(null);
    setSelectedProductImages([]);
    setSelectedTemplateImage(null);
    setPendingAnswerQuestion(null);
    setMessage('');
    setSchedulingProgressItems([]);
    setSchedulingSummary(null);
    setIsSchedulingRunning(false);
    setBatchEditActionType(null);
    setArtLimitFallback(null);
    setBatchEditLimitNoticeVariant(null);
    setPreviewInitialTab('ai');
    setImageSelectionContext(null);
    setIsLoadingBatchEditArtPerCampaign({});
    consumedCampaignArtOperationKeysRef.current = new Set();
    firstCampaignQuantityAutoAnswerRef.current = new Set();

    if (shouldResume && socketRef.current && userId && authToken) {
      socketRef.current.emit('resume_campaign', {
        userAccountId: userId,
        authToken,

      });
    }
  };

  const handleResetToCreate = () => {
    // Guarda a campanha anterior para que eventos atrasados dela sejam
    // ignorados enquanto a nova campanha é criada.
    previousCampaignBeforeCreateRef.current = campaignArtGenerationIdRef.current;
    setPaletteConfirmed(false);
    setLockedSections({ socialMedia: false, publications: false });
    setMessages([]);
    setCampaignArts([]);
    campaignArtsRef.current = [];
    setImgsArray([]);
    setTotalImagesArray(0);
    setMarkdownContent('');
    markdownContentRef.current = '';
    setStrategyResult({});
    setCampaignStatus('in_progress');
    campaignStatusRef.current = 'in_progress';
    setCurrentQuestion(null);
    setCampaignName(tc('chatsocket.new_campaign_name'));
    setIsGeneratingSuggestion(false);
    setIsBulkStep0Active(false);
    setPendingApproval(null);
    setQueueSteps(getInitialQueueSteps().map(s => ({ ...s, status: 'idle' })));
    setShowQueueProgress(false);
    setArtSteps([]);
    setShowArtQueueProgress(false);
    setSocialMediaSelection([]);
    setArtScheduleData({});
    artScheduleDataRef.current = {};
    setSelectedArt(null);
    setShowFullChatLayout(false);
    setActiveTab('estrategia');
    setCampaignArtGenerationId(null);
    campaignArtGenerationIdRef.current = null;
    setIsStrategyLoading(false);
    setIsEditingArt(false);
    setPendingEditArtData(null);
    setPreviewAttachedImages([]);
    setReferenceArt(null);
    setSelectedProductImages([]);
    setSelectedTemplateImage(null);
    setPendingAnswerQuestion(null);
    setMessage('');
    setHasSelectedSchedulePublicationItem(false);
    setSchedulingProgressItems([]);
    setSchedulingSummary(null);
    setIsSchedulingRunning(false);
    schedulingInFlightRef.current = false;
    setBatchEditActionType(null);
    setArtLimitFallback(null);
    setBatchEditLimitNoticeVariant(null);
    setPreviewInitialTab('ai');
    setImageSelectionContext(null);
    setIsLoadingBatchEditArtPerCampaign({});
    consumedCampaignArtOperationKeysRef.current = new Set();
    firstCampaignQuantityAutoAnswerRef.current = new Set();
    const defaultLang = localStorage.getItem('i18nextLng') || 'ptBR';
    campaignLangRef.current = defaultLang;
    setCampaignLanguage(defaultLang);
  }

  const handleCreateCampaignWithGate = async () => {
    const canCreateCampaign = await ensureCampaignCreationAllowed({
      source: 'sidebar_new_campaign',
      eventContext: 'sidebar_new_campaign',
    });

    if (!canCreateCampaign) {
      return;
    }

    return handleCreateNewCampaign();
  };

  const handleCreateNewCampaign = async () => {
    isCreatingNewCampaignRef.current = true;
    suppressTypingDuringRestoreRef.current = false;
    handleResetToCreate();

    if (!socketRef.current || !userId || !authToken) {
      return;
    }

    // socketRef.current.emit('start_campaing', {
    //   userAccountId: userId,
    //   authToken,
    //   campaignArtGenerationId: null,
    //   forceNewCampaign: true,
    // });

    socketRef.current.emit('start_campaign_questions', {
      userAccountId: userId,
      authToken,
      campaignArtGenerationId: null,
      forceNewCampaign: true,
      lang: localStorage.getItem('i18nextLng') || 'ptBR',
    });


  };

  handleSelectCampaignRef.current = handleSelectCampaign;
  handleCreateCampaignWithGateRef.current = handleCreateCampaignWithGate;

  useEffect(() => {
    registerCampaignHandlers({
      onSelect: (campaignId) => handleSelectCampaignRef.current?.(campaignId),
      onCreate: () => handleCreateCampaignWithGateRef.current?.(),
    });

    return () => {
      unregisterCampaignHandlers();
    };
  }, [registerCampaignHandlers, unregisterCampaignHandlers]);

  useEffect(() => {
    if (pendingSelectId) {
      void handleSelectCampaignRef.current?.(pendingSelectId);
      clearPendingActions();
      return;
    }

    if (pendingCreate) {
      void handleCreateCampaignWithGateRef.current?.();
      clearPendingActions();
    }
  }, [clearPendingActions, pendingCreate, pendingSelectId]);

  useEffect(() => {
    setSelectedCampaignId(campaignArtGenerationId || null);
  }, [campaignArtGenerationId, setSelectedCampaignId]);



  return (
    <div className={showFullChatLayout ? styles.landingPage : styles.landingPage_chat}>
      {!showFullChatLayout && (
        <div className={styles.gradientBackground} aria-hidden="true" />
      )}
      {renderConnectionBanner()}

      <div
        className={`${styles.container} ${showFullChatLayout ? styles.containerFullChat : ''} ${!showFullChatLayout && isCampaignStarted ? styles.containerLandingChatExpanded : ''} ${isCampaignSwitching ? styles.containerSwitching : ''}`}
      >
        {isCampaignSwitching && (
          <div
            className={styles.loadingSpinnerOverlay}
            aria-busy="true"
            aria-live="polite"
          >
            <div className={styles.loadingSpinner} />
            <span className={styles.loadingSpinnerText}>
              {tc('chatsocket.loading.loading_conversation')}
            </span>
          </div>
        )}
        {!showFullChatLayout && (
          <>
            {/* <Header /> */}
            <div className={`${styles.bodyWithStepper} ${isCampaignStarted ? styles.bodyWithStepper_started : ''}`}>
              <div className={`${styles.body} ${isCampaignStarted ? styles.body_started : ''}`}>
                <div className={`${styles.bodyContent} ${isCampaignStarted ? styles.bodyContent_started : ''}`}>
                  {!messages.some((m) => !!m.frontend) && (
                    <div className={styles.bodyTextContainer}>
                      <Title />
                      <p className={styles.description}>
                        {tc('chatsocket.title.description')}
                      </p>
                    </div>
                  )}
                  <ChatPage
                    sendMessage={handleSendMessage}
                    messages={messages}
                    message={message}
                    setMessage={setMessage}
                    isFullChatLayout={showFullChatLayout}
                    currentQuestion={currentQuestion}
                    onSelectOption={handleSelectOption}
                    onRespond={handleRespond}
                    onSkip={handleSkip}
                    selectedItems={selectedItems}
                    onPasteImage={handlePasteImage}
                    showQueueProgress={showQueueProgress}
                    queueSteps={queueSteps}
                    showArtQueueProgress={showArtQueueProgress}
                    artSteps={artSteps}
                    onOpenStepsImageSelector={handleOpenStepsImageSelector}
                    onReferenceArtSelect={handleReferenceArtSelect}
                    referenceArt={referenceArt}
                    renderDynamicComponent={stableRenderDynamicComponent}
                    campaignName={campaignName}
                    isCampaignStarted={isCampaignStarted}
                    isLoadingBatchEditArt={isLoadingBatchEditArtPerCampaign[campaignArtGenerationId]}
                    onCampaignNameChange={(newName) => {
                      setCampaignName(newName);
                      if (campaignArtGenerationId && socketRef.current) {
                        socketRef.current.emit('update_campaign_name', {
                          campaignArtGenerationId,
                          campaignName: newName,
                        });
                      }
                    }}
                    selectedArt={selectedArt}
                    isEditingArt={isEditingArt}
                    onEditArtRequest={handleChatEditArtRequest}
                    artAttachedImages={previewAttachedImages}
                    onArtAttachImages={(imgs) => setPreviewAttachedImages(imgs)}
                    onOpenProductImageSelector={() => {
                      setIsSelectingFromPreview(true);
                      setIsProductImageSelectorOpen(true);
                    }}
                    artesCriadas={artesCriadasCount}
                    limiteArtes={limiteArtesCount}
                    artLimitFallback={artLimitFallback}
                    canUseAI={canUseAI}
                    showAISuggestionBanner={showAISuggestionBanner}
                    onGenerateSuggestion={handleGenerateBulkStep0}
                    isGeneratingSuggestion={isGeneratingSuggestion}
                    hasAvailableAISuggestions={hasAvailableAISuggestions}
                    isSubmittingMessage={isSubmittingMessage}
                    campaignLanguage={campaignLanguage}
                  />
                </div>
              </div>
            </div>
          </>
        )}
        {showFullChatLayout && (
          <div className={styles.fullChatLayout}>
            <div className={styles.chatContainer}>
              <div className={styles.chatSection}>
                <ChatPage
                  sendMessage={handleSendMessage}
                  messages={messages}
                  message={message}
                  setMessage={setMessage}
                  isFullChatLayout={showFullChatLayout}
                  currentQuestion={currentQuestion}
                  onSelectOption={handleSelectOption}
                  onRespond={handleRespond}
                  onSkip={handleSkip}
                  selectedItems={selectedItems}
                  onPasteImage={handlePasteImage}
                  showQueueProgress={showQueueProgress}
                  queueSteps={queueSteps}
                  showArtQueueProgress={showArtQueueProgress}
                  artSteps={artSteps}
                  onOpenStepsImageSelector={handleOpenStepsImageSelector}
                  onReferenceArtSelect={handleReferenceArtSelect}
                  referenceArt={referenceArt}
                  renderDynamicComponent={stableRenderDynamicComponent}
                  campaignName={campaignName}
                  onClickZipCard={handleDownloadZipDocument}
                  isZipDownloading={isZipDownloading}
                  isCampaignStarted={isCampaignStarted}
                  isLoadingBatchEditArt={isLoadingBatchEditArtPerCampaign[campaignArtGenerationId]}
                  onCampaignNameChange={(newName) => {
                    setCampaignName(newName);
                    if (campaignArtGenerationId && socketRef.current) {
                      socketRef.current.emit('update_campaign_name', {
                        campaignArtGenerationId,
                        campaignName: newName,
                      });
                    }
                  }}
                  selectedArt={selectedArt}
                  isEditingArt={isEditingArt}
                  onEditArtRequest={handleChatEditArtRequest}
                  artAttachedImages={previewAttachedImages}
                  onArtAttachImages={(imgs) => setPreviewAttachedImages(imgs)}
                  onOpenProductImageSelector={() => {
                    setIsSelectingFromPreview(true);
                    setIsProductImageSelectorOpen(true);
                  }}
                  artesCriadas={artesCriadasCount}
                  limiteArtes={limiteArtesCount}
                  artLimitFallback={artLimitFallback}
                  disableInputFromPreview={(!canUseAI || previewActiveTab === 'edit') && !(['edit_strategy', 'reject_strategy', 'batch_edit_art'].includes(currentQuestion?.stepName))}
                  isArtsApproved={isArtsApproved}
                  canUseAI={canUseAI}
                  showAISuggestionBanner={showAISuggestionBanner}
                  onGenerateSuggestion={handleGenerateBulkStep0}
                  isGeneratingSuggestion={isGeneratingSuggestion}
                  hasAvailableAISuggestions={hasAvailableAISuggestions}
                  isSubmittingMessage={isSubmittingMessage}
                  campaignLanguage={campaignLanguage}
                />
              </div>
              <PreviewPage
                activeTab={activeTab}
                onClickTab={(tab) => {
                  if (tab === 'artes' && !canAccessArtsTab) return;
                  setActiveTab(tab);
                }}
                canAccessArtsTab={canAccessArtsTab}
                preview={true}
              >
                {activeTab === 'estrategia' && isStrategyLoading && (
                  <div className={styles.loadingSpinnerContainer}>
                    <div className={styles.loadingSpinner} />
                    <span className={styles.loadingSpinnerText}>
                      {tc('chatsocket.loading.loading_strategy')}
                    </span>
                  </div>
                )}

                {activeTab === 'estrategia' &&
                  !isStrategyLoading &&
                  markdownContent && (
                  <MarkdownPreview
                    content={markdownContent}
                    editSessionId={markdownEditSessionId}
                    onSave={(e) => handleUpdateStrategy(e)}
                    isEditDisabled={campaignStatus !== 'strategy_ready'}
                  />
                )}

                {activeTab === 'artes' && (
                  <>
                    {isApprovingArts && (
                      <div className={styles.loadingSpinnerContainer} >
                        <div className={styles.loadingSpinner} />
                        <span className={styles.loadingSpinnerText}>
                          {tc('chatsocket.loading.approving_arts')}
                        </span>
                      </div>
                    )}
                    {isLoadingBatchEditArtPerCampaign[campaignArtGenerationId] ? (
                      <div className={styles.loadingSpinnerContainer}>
                        <div className={styles.loadingSpinner} />
                        <span className={styles.loadingSpinnerText}>
                          {tc('chatsocket.loading.processing_arts')}
                        </span>
                      </div>
                    ) : selectedArt ? (
                      <PreviewArt
                        images={imgsArray}
                        totalImages={totalImagesArray}
                        imageRenderKey={artPreviewRenderKey}
                        sessionDetail={campaignPreviewSession}
                        selectedImage={selectedArt}
                        onSelectImage={(image) => {
                          ensureArtScheduleDataInitialized(image);
                          setSelectedArt(image);
                          if (canUseAI) {
                            setPreviewInitialTab('ai');
                          } else {
                            setPreviewInitialTab(isCalendarPreviewEnabled ? 'edit' : 'ai');
                          }
                        }}
                        onClose={() => {
                          setHasSelectedSchedulePublicationItem(false);
                          setSelectedArt(null);
                        }}
                        onEditRequest={handleEditArt}
                        isEditing={isEditingArt}
                        defaultTab={previewInitialTab}
                        isCalendarTabEnabled={isCalendarPreviewEnabled}
                        isScheduleReadOnly={isPublicationReadOnly}
                        scheduleData={
                          artScheduleData[selectedArt?.piece_number] || {}
                        }
                        onScheduleDataChange={(field, value) =>
                          handleUpdateArtSchedule(
                            selectedArt?.piece_number,
                            field,
                            value,
                          )
                        }
                        onOpenProductImageSelector={() => {
                          setIsSelectingFromPreview(true);
                          setIsProductImageSelectorOpen(true);
                        }}
                        attachedImages={previewAttachedImages}
                        onAttachImages={(imgs) => setPreviewAttachedImages(imgs)}
                        isApproved={isArtsApproved}
                        canUseAI={canUseAI}
                        onTabChange={(tab) => setPreviewActiveTab(tab)}
                      />
                    ) : (
                      <div className={styles.imagesContainer}>
                        <ImagesScrollableContainer
                          images={imgsArray}
                          totalImages={totalImagesArray}
                          renderKey={artPreviewRenderKey}
                          sessionDetail={campaignPreviewSession}
                          onSelectImage={(image) => {
                            ensureArtScheduleDataInitialized(image);
                            setHasSelectedSchedulePublicationItem(false);
                            setSelectedArt(image);
                            if (!isArtsApproved) {
                              setPreviewInitialTab('ai');
                            } else {
                              setPreviewInitialTab(isCalendarPreviewEnabled ? 'edit' : 'ai');
                            }
                          }}
                        />
                      </div>
                    )}
                  </>
                )}
              </PreviewPage>
            </div>
          </div>
        )}
        <ColorSelector
          isOpen={isColorSelectorOpen}
          onClose={() => setIsColorSelectorOpen(false)}
          onSave={createSelectorSaveHandler(() =>
            setIsColorSelectorOpen(false),
          )}
          presetsProp={[]}
        />
        <NicheSelector
          isOpen={isNicheSelectorOpen}
          onClose={() => setIsNicheSelectorOpen(false)}
          onSave={createSelectorSaveHandler(() =>
            setIsNicheSelectorOpen(false),
          )}
        />
        <ProductImageSelector
          isOpen={isProductImageSelectorOpen}
          onClose={() => {
            setIsProductImageSelectorOpen(false);
            setIsSelectingFromPreview(false);
          }}
          onSave={createSelectorSaveHandler(() =>
            setIsProductImageSelectorOpen(false),
          )}
          preselectedImages={
            imageSelectionContext
              ? selectedProductImages.filter(
                (img) => img.role === imageSelectionContext,
              )
              : selectedProductImages
          }
          maxImages={5}
        />

        <VisualStyleSelector
          isOpen={isVisualStyleSelectorOpen}
          onClose={() => setIsVisualStyleSelectorOpen(false)}
          preselectedStyle={pendingAnswerQuestion || selectedTemplateImage}
          onSave={createSelectorSaveHandler(() =>
            setIsVisualStyleSelectorOpen(false),
          )}
        />

        <BatchEditLimitNoticeModal
          isOpen={!!batchEditLimitNoticeVariant}
          variant={batchEditLimitNoticeVariant || 'partial'}
          onClose={() => setBatchEditLimitNoticeVariant(null)}
        />

        <UpsellCheckoutProvider>
          <CampaignUpsellFlow
            ref={campaignUpsellFlowRef}
            enabled={isUserWithoutSubscription}
            campaignId={campaignArtGenerationId}
          />

          <ArtsLimitUpsellFlow
            ref={artsLimitUpsellFlowRef}
            enabled={isUserWithoutSubscription && !canDownloadCampaigns}
            campaignId={campaignArtGenerationId}
            onDismiss={handleArtLimitFlowDismiss}
          />

          <SubscribedArtsLimitUpsellFlow
            ref={subscribedArtsLimitUpsellFlowRef}
            enabled={shouldUseSubscribedArtsLimitFlow}
            campaignId={campaignArtGenerationId}
            onDismiss={handleArtLimitFlowDismiss}
          />
        </UpsellCheckoutProvider>

        {shouldRenderUserAvaliationModal && (
          <CampaignEvaluationModal
            isOpen={isUserAvaliationOpen}
            onClose={handleDismissUserAvaliation}
            onSubmit={handleSendUserAvaliation}
            isSubmitting={isSubmittingCampaignEvaluation}
          />
        )}

        <CampaignEvaluationRewardModal
          isOpen={isAvaliationRewardModalOpen}
          onClose={() => setIsAvaliationRewardModalOpen(false)}
        />

        {isCheckingDownloadAccess && (
          <div className={styles.downloadAccessOverlay} />
        )}
      </div>
    </div>
  );
};
